import { isString, useStableCallback, useConstant, isFunction, usePrevious, useForceUpdate, createNamedContext, noop, isBoolean as isBoolean$1, isNumber } from '@reach/utils';
export { checkStyles, cloneValidElement, createNamedContext, getDocumentDimensions, isBoolean, isFunction, isNumber, isString, makeId, noop, useCheckStyles, useComposedRefs, useConstant, useControlledState, useControlledSwitchWarning, useForceUpdate, useComposedRefs as useForkedRef, useIsomorphicLayoutEffect, usePrevious, useStableCallback, useUpdateEffect } from '@reach/utils';
import { __rest } from 'tslib';
import * as React from 'react';
import cx from 'classnames';
import { useId } from '@reach/auto-id';
export { useId } from '@reach/auto-id';
import axios from 'axios';
import s3uri from 's3-url-parser';
import { createPortal } from 'react-dom';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';

function canUseDOM() {
  return !!(typeof window !== "undefined" && window.document && window.document.createElement);
}

/**
 * Get an element's owner document. Useful when components are used in iframes
 * or other environments like dev tools.
 *
 * @param element
 */

function getOwnerDocument(element) {
  return canUseDOM() ? element ? element.ownerDocument : document : null;
}
/**
 * TODO: Remove in 1.0
 */

function getOwnerWindow(element) {
  var ownerDocument = getOwnerDocument(element);
  return ownerDocument ? ownerDocument.defaultView || window : null;
}

/**
 * Get computed style properties of a DOM element.
 *
 * @param element
 * @param styleProp
 */

function getComputedStyles(element) {
  var ownerWindow = getOwnerWindow(element);

  if (ownerWindow) {
    return ownerWindow.getComputedStyle(element, null);
  }

  return null;
}
/**
 * Get a computed style value by property.
 *
 * @param element
 * @param styleProp
 */

function getComputedStyle(element, styleProp) {
  var _getComputedStyles;

  return ((_getComputedStyles = getComputedStyles(element)) == null ? void 0 : _getComputedStyles.getPropertyValue(styleProp)) || null;
}

/**
 * Flatten an array recursively
 *
 * @see https://github.com/jonschlinkert/arr-flatten
 * @license MIT
 */
function flattenArray(nestedArray) {
    const result = [];
    flatten(nestedArray, result);
    return result;
}
function flatten(input, result) {
    let arrLen = input.length;
    let index = 0;
    while (arrLen--) {
        const current = input[index++];
        if (Array.isArray(current)) {
            flatten(current, result);
        }
        else {
            result.push(current);
        }
    }
}
/**
 * Create an array with particular length
 *
 * @param length the length of the array to be created
 * @param filler the value of all elements in the array
 */
const createArray = (length, filler = undefined) => {
    const result = [];
    for (let index = 0; index < length; index++) {
        result.push(filler);
    }
    return result;
};
/**
 * Returns the index of the last element in the array where predicate is true, and -1
 * otherwise.
 * @param array The source array to search in
 * @param predicate find calls predicate once for each element of the array, in descending
 * order, until it finds one where predicate returns true. If such an element is found,
 * findLastIndex immediately returns that element index. Otherwise, findLastIndex returns -1.
 */
function findLastIndex(array, predicate) {
    let l = array.length;
    while (l--) {
        if (predicate(array[l], l, array))
            return l;
    }
    return -1;
}
/**
 * Iterate array like list, e.g. NodeList
 */
function forEach(list, callback) {
    for (let index = 0; index < list.length; index++) {
        const element = list[index];
        callback(element, index);
    }
}
function toArray(list) {
    const result = [];
    forEach(list, (i) => result.push(i));
    return result;
}
function isSimilarArray(arrayOne, arrayTwo) {
    if (arrayOne === arrayTwo) {
        return true;
    }
    if (arrayOne.length !== arrayTwo.length) {
        return false;
    }
    return arrayOne.every((item, index) => item === arrayTwo[index]);
}
const arrayMoveMutate = (array, from, to) => {
    const startIndex = from < 0 ? array.length + from : from;
    if (startIndex >= 0 && startIndex < array.length) {
        const endIndex = to < 0 ? array.length + to : to;
        const [item] = array.splice(from, 1);
        array.splice(endIndex, 0, item);
    }
};
/**
 * Move an array item to a different position.
 *
 * Shamelessly copied from `array-move`
 */
const arrayMove = (array, from, to) => {
    const result = [...array];
    arrayMoveMutate(result, from, to);
    return result;
};
/**
 * Dedupe array item
 *
 * Based on implementation from https://github.com/jonschlinkert/array-unique.
 *
 * @param array array that you wish to dedupe.
 * @param uniqueKey properties to compare. If not provided, we will shallow compare the equality of the item.
 */
function dedupeArray(array, uniqueKey) {
    const arr = array.slice();
    const len = arr.length;
    let i = -1;
    while (i++ < len) {
        var j = i + 1;
        for (; j < arr.length; ++j) {
            if (uniqueKey ? arr[i][uniqueKey] === arr[j][uniqueKey] : arr[i] === arr[j]) {
                arr.splice(j--, 1);
            }
        }
    }
    return arr;
}

/**
 * A higher order component to default props for SVG component
 */
const withDefaultSvgProps = (Component) => {
    const Decorated = (props) => (React.createElement(Component, Object.assign({ "aria-hidden": true, focusable: false }, props)));
    Decorated.displayName = `withDefaultSvgProps(${Component.displayName || Component.name})`;
    return Decorated;
};
const useDefaultSvgProps = (props) => (Object.assign({ 'aria-hidden': true, focusable: false }, props));

const Dots = React.forwardRef(function Dots(_a, ref) {
    var { count, size = 4, spacing = 4 } = _a, providedProps = __rest(_a, ["count", "size", "spacing"]);
    const props = useDefaultSvgProps(providedProps);
    if (count < 1) {
        return null;
    }
    const width = count * size + (count - 1) * spacing;
    return (React.createElement("svg", Object.assign({ viewBox: `0 0 ${width} ${size}`, width: width, height: size }, props, { ref: ref }), createArray(count).map((_, index) => (React.createElement("circle", { cx: size / 2 + index * (spacing + size), cy: size / 2, r: size / 2, fill: "#2D333A", key: index })))));
});

const AddImageIcon = React.forwardRef(function AddImageIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 24 24", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.25 3C3.25 2.0335 4.0335 1.25 5 1.25H16C16.1989 1.25 16.3897 1.32902 16.5303 1.46967L21.5303 6.46967C21.671 6.61032 21.75 6.80109 21.75 7V14C21.75 14.4142 21.4142 14.75 21 14.75C20.5858 14.75 20.25 14.4142 20.25 14V7.75H16C15.5858 7.75 15.25 7.41421 15.25 7V2.75H5C4.86193 2.75 4.75 2.86193 4.75 3V21C4.75 21.1381 4.86193 21.25 5 21.25H14C14.4142 21.25 14.75 21.5858 14.75 22C14.75 22.4142 14.4142 22.75 14 22.75H5C4.0335 22.75 3.25 21.9665 3.25 21V3ZM16.75 3.81066V6.25H19.1893L16.75 3.81066Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.25 9.5C7.25 8.25736 8.25736 7.25 9.5 7.25C10.7426 7.25 11.75 8.25736 11.75 9.5C11.75 10.7426 10.7426 11.75 9.5 11.75C8.25736 11.75 7.25 10.7426 7.25 9.5ZM9.5 8.75C9.08579 8.75 8.75 9.08579 8.75 9.5C8.75 9.91421 9.08579 10.25 9.5 10.25C9.91421 10.25 10.25 9.91421 10.25 9.5C10.25 9.08579 9.91421 8.75 9.5 8.75Z", fill: "currentColor" }),
        React.createElement("path", { d: "M13.9388 12.998C14.439 12.3867 15.3736 12.3867 15.8737 12.998L17.5805 15.0841C17.8428 15.4046 17.7955 15.8772 17.475 16.1395C17.1544 16.4017 16.6819 16.3545 16.4196 16.0339L14.9063 14.1843L12.4021 17.245C12.3857 17.2658 12.3681 17.2859 12.3492 17.3052C12.2917 17.3642 12.2264 17.4119 12.1562 17.448C11.9568 17.551 11.7229 17.5576 11.5204 17.4722C11.4338 17.4357 11.353 17.3823 11.2831 17.3125C11.2678 17.2974 11.2534 17.2817 11.2397 17.2655L9.40626 15.1702L7.56444 17.2751C7.29168 17.5868 6.81786 17.6184 6.50613 17.3457C6.1944 17.0729 6.16282 16.5991 6.43558 16.2874L8.46554 13.9674C8.96355 13.3982 9.84897 13.3982 10.347 13.9674L11.7933 15.6203L13.9388 12.998Z", fill: "currentColor" }),
        React.createElement("path", { d: "M20 16.25C20.4142 16.25 20.75 16.5858 20.75 17V19.25H23C23.4142 19.25 23.75 19.5858 23.75 20C23.75 20.4142 23.4142 20.75 23 20.75H20.75V23C20.75 23.4142 20.4142 23.75 20 23.75C19.5858 23.75 19.25 23.4142 19.25 23V20.75H17C16.5858 20.75 16.25 20.4142 16.25 20C16.25 19.5858 16.5858 19.25 17 19.25H19.25V17C19.25 16.5858 19.5858 16.25 20 16.25Z", fill: "currentColor" })));
});

const AmexIcon = React.forwardRef(function AmexIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "42", height: "30", viewBox: "0 0 42 30", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.6 0H38.4C40.3882 0 42 1.87416 42 4.18605V25.814C42 28.1258 40.3882 30 38.4 30H3.6C1.61177 30 0 28.1258 0 25.814V4.18605C0 1.87416 1.61177 0 3.6 0Z", fill: "white" }),
        React.createElement("path", { d: "M3.6 0.5H38.4C40.044 0.5 41.5 2.07688 41.5 4.18605V25.814C41.5 27.9231 40.044 29.5 38.4 29.5H3.6C1.95602 29.5 0.5 27.9231 0.5 25.814V4.18605C0.5 2.07688 1.95602 0.5 3.6 0.5Z", stroke: "black", strokeOpacity: "0.08" }),
        React.createElement("path", { d: "M9.86168 14.7011V10.8135L11.4687 10.8192L12.4033 13.3257L13.3157 10.8135H14.9098V14.7011H13.9002V11.8365L12.8299 14.7011H11.9445L10.8713 11.8365V14.7011H9.86168Z", fill: "#016FD0" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6.69007 10.8144L4.95312 14.702H6.08397L6.40445 13.9236H8.2676L8.58645 14.702H9.74219L8.0069 10.8144H6.69007ZM7.33271 11.7192L7.90062 13.0795H6.76315L7.33271 11.7192Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M15.6005 10.8135V14.7011H18.895V13.8571H16.6207V13.1665H18.8419V12.3481H16.6207V11.6831H18.895V10.8135H15.6005Z", fill: "#016FD0" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19.4795 10.8141V14.7017H20.4891V13.3206H20.9142L22.1248 14.7017H23.3585L22.0301 13.2694C22.5753 13.2251 23.1377 12.7747 23.1377 12.0754C23.1377 11.2573 22.4707 10.8141 21.7262 10.8141H19.4795ZM20.4891 11.6837H21.6432C21.92 11.6837 22.1214 11.8922 22.1214 12.0929C22.1214 12.3512 21.8605 12.5022 21.6581 12.5022H20.4891V11.6837Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M23.55 14.7011H24.5808V10.8135H23.55V14.7011Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M27.0251 14.7011H26.8026C25.7259 14.7011 25.0723 13.8846 25.0723 12.7733C25.0723 11.6345 25.7186 10.8135 27.0782 10.8135H28.1941V11.7342H27.0374C26.4855 11.7342 26.0952 12.1488 26.0952 12.7829C26.0952 13.5357 26.5415 13.8519 27.1845 13.8519H27.4502L27.0251 14.7011Z", fill: "#016FD0" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M27.4853 14.7017L29.2223 10.8141H30.5391L32.2744 14.7017H31.1187L30.7998 13.9232H28.9367L28.6162 14.7017H27.4853ZM30.4328 13.0792L29.8649 11.7189L29.2954 13.0792H30.4328Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M32.3921 14.7011V10.8135H33.6758L35.3147 13.256V10.8135H36.3244V14.7011H35.0822L33.4018 12.1946V14.7011H32.3921Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M9.27598 15.6768V19.5644H12.5705V18.7203H10.2962V18.0298H12.5174V17.2113H10.2962V16.5464H12.5705V15.6768H9.27598Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M28.7139 19.5644H25.4193V15.6768H28.7139V16.5464H26.4396V17.2113H28.6501V18.0298H26.4396V18.7203H28.7139V19.5644Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M14.3025 17.6445L12.6984 19.5644H13.9521L14.8787 18.3671L15.8284 19.5644H17.1001L15.4931 17.6206L17.1138 15.6768H15.8916L14.9102 16.8932L13.9322 15.6768H12.6602L14.3025 17.6445Z", fill: "#016FD0" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.22 15.6774V19.565H18.2562V18.3373H19.319C20.2182 18.3373 20.8998 17.8781 20.8998 16.985C20.8998 16.2451 20.3653 15.6774 19.4502 15.6774H17.22ZM18.2562 16.5566H19.3755C19.666 16.5566 19.8736 16.728 19.8736 17.0042C19.8736 17.2636 19.667 17.4517 19.3721 17.4517H18.2562V16.5566Z", fill: "#016FD0" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M22.3479 19.5644H21.3383V15.6768H23.585C24.3295 15.6768 24.9965 16.1199 24.9965 16.938C24.9965 17.6373 24.4341 18.0878 23.8889 18.1321L25.2174 19.5644H23.9836L22.773 18.1832H22.3479V19.5644ZM23.502 16.5464H22.3479V17.3648H23.517C23.7193 17.3648 23.9803 17.2139 23.9803 16.9556C23.9803 16.7548 23.7789 16.5464 23.502 16.5464Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M29.1815 19.5644V18.7203H31.2021C31.5011 18.7203 31.6305 18.5648 31.6305 18.3942C31.6305 18.2308 31.5015 18.0656 31.2021 18.0656H30.289C29.4953 18.0656 29.0533 17.6001 29.0533 16.9012C29.0533 16.2779 29.4581 15.6768 30.6375 15.6768H32.6036L32.1785 16.5515H30.4781C30.153 16.5515 30.053 16.7157 30.053 16.8724C30.053 17.0336 30.1766 17.2113 30.4249 17.2113H31.3814C32.2662 17.2113 32.6501 17.6944 32.6501 18.3271C32.6501 19.0073 32.2223 19.5644 31.3333 19.5644H29.1815Z", fill: "#016FD0" }),
        React.createElement("path", { d: "M32.8871 18.7203V19.5644H35.0388C35.9278 19.5644 36.3556 19.0073 36.3556 18.3271C36.3556 17.6944 35.9717 17.2113 35.087 17.2113H34.1305C33.8822 17.2113 33.7585 17.0336 33.7585 16.8724C33.7585 16.7157 33.8586 16.5515 34.1836 16.5515H35.884L36.3091 15.6768H34.343C33.1636 15.6768 32.7589 16.2779 32.7589 16.9012C32.7589 17.6001 33.2009 18.0656 33.9946 18.0656H34.9076C35.207 18.0656 35.3361 18.2308 35.3361 18.3942C35.3361 18.5648 35.2066 18.7203 34.9076 18.7203H32.8871Z", fill: "#016FD0" })));
});

const ArrowLeftIcon = React.forwardRef(function ArrowLeftIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M10 19l-7-7m0 0l7-7m-7 7h18" })));
});

const BadgeIcon = React.forwardRef(function BadgeIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.3984 2.49743C12.5138 2.07577 11.4862 2.07577 10.6016 2.49743L4.60158 5.35743C3.47041 5.89662 2.75 7.03808 2.75 8.29118V16.4657C2.75 17.8187 3.58824 19.0301 4.8544 19.5071L10.8544 21.7671C11.5928 22.0452 12.4072 22.0452 13.1456 21.7671L19.1456 19.5071C20.4118 19.0301 21.25 17.8187 21.25 16.4657V8.29118C21.25 7.03809 20.5296 5.89661 19.3984 5.35743L13.3984 2.49743ZM9.95615 1.14339C11.249 0.527115 12.751 0.527114 14.0438 1.14339L20.0438 4.00339C21.6971 4.79143 22.75 6.45973 22.75 8.29118V16.4657C22.75 18.4431 21.5249 20.2137 19.6743 20.9108L13.6743 23.1708C12.5951 23.5773 11.4049 23.5773 10.3257 23.1708L4.32567 20.9108C2.47512 20.2137 1.25 18.4431 1.25 16.4657V8.29118C1.25 6.45973 2.30291 4.79143 3.95615 4.00339L9.95615 1.14339ZM12 6.75C9.1005 6.75 6.75 9.1005 6.75 12C6.75 14.8995 9.1005 17.25 12 17.25C14.8995 17.25 17.25 14.8995 17.25 12C17.25 11.5858 17.5858 11.25 18 11.25C18.4142 11.25 18.75 11.5858 18.75 12C18.75 15.7279 15.7279 18.75 12 18.75C8.27208 18.75 5.25 15.7279 5.25 12C5.25 8.27208 8.27208 5.25 12 5.25C12.4142 5.25 12.75 5.58579 12.75 6C12.75 6.41421 12.4142 6.75 12 6.75ZM17.1172 7.92615C17.3525 7.5853 17.267 7.11819 16.9262 6.88283C16.5853 6.64748 16.1182 6.73301 15.8828 7.07386L12.2107 12.3921L10.4852 10.9281C10.1694 10.6601 9.69612 10.6989 9.42813 11.0148C9.16015 11.3306 9.19894 11.8039 9.51478 12.0719L11.8719 14.0719C12.0335 14.209 12.2454 14.2713 12.4555 14.2435C12.6655 14.2158 12.854 14.1005 12.9743 13.9262L17.1172 7.92615Z", fill: "currentColor" })));
});

const BankIcon = React.forwardRef(function BankIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2.04595 7.94542L11.5974 1.86725C11.843 1.71092 12.157 1.71092 12.4027 1.86725L21.9541 7.94542C22.5365 8.31606 22.7082 9.08867 22.3376 9.67109C22.1081 10.0317 21.7104 10.25 21.283 10.25H20.75V17.4184C21.3412 17.6993 21.75 18.3019 21.75 19V20C21.75 20.9665 20.9665 21.75 20 21.75H4.00001C3.03351 21.75 2.25001 20.9665 2.25001 20V19C2.25001 18.3019 2.65878 17.6993 3.25001 17.4184V10.25H2.71704C2.02669 10.25 1.46704 9.69036 1.46704 9C1.46704 8.57262 1.68539 8.17487 2.04595 7.94542ZM4.75001 10.25V17.25H7.25001V10.25H4.75001ZM8.75001 10.25V17.25H11.25V10.25H8.75001ZM12.75 10.25V17.25H15.25V10.25H12.75ZM16.75 10.25V17.25H19.25V10.25H16.75ZM16 18.75H12H8.00001H4.00001C3.86194 18.75 3.75001 18.8619 3.75001 19V20C3.75001 20.1381 3.86194 20.25 4.00001 20.25H20C20.1381 20.25 20.25 20.1381 20.25 20V19C20.25 18.8619 20.1381 18.75 20 18.75H16ZM20.425 8.75L12 3.388L3.57401 8.75H20.425Z", fill: "currentColor" })));
});

const BellIcon = React.forwardRef(function BellIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9.52353 2.06992C7.77676 2.4088 6.22358 3.39734 5.2092 4.82776C4.19387 6.25951 3.80163 8.01805 4.13109 9.7163C4.74544 12.883 4.42038 15.0015 3.98043 16.2971C3.75961 16.9474 3.50683 17.3994 3.31921 17.6797C3.22525 17.8201 3.14726 17.918 3.09726 17.9765C3.07225 18.0058 3.05423 18.0252 3.04471 18.0352L3.03737 18.0428C2.80017 18.2724 2.74081 18.6306 2.8921 18.9247C3.04461 19.2212 3.37456 19.3814 3.70189 19.3179L21.3724 15.8898C21.6997 15.8263 21.9458 15.5543 21.9764 15.2223C22.0068 14.8929 21.8178 14.5829 21.5119 14.4587L21.5023 14.4544C21.4897 14.4487 21.4657 14.4374 21.4316 14.4196C21.3633 14.384 21.2544 14.3224 21.1148 14.2273C20.8359 14.0375 20.4325 13.7128 19.9845 13.1923C19.092 12.1552 17.9983 10.3119 17.384 7.14519C17.0545 5.44695 16.0332 3.96265 14.5561 3.01443C13.0804 2.06708 11.2703 1.73105 9.52353 2.06992ZM18.8476 14.1707C19.034 14.3874 19.2163 14.5784 19.3908 14.7463L5.11219 17.5163C5.21121 17.2954 5.30885 17.0501 5.40077 16.7794C5.93323 15.2114 6.26233 12.8259 5.60364 9.43062C5.3543 8.14542 5.64811 6.80192 6.43276 5.69545C7.21835 4.58766 8.43162 3.80972 9.8092 3.54247C11.1868 3.27521 12.6029 3.54304 13.7458 4.2767C14.8872 5.0095 15.6621 6.14567 15.9115 7.43087C16.5701 10.8261 17.7674 12.9156 18.8476 14.1707ZM10.75 19C10.75 18.5858 10.4142 18.25 10 18.25C9.58581 18.25 9.25002 18.5858 9.25002 19C9.25002 19.9098 9.55244 20.8441 10.1895 21.5608C10.8388 22.2912 11.7965 22.75 13 22.75C14.2035 22.75 15.1613 22.2912 15.8106 21.5608C16.4476 20.8441 16.75 19.9098 16.75 19C16.75 18.5858 16.4142 18.25 16 18.25C15.5858 18.25 15.25 18.5858 15.25 19C15.25 19.5902 15.0524 20.1559 14.6895 20.5642C14.3388 20.9588 13.7965 21.25 13 21.25C12.2035 21.25 11.6613 20.9588 11.3106 20.5642C10.9476 20.1559 10.75 19.5902 10.75 19Z", fill: "currentColor" })));
});

const BigCheckIcon = React.forwardRef(function BigCheckIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M19.4697 6.21967C19.7626 5.92678 20.2374 5.92678 20.5303 6.21967C20.7966 6.48594 20.8208 6.9026 20.6029 7.19621L20.5303 7.28033L9.53033 18.2803C9.26406 18.5466 8.8474 18.5708 8.55379 18.3529L8.46967 18.2803L3.46967 13.2803C3.17678 12.9874 3.17678 12.5126 3.46967 12.2197C3.73594 11.9534 4.1526 11.9292 4.44621 12.1471L4.53033 12.2197L9 16.689L19.4697 6.21967Z", fill: "currentColor" })));
});

const BookIcon = React.forwardRef(function BookIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 2.25C1.58579 2.25 1.25 2.58579 1.25 3V18C1.25 18.4142 1.58579 18.75 2 18.75H9C9.59674 18.75 10.169 18.9871 10.591 19.409C11.0129 19.831 11.25 20.4033 11.25 21C11.25 21.4142 11.5858 21.75 12 21.75C12.4142 21.75 12.75 21.4142 12.75 21C12.75 20.4033 12.9871 19.831 13.409 19.409C13.831 18.9871 14.4033 18.75 15 18.75H22C22.4142 18.75 22.75 18.4142 22.75 18V3C22.75 2.58579 22.4142 2.25 22 2.25H16C14.7402 2.25 13.532 2.75044 12.6412 3.64124C12.3975 3.88496 12.183 4.15244 12 4.43823C11.817 4.15244 11.6025 3.88496 11.3588 3.64124C10.468 2.75044 9.25978 2.25 8 2.25H2ZM12.75 18C13.3953 17.516 14.1839 17.25 15 17.25H21.25V3.75H16C15.138 3.75 14.3114 4.09241 13.7019 4.7019C13.0924 5.3114 12.75 6.13805 12.75 7V18ZM11.25 18V7C11.25 6.13805 10.9076 5.3114 10.2981 4.7019C9.6886 4.09241 8.86195 3.75 8 3.75H2.75V17.25H9C9.81606 17.25 10.6047 17.516 11.25 18Z", fill: "currentColor" })));
});

const BoxIcon = React.forwardRef(function BoxIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M0.25 3C0.25 2.58579 0.585786 2.25 1 2.25H23C23.4142 2.25 23.75 2.58579 23.75 3V8C23.75 8.41421 23.4142 8.75 23 8.75H21.75V21C21.75 21.4142 21.4142 21.75 21 21.75H3C2.58579 21.75 2.25 21.4142 2.25 21V8.75H1C0.585786 8.75 0.25 8.41421 0.25 8V3ZM20.25 20.25V8.75H3.75V20.25H20.25ZM22.25 7.25H21H3H1.75V3.75H22.25V7.25ZM10 11.25C9.58579 11.25 9.25 11.5858 9.25 12C9.25 12.4142 9.58579 12.75 10 12.75H14C14.4142 12.75 14.75 12.4142 14.75 12C14.75 11.5858 14.4142 11.25 14 11.25H10Z", fill: "currentColor" })));
});

const BusinessIcon = React.forwardRef(function BusinessIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M6 10.25C6.3797 10.25 6.69349 10.5322 6.74315 10.8982L6.75 11V18C6.75 18.4142 6.41421 18.75 6 18.75C5.6203 18.75 5.30651 18.4678 5.25685 18.1018L5.25 18V11C5.25 10.5858 5.58579 10.25 6 10.25Z", fill: "currentColor" }),
        React.createElement("path", { d: "M18 10.25C18.3797 10.25 18.6935 10.5322 18.7432 10.8982L18.75 11V18C18.75 18.4142 18.4142 18.75 18 18.75C17.6203 18.75 17.3065 18.4678 17.2568 18.1018L17.25 18V11C17.25 10.5858 17.5858 10.25 18 10.25Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 2.25C15.4625 2.25 16.6584 3.3917 16.745 4.83248L16.75 5V6.25H20C21.5188 6.25 22.75 7.48122 22.75 9V19C22.75 20.5188 21.5188 21.75 20 21.75H4C2.48122 21.75 1.25 20.5188 1.25 19V9C1.25 7.48122 2.48122 6.25 4 6.25H7.25V5C7.25 3.53747 8.3917 2.3416 9.83248 2.25502L10 2.25H14ZM15.25 5V6.25H8.75V5L8.75645 4.87219C8.82047 4.24187 9.35279 3.75 10 3.75H14L14.1278 3.75645C14.7581 3.82047 15.25 4.35279 15.25 5ZM4 7.75H20C20.6904 7.75 21.25 8.30964 21.25 9V19C21.25 19.6904 20.6904 20.25 20 20.25H4C3.30964 20.25 2.75 19.6904 2.75 19V9C2.75 8.30964 3.30964 7.75 4 7.75Z", fill: "currentColor" })));
});

const CalendarIcon = React.forwardRef(function CalendarIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M16.7432 1.89823C16.6935 1.53215 16.3797 1.25 16 1.25C15.5858 1.25 15.25 1.58579 15.25 2V3.25H8.75V2L8.74315 1.89823C8.69349 1.53215 8.3797 1.25 8 1.25C7.58579 1.25 7.25 1.58579 7.25 2V3.25H5C3.48122 3.25 2.25 4.48122 2.25 6V10V19C2.25 20.5188 3.48122 21.75 5 21.75H19C20.5188 21.75 21.75 20.5188 21.75 19V10V6C21.75 4.48122 20.5188 3.25 19 3.25H16.75V2L16.7432 1.89823ZM20.25 9.25V6C20.25 5.30964 19.6904 4.75 19 4.75H16.75V6C16.75 6.41421 16.4142 6.75 16 6.75C15.6203 6.75 15.3065 6.46785 15.2568 6.10177L15.25 6V4.75H8.75V6C8.75 6.41421 8.41421 6.75 8 6.75C7.6203 6.75 7.30651 6.46785 7.25685 6.10177L7.25 6V4.75H5C4.30964 4.75 3.75 5.30964 3.75 6V9.25H20.25ZM3.75 10.75H20.25V19C20.25 19.6904 19.6904 20.25 19 20.25H5C4.30964 20.25 3.75 19.6904 3.75 19V10.75Z", fill: "currentColor" })));
});

const CameraIcon = React.forwardRef(function CameraIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9 2.25C8.74924 2.25 8.51506 2.37533 8.37596 2.58397L6.59861 5.25H4C2.48579 5.25 1.25 6.48579 1.25 8V19C1.25 19.7293 1.53973 20.4288 2.05546 20.9445C2.57118 21.4603 3.27065 21.75 4 21.75H20C20.7293 21.75 21.4288 21.4603 21.9445 20.9445C22.4603 20.4288 22.75 19.7293 22.75 19V8C22.75 7.27065 22.4603 6.57118 21.9445 6.05546C21.4288 5.53973 20.7293 5.25 20 5.25H17.4014L15.624 2.58397C15.4849 2.37533 15.2508 2.25 15 2.25H9ZM7.62404 6.41603L9.40139 3.75H14.5986L16.376 6.41603C16.5151 6.62467 16.7492 6.75 17 6.75H20C20.3315 6.75 20.6495 6.8817 20.8839 7.11612C21.1183 7.35054 21.25 7.66848 21.25 8V19C21.25 19.3315 21.1183 19.6495 20.8839 19.8839C20.6495 20.1183 20.3315 20.25 20 20.25H4C3.66848 20.25 3.35054 20.1183 3.11612 19.8839C2.8817 19.6495 2.75 19.3315 2.75 19V8C2.75 7.31421 3.31421 6.75 4 6.75H7C7.25076 6.75 7.48494 6.62467 7.62404 6.41603ZM8.75 13C8.75 11.2051 10.2051 9.75 12 9.75C13.7949 9.75 15.25 11.2051 15.25 13C15.25 14.7949 13.7949 16.25 12 16.25C10.2051 16.25 8.75 14.7949 8.75 13ZM12 8.25C9.37665 8.25 7.25 10.3766 7.25 13C7.25 15.6234 9.37665 17.75 12 17.75C14.6234 17.75 16.75 15.6234 16.75 13C16.75 10.3766 14.6234 8.25 12 8.25Z", fill: "currentColor" })));
});

const CardIcon = React.forwardRef(function CardIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4 3.25H20C21.5188 3.25 22.75 4.48122 22.75 6V18C22.75 19.5188 21.5188 20.75 20 20.75H4C2.48122 20.75 1.25 19.5188 1.25 18V6C1.25 4.48122 2.48122 3.25 4 3.25ZM20 4.75H4C3.30964 4.75 2.75 5.30964 2.75 6V18C2.75 18.6904 3.30964 19.25 4 19.25H20C20.6904 19.25 21.25 18.6904 21.25 18V6C21.25 5.30964 20.6904 4.75 20 4.75ZM18.25 8.75C18.6642 8.75 19 9.08579 19 9.5C19 9.91421 18.6642 10.25 18.25 10.25H5.75C5.33579 10.25 5 9.91421 5 9.5C5 9.08579 5.33579 8.75 5.75 8.75H18.25ZM8.75 15C8.75 14.5858 8.41421 14.25 8 14.25H6L5.89823 14.2568C5.53215 14.3065 5.25 14.6203 5.25 15C5.25 15.4142 5.58579 15.75 6 15.75H8L8.10177 15.7432C8.46785 15.6935 8.75 15.3797 8.75 15Z", fill: "currentColor" })));
});

const CardsIcon = React.forwardRef(function CardsIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.93359 10.8838C3.71781 9.66002 4.53493 8.49304 5.7587 8.27726C6.98246 8.06147 8.14944 8.8786 8.36522 10.1024C8.58101 11.3261 7.76388 12.4931 6.54011 12.7089C5.31635 12.9247 4.14937 12.1075 3.93359 10.8838ZM6.88801 10.3628C6.81608 9.95492 6.42709 9.68254 6.01917 9.75447C5.61125 9.82639 5.33887 10.2154 5.4108 10.6233C5.48273 11.0312 5.87172 11.3036 6.27964 11.2317C6.68756 11.1598 6.95994 10.7708 6.88801 10.3628Z", fill: "currentColor" }),
        React.createElement("path", { d: "M7.84308 15.7793C7.77115 15.3714 7.38215 15.099 6.97423 15.1709L5.49702 15.4314L5.39799 15.4558C5.0461 15.5683 4.82272 15.9263 4.88865 16.3002C4.96058 16.7081 5.34957 16.9805 5.75749 16.9086L7.23471 16.6481L7.33374 16.6237C7.68563 16.5112 7.90901 16.1532 7.84308 15.7793Z", fill: "currentColor" }),
        React.createElement("path", { d: "M11.7823 15.0847C11.7104 14.6768 11.3214 14.4044 10.9135 14.4763L9.43625 14.7368L9.33722 14.7612C8.98533 14.8737 8.76195 15.2317 8.82788 15.6056C8.89981 16.0136 9.2888 16.2859 9.69672 16.214L11.1739 15.9535L11.273 15.9291C11.6249 15.8166 11.8482 15.4586 11.7823 15.0847Z", fill: "currentColor" }),
        React.createElement("path", { d: "M14.8527 13.7817C15.2606 13.7098 15.6496 13.9822 15.7215 14.3901C15.7875 14.764 15.5641 15.122 15.2122 15.2345L15.1132 15.2589L13.636 15.5194C13.228 15.5913 12.839 15.319 12.7671 14.911C12.7012 14.5371 12.9246 14.1791 13.2764 14.0666L13.3755 14.0422L14.8527 13.7817Z", fill: "currentColor" }),
        React.createElement("path", { d: "M19.6608 13.6955C19.5888 13.2876 19.1998 13.0152 18.7919 13.0871L17.3147 13.3476L17.2157 13.372C16.8638 13.4845 16.6404 13.8425 16.7063 14.2164C16.7783 14.6244 17.1673 14.8967 17.5752 14.8248L19.0524 14.5643L19.1514 14.5399C19.5033 14.4275 19.7267 14.0694 19.6608 13.6955Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23.4549 15.0573L22.9792 12.3594L23.0757 12.0125L23.6403 9.74921L23.6759 9.58545C23.9206 8.27331 23.1829 6.97983 21.9483 6.51278L21.7184 5.20927C21.4547 3.71356 20.0284 2.71485 18.5327 2.97858L12.2765 4.08171L6.1125 2.54486L5.94874 2.5092C4.52982 2.24465 3.13272 3.1288 2.7789 4.54789L2.45869 5.83219C1.14191 6.22777 0.300422 7.55539 0.545044 8.94271L1.01763 11.6229C1.01088 11.6427 1.00491 11.663 0.999752 11.6837L0.35968 14.2508L0.324022 14.4146C0.0793811 15.7267 0.817054 17.0202 2.05168 17.4873L2.28153 18.7908C2.54526 20.2865 3.97157 21.2852 5.46728 21.0215L11.7235 19.9183L17.8874 21.4552L18.0512 21.4909C19.4701 21.7554 20.8672 20.8713 21.221 19.4522L21.5412 18.1679C22.858 17.7723 23.6995 16.4447 23.4549 15.0573ZM5.74961 4.0003L8.64462 4.72211L4.08074 5.52685L4.23434 4.91077L4.27152 4.78832C4.48612 4.19221 5.12163 3.84373 5.74961 4.0003ZM11.5537 18.4251C11.5937 18.4146 11.6346 18.4073 11.6763 18.4035L20.9637 16.7659C21.6436 16.646 22.0976 15.9977 21.9777 15.3178L20.2412 5.46974C20.1213 4.78987 19.473 4.33591 18.7931 4.45579L3.03621 7.23416C2.35634 7.35404 1.90238 8.00237 2.02226 8.68223L3.75874 18.5303C3.87862 19.2102 4.52694 19.6641 5.20681 19.5443L11.5537 18.4251ZM18.2503 19.9998L15.3553 19.2779L19.9192 18.4732L19.7656 19.0893L19.7284 19.2117C19.5138 19.8078 18.8783 20.1563 18.2503 19.9998Z", fill: "currentColor" })));
});

const CartIcon = React.forwardRef(function CartIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 2.25201C1.58579 2.25201 1.25 2.5878 1.25 3.00201C1.25 3.41623 1.58579 3.75201 2 3.75201H4.36465L5.98167 13.4541C6.02993 13.7436 6.12234 14.0163 6.25132 14.2656C5.12569 14.3894 4.25 15.3434 4.25 16.502C4.25 17.7447 5.25736 18.752 6.5 18.752H18C18.4142 18.752 18.75 18.4162 18.75 18.002C18.75 17.5878 18.4142 17.252 18 17.252H6.5C6.08579 17.252 5.75 16.9162 5.75 16.502C5.75 16.0878 6.08579 15.752 6.5 15.752H17.3057C18.6501 15.752 19.7973 14.7801 20.0183 13.4541L21.0731 7.12531C21.1094 6.90785 21.0481 6.68545 20.9056 6.51723C20.7631 6.34901 20.5538 6.25201 20.3333 6.25201H6.5C6.43299 6.25201 6.36804 6.2608 6.30622 6.27729L5.7398 2.87871C5.67952 2.51707 5.36663 2.25201 5 2.25201H2ZM17.3057 14.252C17.9168 14.252 18.4383 13.8102 18.5387 13.2075L19.448 7.75201H6.55201L7.46126 13.2075C7.56172 13.8102 8.0832 14.252 8.69425 14.252H17.3057Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15.25 20.502C15.25 19.8117 15.8096 19.252 16.5 19.252C17.1904 19.252 17.75 19.8117 17.75 20.502C17.75 21.1924 17.1904 21.752 16.5 21.752C15.8096 21.752 15.25 21.1924 15.25 20.502Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.5 19.252C7.80964 19.252 7.25 19.8117 7.25 20.502C7.25 21.1924 7.80964 21.752 8.5 21.752C9.19036 21.752 9.75 21.1924 9.75 20.502C9.75 19.8117 9.19036 19.252 8.5 19.252Z", fill: "currentColor" })));
});

const CheckIcon = React.forwardRef(function CheckIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "currentColor", viewBox: "0 0 20 20", width: 20, height: 20 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", d: "M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z", clipRule: "evenodd" })));
});

const CheckInCircleIcon = React.forwardRef(function CheckInCircleIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", fill: "none", width: 16, height: 16 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM11.6306 5.3579C11.276 5.00961 10.7062 5.01474 10.3579 5.36936L6.874 8.915L5.775 7.796L5.73875 7.73581C5.57612 7.50259 5.30588 7.35 5 7.35C4.50294 7.35 4.1 7.75294 4.1 8.25V8.29091L4.10763 8.40784C4.13289 8.60067 4.22016 8.7813 4.3579 8.92155L6.2329 10.8306L6.32553 10.9129C6.68052 11.1873 7.19376 11.1599 7.5171 10.8306L11.6421 6.63064L11.7218 6.53775C11.9871 6.18258 11.9557 5.67716 11.6306 5.3579Z", fill: "currentColor" })));
});

const CheckInCircleOutlineIcon = React.forwardRef(function CheckInCircleOutlineIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C7.30558 20.5 3.5 16.6944 3.5 12C3.5 7.30558 7.30558 3.5 12 3.5ZM17.0384 8.72789C16.7501 8.43053 16.2753 8.42322 15.9779 8.71157L10.312 14.205L8.02211 11.9843L7.93688 11.913C7.63996 11.6997 7.22371 11.7303 6.96157 12.0006C6.67322 12.298 6.68053 12.7728 6.97789 13.0612L9.79039 15.7884L9.88247 15.8646C10.1721 16.0677 10.5728 16.0423 10.8346 15.7884L17.0221 9.78843L17.096 9.70543C17.3184 9.41521 17.3006 8.99822 17.0384 8.72789Z", fill: "currentColor" })));
});

const CheckedIcon = React.forwardRef(function CheckedIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: 24, height: 24, viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.5303 5.96967C17.8232 6.26256 17.8232 6.73744 17.5303 7.03033L11.2511 13.3096C10.9582 13.6025 10.4833 13.6025 10.1904 13.3096C9.89751 13.0167 9.89751 12.5418 10.1904 12.2489L16.4697 5.96967C16.7626 5.67678 17.2374 5.67678 17.5303 5.96967ZM22.5303 5.96967C22.8232 6.26256 22.8232 6.73744 22.5303 7.03033L11.5303 18.0303C11.2374 18.3232 10.7626 18.3232 10.4697 18.0303L5.96967 13.5303C5.67678 13.2374 5.67678 12.7626 5.96967 12.4697C6.26256 12.1768 6.73744 12.1768 7.03033 12.4697L11 16.4393L21.4697 5.96967C21.7626 5.67678 22.2374 5.67678 22.5303 5.96967ZM1.46967 12.4697C1.76256 12.1768 2.23744 12.1768 2.53033 12.4697L6.77832 16.7177C7.07122 17.0106 7.07122 17.4854 6.77832 17.7783C6.48543 18.0712 6.01056 18.0712 5.71766 17.7783L1.46967 13.5303C1.17678 13.2374 1.17678 12.7626 1.46967 12.4697Z", fill: "currentColor" })));
});

const ChevronDoubleRightIcon = React.forwardRef(function ChevronDoubleRightIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", fill: "none", width: 16, height: 16 }, props, { ref: ref }),
        React.createElement("path", { d: "M8.31442 13.3536C8.49193 13.5311 8.7697 13.5472 8.96544 13.402L9.02152 13.3536L13.6882 8.68689C13.8657 8.50938 13.8818 8.2316 13.7366 8.03586L13.6882 7.97978L9.02152 3.31311C8.82626 3.11785 8.50968 3.11785 8.31441 3.31311C8.1369 3.49062 8.12077 3.7684 8.266 3.96414L8.31441 4.02022L12.6273 8.33333L8.31442 12.6464C8.1369 12.824 8.12077 13.1017 8.266 13.2975L8.31442 13.3536Z", fill: "currentColor" }),
        React.createElement("path", { d: "M2.14645 13.1868C2.32396 13.3643 2.60173 13.3805 2.79747 13.2352L2.85355 13.1868L7.52022 8.52014C7.69773 8.34263 7.71387 8.06485 7.56863 7.86911L7.52022 7.81303L2.85355 3.14637C2.65829 2.9511 2.34171 2.9511 2.14645 3.14637C1.96893 3.32388 1.9528 3.60165 2.09803 3.79739L2.14645 3.85347L6.45933 8.16658L2.14645 12.4797C1.96894 12.6572 1.9528 12.935 2.09803 13.1307L2.14645 13.1868Z", fill: "currentColor" })));
});

const ChevronDownIcon = React.forwardRef(function ChevronDownIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M19.5303 8.21967C19.7966 8.48594 19.8208 8.9026 19.6029 9.19621L19.5303 9.28033L12.5303 16.2803C12.2641 16.5466 11.8474 16.5708 11.5538 16.3529L11.4697 16.2803L4.46967 9.28033C4.17678 8.98744 4.17678 8.51256 4.46967 8.21967C4.73594 7.9534 5.1526 7.9292 5.44621 8.14705L5.53033 8.21967L12 14.689L18.4697 8.21967C18.7359 7.9534 19.1526 7.9292 19.4462 8.14705L19.5303 8.21967Z", fill: "currentColor" })));
});

const ChevronLeftIcon = React.forwardRef(function ChevronLeftIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", xmlns: "http://www.w3.org/2000/svg", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M10.5303 2.46967C10.2641 2.2034 9.8474 2.1792 9.55379 2.39705L9.46967 2.46967L4.46967 7.46967C4.2034 7.73594 4.1792 8.1526 4.39705 8.44621L4.46967 8.53033L9.46967 13.5303C9.76256 13.8232 10.2374 13.8232 10.5303 13.5303C10.7966 13.2641 10.8208 12.8474 10.6029 12.5538L10.5303 12.4697L6.061 8L10.5303 3.53033C10.7966 3.26406 10.8208 2.8474 10.6029 2.55379L10.5303 2.46967Z", fill: "currentColor" })));
});

const ChevronRightIcon = React.forwardRef(function ChevronRightIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", fill: "none", width: 16, height: 16 }, props, { ref: ref }),
        React.createElement("path", { d: "M5.46967 2.46967C5.73594 2.2034 6.1526 2.1792 6.44621 2.39705L6.53033 2.46967L11.5303 7.46967C11.7966 7.73594 11.8208 8.1526 11.6029 8.44621L11.5303 8.53033L6.53033 13.5303C6.23744 13.8232 5.76256 13.8232 5.46967 13.5303C5.2034 13.2641 5.1792 12.8474 5.39705 12.5538L5.46967 12.4697L9.939 8L5.46967 3.53033C5.2034 3.26406 5.1792 2.8474 5.39705 2.55379L5.46967 2.46967Z", fill: "currentColor" })));
});

const ChevronUpIcon = withDefaultSvgProps(function ChevronUp(props) {
    return (React.createElement("svg", Object.assign({ fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", width: 24, height: 24 }, props),
        React.createElement("path", { d: "M19.5303 16.2803C19.7966 16.0141 19.8208 15.5974 19.6029 15.3038L19.5303 15.2197L12.5303 8.21967C12.2641 7.9534 11.8474 7.9292 11.5538 8.14705L11.4697 8.21967L4.46967 15.2197C4.17678 15.5126 4.17678 15.9874 4.46967 16.2803C4.73594 16.5466 5.1526 16.5708 5.44621 16.3529L5.53033 16.2803L12 9.811L18.4697 16.2803C18.7359 16.5466 19.1526 16.5708 19.4462 16.3529L19.5303 16.2803Z", fill: "currentColor" })));
});

const CircleIcon = React.forwardRef(function CircleIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 20 20", fill: "currentColor", width: 20, height: 20 }, props, { ref: ref }),
        React.createElement("circle", { cx: "10", cy: "10", r: "10" })));
});

const ClipboardIcon = React.forwardRef(function ClipboardIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: 24, height: 24, viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.25 10.9932C8.25 10.579 8.58579 10.2432 9 10.2432H9.01C9.42421 10.2432 9.76 10.579 9.76 10.9932C9.76 11.4074 9.42421 11.7432 9.01 11.7432H9C8.58579 11.7432 8.25 11.4074 8.25 10.9932ZM11.25 10.9932C11.25 10.579 11.5858 10.2432 12 10.2432H15C15.4142 10.2432 15.75 10.579 15.75 10.9932C15.75 11.4074 15.4142 11.7432 15 11.7432H12C11.5858 11.7432 11.25 11.4074 11.25 10.9932ZM8.25 14.9932C8.25 14.579 8.58579 14.2432 9 14.2432H9.01C9.42421 14.2432 9.76 14.579 9.76 14.9932C9.76 15.4074 9.42421 15.7432 9.01 15.7432H9C8.58579 15.7432 8.25 15.4074 8.25 14.9932ZM11.25 14.9932C11.25 14.579 11.5858 14.2432 12 14.2432H15C15.4142 14.2432 15.75 14.579 15.75 14.9932C15.75 15.4074 15.4142 15.7432 15 15.7432H12C11.5858 15.7432 11.25 15.4074 11.25 14.9932Z", fill: "currentColor" }),
        React.createElement("path", { d: "M8.01445 4.25839C8.00491 4.33759 8 4.41822 8 4.5V5.5C8 5.58756 8.00563 5.6738 8.01654 5.75839H7C6.72385 5.75839 6.5 5.98225 6.5 6.25839V19.5C6.5 19.7761 6.72386 20 7 20H17C17.2761 20 17.5 19.7761 17.5 19.5V6.25839C17.5 5.98225 17.2761 5.75839 17 5.75839H15.9835C15.9944 5.6738 16 5.58756 16 5.5V4.5C16 4.41822 15.9951 4.33759 15.9856 4.25839H17C18.1046 4.25839 19 5.15382 19 6.25839V19.5C19 20.6046 18.1046 21.5 17 21.5H7C5.89543 21.5 5 20.6046 5 19.5V6.25839C5 5.15382 5.89543 4.25839 7 4.25839H8.01445Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 4H10C9.72386 4 9.5 4.22386 9.5 4.5V5.5C9.5 5.77614 9.72386 6 10 6H14C14.2761 6 14.5 5.77614 14.5 5.5V4.5C14.5 4.22386 14.2761 4 14 4ZM10 2.5C8.89543 2.5 8 3.39543 8 4.5V5.5C8 6.60457 8.89543 7.5 10 7.5H14C15.1046 7.5 16 6.60457 16 5.5V4.5C16 3.39543 15.1046 2.5 14 2.5H10Z", fill: "currentColor" })));
});

const ClockIcon = React.forwardRef(function ClockIcon(providedProps, ref) {
    const _a = useDefaultSvgProps(providedProps), { width = 24 } = _a, props = __rest(_a, ["width"]);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: width }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2ZM12 3.5C16.6944 3.5 20.5 7.30558 20.5 12C20.5 16.6944 16.6944 20.5 12 20.5C7.30558 20.5 3.5 16.6944 3.5 12C3.5 7.30558 7.30558 3.5 12 3.5ZM12.7432 6.89823C12.6935 6.53215 12.3797 6.25 12 6.25C11.5858 6.25 11.25 6.58579 11.25 7V13L11.2574 13.1052C11.2916 13.3469 11.4423 13.5597 11.6646 13.6708L15.6646 15.6708L15.7587 15.7102C16.1083 15.8295 16.501 15.675 16.6708 15.3354L16.7102 15.2413C16.8295 14.8917 16.675 14.499 16.3354 14.3292L12.75 12.537V7L12.7432 6.89823Z", fill: "currentColor" })));
});

const CogIcon = React.forwardRef(function CogIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25ZM12 9.75C13.2426 9.75 14.25 10.7574 14.25 12C14.25 13.2426 13.2426 14.25 12 14.25C10.7574 14.25 9.75 13.2426 9.75 12C9.75 10.7574 10.7574 9.75 12 9.75Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11 1.75L10.8565 1.7558C9.95711 1.82881 9.25 2.58183 9.25 3.5L9.249 4.753L9.18075 4.77896L9.03252 4.83865L8.82 4.931L7.93429 4.0452C7.25072 3.36163 6.14268 3.36163 5.45926 4.04505L4.04505 5.45926L3.93154 5.5841C3.364 6.27157 3.40183 7.29092 4.04505 7.93414L4.931 8.82L4.90157 8.88518C4.85876 8.9826 4.81797 9.08093 4.77921 9.18011L4.754 9.249L3.49956 9.25C2.5335 9.25 1.75 10.0335 1.75 11V13L1.7558 13.1435C1.82881 14.0429 2.58183 14.75 3.5 14.75H4.753L4.77896 14.8193L4.83865 14.9675L4.931 15.179L4.0452 16.0657C3.36163 16.7493 3.36163 17.8573 4.04505 18.5407L5.45926 19.955L5.5841 20.0685C6.27157 20.636 7.29092 20.5982 7.93414 19.955L8.82 19.068L8.8856 19.0986C8.9831 19.1414 9.0815 19.1823 9.18075 19.221L9.249 19.246L9.25 20.5C9.25 21.4665 10.0335 22.25 11 22.25H13L13.1435 22.2442C14.0429 22.1712 14.75 21.4182 14.75 20.5L14.751 19.245L14.968 19.1611L15.179 19.068L16.0657 19.9548C16.7493 20.6384 17.8573 20.6384 18.5407 19.955L19.955 18.5407L20.0685 18.4159C20.636 17.7284 20.5982 16.7091 19.955 16.0659L19.068 15.179L19.0984 15.1148C19.1412 15.0174 19.182 14.9191 19.2208 14.8199L19.245 14.75H20.5004C21.4665 14.75 22.25 13.9665 22.25 13V11L22.2442 10.8565C22.1712 9.95711 21.4182 9.25 20.5 9.25L19.246 9.249L19.221 9.18075L19.1614 9.03252L19.068 8.82L19.9548 7.93429C20.6384 7.25072 20.6384 6.14268 19.955 5.45926L18.5407 4.04505L18.4159 3.93154C17.7284 3.364 16.7091 3.40183 16.0659 4.04505L15.179 4.931L15.1148 4.90157C15.0174 4.85876 14.9191 4.81797 14.8199 4.77921L14.75 4.754V3.49956C14.75 2.5335 13.9665 1.75 13 1.75H11ZM11 3.25H13C13.1381 3.25 13.25 3.36193 13.25 3.5L13.251 5.29063C13.2512 5.62223 13.4692 5.91435 13.787 6.009C14.199 6.1317 14.5963 6.29671 14.9733 6.50102C15.2652 6.65923 15.6264 6.60667 15.8611 6.37181L17.1267 5.10556C17.2242 5.00808 17.3824 5.00808 17.4801 5.10571L18.8943 6.51992C18.9919 6.61755 18.9919 6.77584 18.8943 6.87348L17.6282 8.13886C17.3933 8.37358 17.3408 8.73479 17.499 9.02671C17.7034 9.40395 17.8685 9.80153 17.9913 10.2139C18.0859 10.5319 18.3783 10.7499 18.7101 10.75L20.5 10.75C20.6381 10.75 20.75 10.8619 20.75 11V13C20.75 13.1381 20.6381 13.25 20.5 13.25L18.7094 13.251C18.3778 13.2512 18.0856 13.4692 17.991 13.787C17.8683 14.199 17.7033 14.5963 17.499 14.9733C17.3408 15.2652 17.3933 15.6264 17.6282 15.8611L18.8944 17.1267C18.9919 17.2242 18.9919 17.3824 18.8943 17.4801L17.4801 18.8943C17.3824 18.9919 17.2242 18.9919 17.1265 18.8943L15.8611 17.6282C15.6264 17.3933 15.2652 17.3408 14.9733 17.499C14.5963 17.7033 14.199 17.8683 13.787 17.991C13.4692 18.0856 13.2512 18.3778 13.251 18.7094L13.25 20.4996C13.25 20.6381 13.1381 20.75 13 20.75H11C10.8619 20.75 10.75 20.6381 10.75 20.5L10.75 18.7101C10.7499 18.3783 10.5319 18.0859 10.2139 17.9913C9.80153 17.8685 9.40395 17.7034 9.02671 17.499C8.73479 17.3408 8.37358 17.3933 8.13886 17.6282L6.87332 18.8944C6.77584 18.9919 6.61755 18.9919 6.51992 18.8943L5.10571 17.4801C5.00808 17.3824 5.00808 17.2242 5.10571 17.1265L6.37181 15.8611C6.60667 15.6264 6.65923 15.2652 6.50102 14.9733C6.29656 14.596 6.13146 14.1985 6.00873 13.7861C5.91408 13.4681 5.62172 13.2501 5.28991 13.25L3.50002 13.25C3.36193 13.25 3.25 13.1381 3.25 13V11C3.25 10.8619 3.36193 10.75 3.5 10.75L5.29063 10.749C5.62223 10.7488 5.91435 10.5308 6.009 10.213C6.1317 9.80096 6.29671 9.40368 6.50102 9.02671C6.65923 8.73479 6.60667 8.37358 6.37181 8.13886L5.10556 6.87332C5.00808 6.77584 5.00808 6.61755 5.10571 6.51992L6.51992 5.10571C6.61755 5.00808 6.77584 5.00808 6.87348 5.10571L8.13886 6.37181C8.37358 6.60667 8.73479 6.65923 9.02671 6.50102C9.40395 6.29656 9.80153 6.13146 10.2139 6.00873C10.5319 5.91408 10.7499 5.62172 10.75 5.28991L10.75 3.50002C10.75 3.36193 10.8619 3.25 11 3.25Z", fill: "currentColor" })));
});

const CompassIcon = React.forwardRef(function CompassIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12ZM12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM16.9148 8.02694C17 7.75863 16.9277 7.46521 16.7277 7.26712C16.5277 7.06903 16.2336 6.99962 15.9661 7.08739L9.56612 9.18739C9.33704 9.26256 9.15806 9.44327 9.0851 9.67307L7.0851 15.9731C6.99993 16.2414 7.07217 16.5348 7.27218 16.7329C7.4722 16.931 7.7663 17.0004 8.03377 16.9126L14.4338 14.8126C14.6629 14.7375 14.8418 14.5567 14.9148 14.3269L16.9148 8.02694ZM8.95805 15.0307L10.3986 10.4929L15.0418 8.96935L13.6013 13.5071L8.95805 15.0307Z", fill: "currentColor" })));
});

const ContactBookIcon = React.forwardRef(function ContactBookIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M4.82051 1C3.26279 1 2 2.25985 2 3.81395V6.88372C2 7.30757 2.3444 7.65116 2.76923 7.65116C3.19407 7.65116 3.53846 7.30757 3.53846 6.88372V3.81395C3.53846 3.10754 4.11246 2.53488 4.82051 2.53488H19.1795C19.8875 2.53488 20.4615 3.10754 20.4615 3.81395V20.186C20.4615 20.8925 19.8875 21.4651 19.1795 21.4651H4.82051C4.11246 21.4651 3.53846 20.8925 3.53846 20.186V17.1163C3.53846 16.6924 3.19407 16.3488 2.76923 16.3488C2.3444 16.3488 2 16.6924 2 17.1163V20.186C2 21.7402 3.26279 23 4.82051 23H19.1795C20.7372 23 22 21.7401 22 20.186V3.81395C22 2.25985 20.7372 1 19.1795 1H4.82051Z", fill: "currentColor" }),
        React.createElement("path", { d: "M3.53846 9.95349C3.53846 9.52964 3.19407 9.18605 2.76923 9.18605C2.3444 9.18605 2 9.52964 2 9.95349V14.0465C2 14.4704 2.3444 14.814 2.76923 14.814C3.19407 14.814 3.53846 14.4704 3.53846 14.0465V9.95349Z", fill: "currentColor" }),
        React.createElement("path", { d: "M11.9999 7.13953C11.3725 7.13953 10.6353 7.35759 10.0431 7.86089C9.42814 8.38355 9.00904 9.18185 9.00904 10.239C9.00904 11.0335 9.29774 11.8963 9.67565 12.5731C9.26706 12.6803 8.8348 12.8261 8.44972 13.0285C7.83047 13.3539 7.12821 13.9446 7.12821 14.9267V16.0929C7.12821 16.5168 7.4726 16.8604 7.89744 16.8604C8.32227 16.8604 8.66667 16.5168 8.66667 16.0929V14.9267C8.66667 14.7992 8.73642 14.6126 9.1668 14.3864C9.58573 14.1662 10.1557 14.0196 10.6936 13.919C11.1493 13.8338 11.3882 13.4786 11.4709 13.2053C11.5533 12.933 11.5445 12.5524 11.2929 12.2393C10.909 11.7618 10.5475 10.8972 10.5475 10.239C10.5475 9.62173 10.7763 9.25402 11.0408 9.02928C11.328 8.78517 11.7016 8.67442 11.9999 8.67442C12.2982 8.67442 12.6719 8.78518 12.9591 9.02934C13.2236 9.25412 13.4525 9.62186 13.4525 10.2391C13.4525 10.8973 13.091 11.7619 12.7071 12.2394C12.4555 12.5525 12.4467 12.933 12.5291 13.2054C12.6119 13.4787 12.8507 13.8339 13.3064 13.9191C13.8443 14.0197 14.4143 14.1663 14.8332 14.3865C15.2636 14.6127 15.3333 14.7993 15.3333 14.9268V16.093C15.3333 16.5169 15.6777 16.8605 16.1026 16.8605C16.5274 16.8605 16.8718 16.5169 16.8718 16.093V14.9268C16.8718 13.9447 16.1695 13.354 15.5503 13.0286C15.1652 12.8262 14.7329 12.6804 14.3244 12.5732C14.7023 11.8964 14.991 11.0336 14.991 10.2391C14.991 9.18189 14.5718 8.38358 13.9568 7.86092C13.3646 7.35762 12.6273 7.13953 11.9999 7.13953Z", fill: "currentColor" })));
});

const CookiesIcon = React.forwardRef(function CookiesIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M10.495 16.75C10.0808 16.75 9.745 17.0858 9.745 17.5C9.745 17.9142 10.0808 18.25 10.495 18.25H10.505C10.9192 18.25 11.255 17.9142 11.255 17.5C11.255 17.0858 10.9192 16.75 10.505 16.75H10.495Z", fill: "currentColor" }),
        React.createElement("path", { d: "M14.745 16.5C14.745 16.0858 15.0808 15.75 15.495 15.75H15.505C15.9192 15.75 16.255 16.0858 16.255 16.5C16.255 16.9142 15.9192 17.25 15.505 17.25H15.495C15.0808 17.25 14.745 16.9142 14.745 16.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M6.495 14.75C6.08078 14.75 5.745 15.0858 5.745 15.5C5.745 15.9142 6.08078 16.25 6.495 16.25H6.505C6.91921 16.25 7.255 15.9142 7.255 15.5C7.255 15.0858 6.91921 14.75 6.505 14.75H6.495Z", fill: "currentColor" }),
        React.createElement("path", { d: "M9.745 12.5C9.745 12.0858 10.0808 11.75 10.495 11.75H10.505C10.9192 11.75 11.255 12.0858 11.255 12.5C11.255 12.9142 10.9192 13.25 10.505 13.25H10.495C10.0808 13.25 9.745 12.9142 9.745 12.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M14.495 11.75C14.0808 11.75 13.745 12.0858 13.745 12.5C13.745 12.9142 14.0808 13.25 14.495 13.25H14.505C14.9192 13.25 15.255 12.9142 15.255 12.5C15.255 12.0858 14.9192 11.75 14.505 11.75H14.495Z", fill: "currentColor" }),
        React.createElement("path", { d: "M8.745 6.5C8.745 6.08579 9.08078 5.75 9.495 5.75H9.505C9.91921 5.75 10.255 6.08579 10.255 6.5C10.255 6.91421 9.91921 7.25 9.505 7.25H9.495C9.08078 7.25 8.745 6.91421 8.745 6.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M6.495 8.75C6.08078 8.75 5.745 9.08579 5.745 9.5C5.745 9.91421 6.08078 10.25 6.495 10.25H6.505C6.91921 10.25 7.255 9.91421 7.255 9.5C7.255 9.08579 6.91921 8.75 6.505 8.75H6.495Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.7622 22.75 22.4652 18.2167 22.7376 12.5215C22.7503 12.2548 22.6203 12.0014 22.3962 11.8562C22.172 11.711 21.8876 11.696 21.6494 11.8167C20.4706 12.414 19.0331 12.1436 17.9616 11.31C16.8955 10.4806 16.3419 9.21655 16.736 7.9533C16.8096 7.71712 16.7621 7.45981 16.6089 7.26555C16.4557 7.07129 16.2166 6.96508 15.9697 6.98166C14.2652 7.09615 13.2879 6.3296 12.794 5.37237C12.2668 4.35052 12.2868 3.10343 12.6708 2.33541C12.7871 2.10292 12.7746 1.82681 12.638 1.6057C12.5013 1.38459 12.2599 1.25 12 1.25ZM2.75 12C2.75 7.24223 6.34203 3.32343 10.9621 2.80758C10.7803 3.85482 10.9377 5.04593 11.461 6.06012C12.1124 7.32283 13.3292 8.30947 15.1181 8.47004C14.9948 10.1005 15.8458 11.5644 17.0405 12.4939C18.1516 13.3583 19.6517 13.8357 21.1184 13.564C20.3754 17.9276 16.5755 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12Z", fill: "currentColor" })));
});

const CreditCardIcon = React.forwardRef(function CreditCardIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M13.5 15.25C13.9142 15.25 14.25 15.5858 14.25 16C14.25 16.3797 13.9678 16.6935 13.6018 16.7432L13.5 16.75H10.5C10.0858 16.75 9.75 16.4142 9.75 16C9.75 15.6203 10.0322 15.3065 10.3982 15.2568L10.5 15.25H13.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M19.25 16C19.25 15.5858 18.9142 15.25 18.5 15.25H16L15.8982 15.2568C15.5322 15.3065 15.25 15.6203 15.25 16C15.25 16.4142 15.5858 16.75 16 16.75H18.5L18.6018 16.7432C18.9678 16.6935 19.25 16.3797 19.25 16Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4.75 9.5C4.75 7.98122 5.98122 6.75 7.5 6.75C9.01878 6.75 10.25 7.98122 10.25 9.5C10.25 11.0188 9.01878 12.25 7.5 12.25C5.98122 12.25 4.75 11.0188 4.75 9.5ZM8.75 9.5C8.75 8.80964 8.19036 8.25 7.5 8.25C6.80964 8.25 6.25 8.80964 6.25 9.5C6.25 10.1904 6.80964 10.75 7.5 10.75C8.19036 10.75 8.75 10.1904 8.75 9.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M8.75 16C8.75 15.5858 8.41421 15.25 8 15.25H5.5L5.39823 15.2568C5.03215 15.3065 4.75 15.6203 4.75 16C4.75 16.4142 5.08579 16.75 5.5 16.75H8L8.10177 16.7432C8.46785 16.6935 8.75 16.3797 8.75 16Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4 3.25H20C21.5188 3.25 22.75 4.48122 22.75 6V18C22.75 19.5188 21.5188 20.75 20 20.75H4C2.48122 20.75 1.25 19.5188 1.25 18V6C1.25 4.48122 2.48122 3.25 4 3.25ZM20 4.75H4C3.30964 4.75 2.75 5.30964 2.75 6V18C2.75 18.6904 3.30964 19.25 4 19.25H20C20.6904 19.25 21.25 18.6904 21.25 18V6C21.25 5.30964 20.6904 4.75 20 4.75Z", fill: "currentColor" })));
});

const CrossIcon = React.forwardRef(function CrossIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M5.44621 4.39705L5.53033 4.46967L12 10.939L18.4697 4.46967C18.7626 4.17678 19.2374 4.17678 19.5303 4.46967C19.7966 4.73594 19.8208 5.1526 19.6029 5.44621L19.5303 5.53033L13.061 12L19.5303 18.4697C19.8232 18.7626 19.8232 19.2374 19.5303 19.5303C19.2641 19.7966 18.8474 19.8208 18.5538 19.6029L18.4697 19.5303L12 13.061L5.53033 19.5303C5.23744 19.8232 4.76256 19.8232 4.46967 19.5303C4.2034 19.2641 4.1792 18.8474 4.39705 18.5538L4.46967 18.4697L10.939 12L4.46967 5.53033C4.17678 5.23744 4.17678 4.76256 4.46967 4.46967C4.70635 4.23299 5.06186 4.18756 5.34416 4.3334L5.44621 4.39705Z", fill: "currentColor" })));
});

const CrossInCircleIcon = React.forwardRef(function CrossInCircleIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", fill: "none", width: 16, height: 16 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM9.6636 5.0636C10.0151 4.71213 10.5849 4.71213 10.9364 5.0636C11.2586 5.38579 11.2854 5.89147 11.0169 6.24423L10.9364 6.3364L9.27279 8L10.9364 9.6636C11.2879 10.0151 11.2879 10.5849 10.9364 10.9364C10.6142 11.2586 10.1085 11.2854 9.75577 11.0169L9.6636 10.9364L8 9.27279L6.3364 10.9364C5.98492 11.2879 5.41508 11.2879 5.0636 10.9364C4.74142 10.6142 4.71457 10.1085 4.98306 9.75577L5.0636 9.6636L6.72721 8L5.0636 6.3364C4.71213 5.98492 4.71213 5.41508 5.0636 5.0636C5.38579 4.74142 5.89147 4.71457 6.24423 4.98306L6.3364 5.0636L8 6.72721L9.6636 5.0636Z", fill: "currentColor" })));
});

const DiamondIcon = React.forwardRef(function DiamondIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18.827 2.25H5.17305C4.31289 2.25 3.52537 2.73233 3.13451 3.49856L0.331916 8.99252C0.192991 9.26486 0.233275 9.59427 0.433735 9.8251L10.6787 21.6223C10.7325 21.6843 10.7906 21.7424 10.8526 21.7962C11.5823 22.4299 12.6876 22.3521 13.3213 21.6223L23.5663 9.8251C23.7667 9.59427 23.807 9.26486 23.6681 8.99252L20.8655 3.49856C20.4746 2.73233 19.6871 2.25 18.827 2.25ZM5.17305 3.75H10.6071L7.73201 7.735L5.84959 5.22944L5.77651 5.14573C5.51642 4.88878 5.10049 4.8542 4.79945 5.08042C4.46831 5.32925 4.40159 5.79942 4.65043 6.13056L6.61881 8.75H2.13932L4.47069 4.18018C4.60536 3.91618 4.87669 3.75 5.17305 3.75ZM2.78984 10.25L10.537 19.1715L7.29517 10.2563C7.2944 10.2542 7.29365 10.2521 7.2929 10.25H2.78984ZM8.88897 10.25L12 18.8054L14.4383 12.1001L14.4795 12.0068C14.6513 11.6797 15.0426 11.5218 15.3995 11.6515C15.7887 11.7931 15.9895 12.2234 15.848 12.6127L13.4631 19.1712L21.2093 10.25H8.88897ZM21.8598 8.75L19.5293 4.18018C19.4115 3.94918 19.189 3.79307 18.9367 3.75766L18.827 3.75H13.5384L16.393 7.709L18.1378 5.2452C18.3554 4.93784 18.762 4.84371 19.0881 5.00893L19.1833 5.06632C19.4907 5.28386 19.5848 5.69048 19.4196 6.01662L19.3622 6.11178L17.4949 8.75H21.8598ZM15.2941 8.75L12.072 4.282L8.84981 8.75H15.2941Z", fill: "currentColor" })));
});

const DocumentIcon = React.forwardRef(function DocumentIcon(providedProps, ref) {
    const _a = useDefaultSvgProps(providedProps), { width = 24 } = _a, props = __rest(_a, ["width"]);
    return (React.createElement("svg", Object.assign({ xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 24 24", width: width, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.25 6.75C7.55964 6.75 7 7.30964 7 8V10C7 10.6904 7.55964 11.25 8.25 11.25H10.25C10.9404 11.25 11.5 10.6904 11.5 10V8C11.5 7.30964 10.9404 6.75 10.25 6.75H8.25ZM8.5 9.75V8.25H10V9.75H8.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M7 13.5C7 13.0858 7.33579 12.75 7.75 12.75H16.75C17.1642 12.75 17.5 13.0858 17.5 13.5C17.5 13.9142 17.1642 14.25 16.75 14.25H7.75C7.33579 14.25 7 13.9142 7 13.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M7.75 15.75C7.33579 15.75 7 16.0858 7 16.5C7 16.9142 7.33579 17.25 7.75 17.25H11.75C12.1642 17.25 12.5 16.9142 12.5 16.5C12.5 16.0858 12.1642 15.75 11.75 15.75H7.75Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M21.5 21V7.00584V7C21.5 6.98723 21.4997 6.97449 21.499 6.9618L21.5 7.00584L15.9367 1.27361C16.0656 1.30675 16.1846 1.37393 16.2803 1.46967C16.1833 1.37261 16.0641 1.30634 15.9367 1.27361C15.8762 1.25806 15.8135 1.25 15.75 1.25H4.75C3.7835 1.25 3 2.0335 3 3V21C3 21.9665 3.7835 22.75 4.75 22.75H19.75C20.7165 22.75 21.5 21.9665 21.5 21ZM4.75 2.75H15V7C15 7.41421 15.3358 7.75 15.75 7.75H20V21C20 21.1381 19.8881 21.25 19.75 21.25H4.75C4.61193 21.25 4.5 21.1381 4.5 21V3C4.5 2.86193 4.61193 2.75 4.75 2.75ZM16.5 3.81066L18.9393 6.25H16.5V3.81066Z", fill: "currentColor" })));
});

/**
 * @alias DocumentIcon
 */
const DocIcon = DocumentIcon;

const DotHorizontalIcon = React.forwardRef(function DotHorizontalIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.5 13.5C16.6716 13.5 16 12.8284 16 12C16 11.1716 16.6716 10.5 17.5 10.5C18.3284 10.5 19 11.1716 19 12C19 12.8284 18.3284 13.5 17.5 13.5ZM12 13.5C11.1716 13.5 10.5 12.8284 10.5 12C10.5 11.1716 11.1716 10.5 12 10.5C12.8284 10.5 13.5 11.1716 13.5 12C13.5 12.8284 12.8284 13.5 12 13.5ZM5 12C5 12.8284 5.67157 13.5 6.5 13.5C7.32843 13.5 8 12.8284 8 12C8 11.1716 7.32843 10.5 6.5 10.5C5.67157 10.5 5 11.1716 5 12Z", fill: "currentColor" })));
});

const DotVerticalIcon = React.forwardRef(function DotVerticalIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M13.5 6.5C13.5 7.32843 12.8284 8 12 8C11.1716 8 10.5 7.32843 10.5 6.5C10.5 5.67157 11.1716 5 12 5C12.8284 5 13.5 5.67157 13.5 6.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M13.5 12C13.5 12.8284 12.8284 13.5 12 13.5C11.1716 13.5 10.5 12.8284 10.5 12C10.5 11.1716 11.1716 10.5 12 10.5C12.8284 10.5 13.5 11.1716 13.5 12Z", fill: "currentColor" }),
        React.createElement("path", { d: "M12 19C12.8284 19 13.5 18.3284 13.5 17.5C13.5 16.6716 12.8284 16 12 16C11.1716 16 10.5 16.6716 10.5 17.5C10.5 18.3284 11.1716 19 12 19Z", fill: "currentColor" })));
});

const DownloadIcon = React.forwardRef(function DownloadIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.5303 16.0303L12.4462 16.1029C12.1526 16.3208 11.7359 16.2966 11.4697 16.0303L6.46967 11.0303L6.39705 10.9462C6.1792 10.6526 6.2034 10.2359 6.46967 9.96967L6.55379 9.89705C6.8474 9.6792 7.26406 9.7034 7.53033 9.96967L11.25 13.689V4L11.2568 3.89823C11.3065 3.53215 11.6203 3.25 12 3.25C12.4142 3.25 12.75 3.58579 12.75 4V13.689L16.4697 9.96967L16.5538 9.89705C16.8474 9.6792 17.2641 9.7034 17.5303 9.96967C17.8232 10.2626 17.8232 10.7374 17.5303 11.0303L12.5303 16.0303ZM21.7432 13.8982C21.6935 13.5322 21.3797 13.25 21 13.25C20.5858 13.25 20.25 13.5858 20.25 14V18L20.2435 18.1278C20.1795 18.7581 19.6472 19.25 19 19.25H5L4.87295 19.2435C4.24589 19.179 3.75 18.6429 3.75 18V14L3.74315 13.8982C3.69349 13.5322 3.3797 13.25 3 13.25C2.58579 13.25 2.25 13.5858 2.25 14V18L2.25504 18.1671C2.34192 19.6041 3.54187 20.75 5 20.75H19L19.1675 20.745C20.6083 20.6584 21.75 19.4625 21.75 18V14L21.7432 13.8982Z", fill: "currentColor" })));
});

const EditIcon = React.forwardRef(function EditIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M15.5653 4.16734L4.6719 15.4012C4.5824 15.4935 4.51813 15.6072 4.48525 15.7315L3.27487 20.3083C3.12648 20.8694 3.64429 21.3792 4.20301 21.222L8.64106 19.9738C8.76854 19.938 8.88425 19.869 8.97643 19.7739L19.8698 8.5401C20.6294 7.75681 20.9246 6.62055 20.648 5.55595C20.3702 4.48654 19.556 3.64696 18.508 3.35738C17.4551 3.06641 16.332 3.37675 15.5653 4.16734ZM18.1085 4.80319C18.6358 4.94888 19.052 5.37813 19.1962 5.93317L19.2285 6.0866C19.3115 6.59997 19.1508 7.1269 18.793 7.49589L8.03995 18.584L5.05995 19.422L5.803 16.6137L6.96962 17.7804L7.05374 17.853C7.34735 18.0708 7.76401 18.0466 8.03028 17.7804C8.32317 17.4875 8.32317 17.0126 8.03028 16.7197L6.73651 15.4259L16.6422 5.21156C16.9935 4.84921 17.4863 4.68893 17.9654 4.7712L18.1085 4.80319Z", fill: "currentColor" })));
});

const EngineIcon = React.forwardRef(function EngineIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17 1.75C17.4142 1.75 17.75 2.08579 17.75 2.5C17.75 2.8797 17.4678 3.19349 17.1018 3.24315L17 3.25H12.75V4.75H15C16.5188 4.75 17.75 5.98122 17.75 7.5V9.75H18.25V9C18.25 7.80914 19.1752 6.83436 20.346 6.75519L20.5 6.75C21.7426 6.75 22.75 7.75736 22.75 9V19C22.75 20.2426 21.7426 21.25 20.5 21.25C19.2574 21.25 18.25 20.2426 18.25 19V18.25H17.75V19.5C17.75 20.9625 16.6083 22.1584 15.1675 22.245L15 22.25H12.5833C12.2926 22.25 12.028 22.0819 11.9044 21.8187L11.023 19.942L7 19.9423C5.53747 19.9423 4.3416 18.8006 4.25502 17.3598L4.25 17.1923V14.75H2.75V18C2.75 18.4142 2.41421 18.75 2 18.75C1.6203 18.75 1.30651 18.4678 1.25685 18.1018L1.25 18V14V10C1.25 9.58579 1.58579 9.25 2 9.25C2.3797 9.25 2.69349 9.53215 2.74315 9.89823L2.75 10V13.25H4.25V9.32843C4.25 8.59908 4.53973 7.89961 5.05546 7.38388L7.46967 4.96967C7.61032 4.82902 7.80109 4.75 8 4.75H11.25V3.25H7C6.58579 3.25 6.25 2.91421 6.25 2.5C6.25 2.1203 6.53215 1.80651 6.89823 1.75685L7 1.75H17ZM5.75 17.1923V14V9.32843C5.75 8.99691 5.8817 8.67896 6.11612 8.44454L8.31 6.25H15C15.6904 6.25 16.25 6.80964 16.25 7.5V10.5C16.25 10.9142 16.5858 11.25 17 11.25H19C19.4142 11.25 19.75 10.9142 19.75 10.5V9C19.75 8.58579 20.0858 8.25 20.5 8.25C20.9142 8.25 21.25 8.58579 21.25 9V19C21.25 19.4142 20.9142 19.75 20.5 19.75C20.0858 19.75 19.75 19.4142 19.75 19V17.5C19.75 17.0858 19.4142 16.75 19 16.75H17C16.5858 16.75 16.25 17.0858 16.25 17.5V19.5L16.2435 19.6278C16.1795 20.2581 15.6472 20.75 15 20.75H13.06L12.1789 18.8736C12.0553 18.6104 11.7908 18.4423 11.5 18.4423H7C6.30964 18.4423 5.75 17.8827 5.75 17.1923Z", fill: "currentColor" })));
});

const ErrorIcon = React.forwardRef(function ErrorIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5C16.6944 20.5 20.5 16.6944 20.5 12ZM14.4697 8.46967C14.7626 8.17678 15.2374 8.17678 15.5303 8.46967C15.7966 8.73594 15.8208 9.1526 15.6029 9.44621L15.5303 9.53033L13.0607 12L15.5303 14.4697C15.8232 14.7626 15.8232 15.2374 15.5303 15.5303C15.2641 15.7966 14.8474 15.8208 14.5538 15.6029L14.4697 15.5303L12 13.0607L9.53033 15.5303C9.23744 15.8232 8.76256 15.8232 8.46967 15.5303C8.2034 15.2641 8.1792 14.8474 8.39705 14.5538L8.46967 14.4697L10.9393 12L8.46967 9.53033C8.17678 9.23744 8.17678 8.76256 8.46967 8.46967C8.73594 8.2034 9.1526 8.1792 9.44621 8.39705L9.53033 8.46967L12 10.9393L14.4697 8.46967Z", fill: "currentColor" })));
});

const ExclamationIcon = React.forwardRef(function ExclamationIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", fill: "none", width: 16, height: 16 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM8 3.5C8.51284 3.5 8.93551 3.88604 8.99327 4.38338L9 4.5V8C9 8.55228 8.55228 9 8 9C7.48716 9 7.06449 8.61396 7.00673 8.11662L7 8V4.5C7 3.94772 7.44772 3.5 8 3.5ZM8.01 10.25C8.56228 10.25 9.01 10.6977 9.01 11.25C9.01 11.7628 8.62396 12.1855 8.12662 12.2433L8 12.25C7.44772 12.25 7 11.8023 7 11.25C7 10.7372 7.38604 10.3145 7.88338 10.2567L8.01 10.25Z", fill: "currentColor" })));
});

const ExpandLessIcon = React.forwardRef(function ExpandLessIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ stroke: "currentColor", fill: "currentColor", strokeWidth: "0", viewBox: "0 0 24 24", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M12 8l-6 6 1.41 1.41L12 10.83l4.59 4.58L18 14z" })));
});

/**
 * @alias ChevronDownIcon
 */
const ExpandMoreIcon = ChevronDownIcon;

const ExternalIcon = React.forwardRef(function ExternalIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14.2432 2.39823C14.1935 2.03215 13.8797 1.75 13.5 1.75H8.5L8.39823 1.75685C8.03215 1.80651 7.75 2.1203 7.75 2.5L7.75685 2.60177C7.80651 2.96785 8.1203 3.25 8.5 3.25H11.689L5.63634 9.303L5.56372 9.38712C5.34586 9.68073 5.37007 10.0974 5.63634 10.3637C5.92923 10.6566 6.4041 10.6566 6.697 10.3637L12.75 4.309V7.5L12.7568 7.60177C12.8065 7.96785 13.1203 8.25 13.5 8.25C13.9142 8.25 14.25 7.91421 14.25 7.5V2.5L14.2432 2.39823ZM5.91667 3.5C5.91667 3.08579 5.58088 2.75 5.16667 2.75H3.83333L3.68491 2.75525C2.60618 2.83177 1.75 3.73568 1.75 4.83333V12.1667L1.75523 12.3154C1.83152 13.3966 2.73277 14.25 3.83333 14.25H11.1667L11.3154 14.2448C12.3966 14.1685 13.25 13.2672 13.25 12.1667V10.8333L13.2432 10.7316C13.1935 10.3655 12.8797 10.0833 12.5 10.0833C12.0858 10.0833 11.75 10.4191 11.75 10.8333V12.1667L11.7424 12.2613C11.6972 12.5385 11.4566 12.75 11.1667 12.75H3.83333L3.73871 12.7424C3.46155 12.6972 3.25 12.4566 3.25 12.1667V4.83333L3.25772 4.73945C3.30339 4.46401 3.54613 4.25 3.83333 4.25H5.16667L5.26844 4.24315C5.63451 4.19349 5.91667 3.8797 5.91667 3.5Z", fill: "currentColor" })));
});

const EyeHideIcon = React.forwardRef(function EyeHideIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.23464 6.1953C7.1379 6.25695 7.04176 6.32039 6.94625 6.38559L5.53033 4.96967L5.44621 4.89705C5.1526 4.6792 4.73594 4.7034 4.46967 4.96967C4.17678 5.26256 4.17678 5.73744 4.46967 6.03033L5.75512 7.31578C5.431 7.60191 5.11491 7.90889 4.80691 8.23514C4.10256 8.98123 3.49068 9.77812 2.97212 10.5755L2.75573 10.9167C2.55785 11.2371 2.41566 11.4928 2.33012 11.6627C2.22329 11.8749 2.22329 12.1251 2.33012 12.3373L2.43451 12.535C2.55771 12.7598 2.73724 13.0634 2.97212 13.4245C3.49068 14.2219 4.10256 15.0188 4.80691 15.7649C6.85357 17.9328 9.25769 19.25 12 19.25C13.5966 19.25 15.0785 18.8035 16.442 18.0026L18.4697 20.0303L18.5538 20.1029C18.8474 20.3208 19.2641 20.2966 19.5303 20.0303C19.8232 19.7374 19.8232 19.2626 19.5303 18.9697L17.7 17.1393C17.7887 17.0694 17.8768 16.998 17.9643 16.925L16.8982 15.8588C16.8094 15.9309 16.72 16.0011 16.6301 16.0695L14.9172 14.3566C14.9889 14.2679 15.0567 14.1759 15.1202 14.0808L14.0238 12.9844C13.9724 13.0898 13.9131 13.1906 13.8466 13.2859L10.7141 10.1534C10.8094 10.0869 10.9102 10.0276 11.0156 9.97622L9.91915 8.87982C9.8241 8.94333 9.73211 9.01108 9.64345 9.08279L8.03012 7.46946C8.12856 7.40668 8.2276 7.34615 8.32725 7.28791L7.23464 6.1953ZM6.81843 8.37909C6.5044 8.65151 6.19742 8.94731 5.89764 9.26486C5.25966 9.94064 4.70179 10.6672 4.22958 11.3933C4.08894 11.6095 3.97164 11.8006 3.87818 11.9601L3.855 12L3.95274 12.1654C4.00541 12.2528 4.06402 12.3477 4.12849 12.4494L4.22958 12.6067C4.70179 13.3328 5.25966 14.0594 5.89764 14.7351C7.68621 16.6297 9.73095 17.75 12 17.75C13.174 17.75 14.288 17.4501 15.3375 16.8982L13.7545 15.3151C13.231 15.5927 12.6338 15.75 12 15.75C9.92893 15.75 8.25 14.0711 8.25 12C8.25 11.3662 8.40726 10.769 8.68488 10.2455L6.81843 8.37909ZM12.6066 14.1673L9.83272 11.3934C9.77882 11.5864 9.75 11.7898 9.75 12C9.75 13.2426 10.7574 14.25 12 14.25C12.2102 14.25 12.4136 14.2212 12.6066 14.1673Z", fill: "currentColor" }),
        React.createElement("path", { d: "M11.4506 8.28996L13.3959 10.2353C13.5326 10.3435 13.6565 10.4674 13.7647 10.6041L15.71 12.5494C15.7364 12.3701 15.75 12.1866 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C11.8134 8.25 11.6299 8.26363 11.4506 8.28996Z", fill: "currentColor" }),
        React.createElement("path", { d: "M18.0015 14.8409C18.0352 14.8059 18.0688 14.7707 18.1024 14.7351C18.7403 14.0594 19.2982 13.3328 19.7704 12.6067C19.9111 12.3905 20.0284 12.1994 20.1218 12.0399L20.144 12L20.0473 11.8346C19.9946 11.7472 19.936 11.6523 19.8715 11.5506L19.7704 11.3933C19.2982 10.6672 18.7403 9.94064 18.1024 9.26486C16.3138 7.3703 14.2691 6.25 12 6.25C11.2341 6.25 10.4938 6.37763 9.78025 6.61959L8.61845 5.45779C9.68073 4.99869 10.8084 4.75 12 4.75C14.7423 4.75 17.1464 6.0672 19.1931 8.23514C19.8974 8.98123 20.5093 9.77812 21.0279 10.5755C21.2628 10.9366 21.4423 11.2402 21.5655 11.465L21.6699 11.6627C21.7767 11.8749 21.7767 12.1251 21.6699 12.3373C21.5843 12.5072 21.4421 12.7629 21.2443 13.0833L21.0279 13.4245C20.5093 14.2219 19.8974 15.0188 19.1931 15.7649C19.1497 15.8108 19.1061 15.8565 19.0623 15.9017L18.0015 14.8409Z", fill: "currentColor" })));
});

const EyeShowIcon = React.forwardRef(function EyeShowIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75C14.0711 15.75 15.75 14.0711 15.75 12C15.75 9.92893 14.0711 8.25 12 8.25ZM12 9.75C13.2426 9.75 14.25 10.7574 14.25 12C14.25 13.2426 13.2426 14.25 12 14.25C10.7574 14.25 9.75 13.2426 9.75 12C9.75 10.7574 10.7574 9.75 12 9.75Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 4.75C9.25769 4.75 6.85357 6.0672 4.80691 8.23514C4.10256 8.98123 3.49068 9.77812 2.97212 10.5755L2.75573 10.9167C2.55785 11.2371 2.41566 11.4928 2.33012 11.6627C2.22329 11.8749 2.22329 12.1251 2.33012 12.3373L2.43451 12.535C2.55771 12.7598 2.73724 13.0634 2.97212 13.4245C3.49068 14.2219 4.10256 15.0188 4.80691 15.7649C6.85357 17.9328 9.25769 19.25 12 19.25C14.7423 19.25 17.1464 17.9328 19.1931 15.7649C19.8974 15.0188 20.5093 14.2219 21.0279 13.4245L21.2443 13.0833C21.4421 12.7629 21.5843 12.5072 21.6699 12.3373C21.7767 12.1251 21.7767 11.8749 21.6699 11.6627L21.5655 11.465C21.4423 11.2402 21.2628 10.9366 21.0279 10.5755C20.5093 9.77812 19.8974 8.98123 19.1931 8.23514C17.1464 6.0672 14.7423 4.75 12 4.75ZM12 6.25C14.2691 6.25 16.3138 7.3703 18.1024 9.26486C18.7403 9.94064 19.2982 10.6672 19.7704 11.3933L19.8715 11.5506C19.936 11.6523 19.9946 11.7472 20.0473 11.8346L20.144 12L20.1218 12.0399C20.0284 12.1994 19.9111 12.3905 19.7704 12.6067C19.2982 13.3328 18.7403 14.0594 18.1024 14.7351C16.3138 16.6297 14.2691 17.75 12 17.75C9.73095 17.75 7.68621 16.6297 5.89764 14.7351C5.25966 14.0594 4.70179 13.3328 4.22958 12.6067L4.12849 12.4494C4.06402 12.3477 4.00541 12.2528 3.95274 12.1654L3.855 12L3.87818 11.9601C3.97164 11.8006 4.08894 11.6095 4.22958 11.3933C4.70179 10.6672 5.25966 9.94064 5.89764 9.26486C7.68621 7.3703 9.73095 6.25 12 6.25Z", fill: "currentColor" })));
});

const FamilyIcon = React.forwardRef(function FamilyIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.6874 9.25768C13.0695 9.30361 12.4835 9.55404 12.0176 9.96999L12 9.986L11.8519 9.86011C10.729 8.96946 9.10563 9.05429 8.07808 10.1146C7.54535 10.6643 7.25538 11.3943 7.25003 12.1516C7.24596 14.396 10.4765 17.95 11.9999 17.95C13.5272 17.95 16.75 14.4237 16.75 12.1728C16.75 11.4025 16.4528 10.6624 15.9216 10.1145C15.3864 9.56196 14.6568 9.25 13.8944 9.25L13.6874 9.25768ZM13.8944 10.75C14.2488 10.75 14.5903 10.896 14.8444 11.1584C15.1034 11.4254 15.25 11.7906 15.25 12.1728C15.25 13.6239 12.6672 16.45 11.9999 16.45C11.3389 16.45 8.7474 13.599 8.75001 12.1583C8.75266 11.7855 8.8951 11.4269 9.15524 11.1585C9.68279 10.6141 10.5281 10.6141 11.0556 11.1585L11.4613 11.5771C11.756 11.8812 12.2438 11.8812 12.5385 11.5771L12.9442 11.1585C13.1984 10.896 13.5399 10.75 13.8944 10.75Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11.5128 1.17983C11.7622 0.966699 12.1165 0.943018 12.3892 1.10878L12.4873 1.17983L20.8855 8.35653C20.9574 8.3997 21.0215 8.45457 21.0751 8.51851L23.4873 10.5798C23.8022 10.8489 23.8393 11.3223 23.5702 11.6372C23.3256 11.9235 22.9121 11.9802 22.6023 11.786L22.5128 11.7202L21.25 10.6411V19.9038C21.25 21.3089 20.0364 22.4153 18.5479 22.4954L18.375 22.5H5.62501C4.11682 22.5 2.84731 21.4456 2.75535 20.0646L2.75001 19.9038V10.6411L1.48725 11.7202C1.20098 11.9648 0.783702 11.9564 0.508023 11.7162L0.429841 11.6372C0.185209 11.351 0.193656 10.9337 0.433802 10.658L0.512775 10.5798L2.92496 8.51851C2.97855 8.45457 3.04261 8.3997 3.1145 8.35653L11.5128 1.17983ZM12 2.737L19.75 9.35939V19.9038L19.7432 20.0118C19.675 20.5464 19.1072 21 18.375 21H5.62501L5.48087 20.9941C4.77208 20.9352 4.25001 20.4477 4.25001 19.9038V9.35939L12 2.737Z", fill: "currentColor" })));
});

const FilledStarIcon = React.forwardRef(function FilledStarIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 20 20", fill: "currentColor", width: 20, height: 20 }, props, { ref: ref }),
        React.createElement("path", { d: "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" })));
});

const FilterIcon = React.forwardRef(function FilterIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 20 20", width: 20, height: 20 }, props, { ref: ref }),
        React.createElement("path", { fill: "currentColor", d: "M18.125 5.833c0-.345-.28-.625-.625-.625h-15l-.085.006c-.305.041-.54.303-.54.62 0 .345.28.624.625.624h15l.085-.005c.305-.042.54-.303.54-.62zM15 9.792c.345 0 .625.28.625.625 0 .316-.235.578-.54.619l-.085.006H5c-.345 0-.625-.28-.625-.625 0-.317.235-.578.54-.62L5 9.792h10zM12.5 14.375c.345 0 .625.28.625.625 0 .316-.235.578-.54.62l-.085.005h-5c-.345 0-.625-.28-.625-.625 0-.316.235-.578.54-.62l.085-.005h5z" })));
});

const FuelTankIcon = React.forwardRef(function FuelTankIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19 1.25H10C9.80109 1.25 9.61032 1.32902 9.46967 1.46967L8.26777 2.67157L7.44454 1.84835L7.33895 1.75096C6.65138 1.16664 5.61892 1.1991 4.96967 1.84835L2.84835 3.96967L2.75096 4.07526C2.16664 4.76283 2.1991 5.7953 2.84835 6.44454L3.67157 7.26777L2.46967 8.46967C2.32902 8.61032 2.25 8.80109 2.25 9V20C2.25 21.5188 3.48122 22.75 5 22.75H19C20.5188 22.75 21.75 21.5188 21.75 20V4C21.75 2.48122 20.5188 1.25 19 1.25ZM6.38388 2.90901L7.20711 3.73223L4.73223 6.20711L3.90901 5.38388L3.87315 5.33868C3.81337 5.24226 3.82533 5.11401 3.90901 5.03033L6.03033 2.90901L6.07553 2.87315C6.17195 2.81337 6.3002 2.82533 6.38388 2.90901ZM3.74967 9.31L10.3097 2.75H19C19.6904 2.75 20.25 3.30964 20.25 4V20C20.25 20.6904 19.6904 21.25 19 21.25H5C4.30964 21.25 3.75 20.6904 3.75 20L3.74967 9.31ZM16.0711 10.2197C16.3396 10.4882 16.3619 10.9096 16.1382 11.2035L16.0711 11.2803L14.4072 12.945C14.8714 13.7758 14.8712 14.7963 14.4067 15.627L16.0711 17.2907C16.364 17.5836 16.364 18.0585 16.0711 18.3514C15.8026 18.6199 15.3812 18.6423 15.0872 18.4185L15.0104 18.3514L13.3457 16.6875C12.5152 17.1515 11.4952 17.1515 10.6647 16.6875L9 18.3514C8.70711 18.6443 8.23223 18.6443 7.93934 18.3514C7.67085 18.0829 7.64848 17.6615 7.87222 17.3675L7.93934 17.2907L9.60375 15.627C9.13919 14.7963 9.13902 13.7758 9.60325 12.945L7.93934 11.2803C7.64645 10.9874 7.64645 10.5126 7.93934 10.2197C8.20783 9.95118 8.62922 9.92881 8.9232 10.1525L9 10.2197L10.6638 11.8841C11.4947 11.4194 12.5157 11.4194 13.3466 11.8841L15.0104 10.2197C15.3033 9.92678 15.7782 9.92678 16.0711 10.2197ZM12.8891 13.4017C12.4009 12.9135 11.6095 12.9135 11.1213 13.4017C10.6332 13.8898 10.6332 14.6813 11.1213 15.1694C11.6095 15.6576 12.4009 15.6576 12.8891 15.1694C13.3772 14.6813 13.3772 13.8898 12.8891 13.4017ZM18.75 5C18.75 4.58579 18.4142 4.25 18 4.25H11L10.8982 4.25685C10.5322 4.30651 10.25 4.6203 10.25 5C10.25 5.41421 10.5858 5.75 11 5.75H18L18.1018 5.74315C18.4678 5.69349 18.75 5.3797 18.75 5Z", fill: "currentColor" })));
});

const GasTankIcon = React.forwardRef(function GasTankIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M19.0731 11.8834C19.4136 11.6476 19.8809 11.7325 20.1166 12.0731C20.3524 12.4137 20.2675 12.8809 19.9269 13.1167L18.9454 13.7961C17.9722 14.4699 16.6771 14.4453 15.7301 13.7351L15.4167 13.5C14.9722 13.1667 14.3611 13.1667 13.9167 13.5L13.65 13.7C12.6722 14.4333 11.3278 14.4333 10.35 13.7L10.0833 13.5C9.63888 13.1667 9.02777 13.1667 8.58333 13.5L8.26987 13.7351C7.32293 14.4453 6.02776 14.4699 5.05455 13.7961L4.07309 13.1167C3.73252 12.8809 3.64757 12.4137 3.88335 12.0731C4.11912 11.7325 4.58634 11.6476 4.9269 11.8834L5.90836 12.5628C6.35073 12.8691 6.93944 12.8579 7.36987 12.5351L7.68333 12.3C8.6611 11.5667 10.0055 11.5667 10.9833 12.3L11.25 12.5C11.6944 12.8333 12.3055 12.8333 12.75 12.5L13.0167 12.3C13.9944 11.5667 15.3389 11.5667 16.3167 12.3L16.6301 12.5351C17.0605 12.8579 17.6493 12.8691 18.0916 12.5628L19.0731 11.8834Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.25 4.75V8.25H4C2.48122 8.25 1.25 9.48122 1.25 11V18C1.25 19.5188 2.48122 20.75 4 20.75H20C21.5188 20.75 22.75 19.5188 22.75 18V11C22.75 9.48122 21.5188 8.25 20 8.25H19.75V4.75H20C20.4142 4.75 20.75 4.41421 20.75 4C20.75 3.58579 20.4142 3.25 20 3.25H12C11.5858 3.25 11.25 3.58579 11.25 4C11.25 4.41421 11.5858 4.75 12 4.75H12.25ZM13.75 4.75H18.25V8.25H13.75V4.75ZM4 9.75C3.30964 9.75 2.75 10.3096 2.75 11V18C2.75 18.6904 3.30964 19.25 4 19.25H20C20.6904 19.25 21.25 18.6904 21.25 18V11C21.25 10.3096 20.6904 9.75 20 9.75H4Z", fill: "currentColor" })));
});

const HeartIcon = React.forwardRef(function HeartIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11.1711 6.1205C8.81415 3.17426 4.07693 4.46709 3.54334 8.2022C3.35682 9.50784 3.77874 10.827 4.68833 11.7821L12.4541 19.9361L20.2199 11.7821C21.1295 10.827 21.5514 9.50784 21.3649 8.20219C20.8313 4.46709 16.094 3.17426 13.7371 6.1205L13.0397 6.99214C12.8974 7.17005 12.6819 7.27361 12.4541 7.27361C12.2263 7.27361 12.0108 7.17005 11.8684 6.99214L11.1711 6.1205ZM2.05842 7.99006C2.77782 2.95428 9.16468 1.21126 12.3424 5.18346L12.4541 5.32303L12.5658 5.18346C15.7435 1.21126 22.1304 2.95428 22.8498 7.99006C23.1012 9.75038 22.5324 11.5289 21.3061 12.8165L12.9972 21.5409C12.8556 21.6895 12.6594 21.7736 12.4541 21.7736C12.2488 21.7736 12.0526 21.6895 11.911 21.5409L3.60212 12.8165C2.37578 11.5289 1.80695 9.75038 2.05842 7.99006Z", fill: "currentColor" })));
});

const HelpIcon = React.forwardRef(function HelpIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M8.3825 8.75112C8.9893 7.02617 10.7517 5.99038 12.554 6.29951C14.3562 6.60864 15.6727 8.17255 15.67 10C15.67 11.1785 14.9607 12.1242 13.836 12.874C13.5224 13.0831 13.2109 13.2564 12.9236 13.3955L12.669 13.512L12.67 14.2977C12.67 14.6774 12.3878 14.9912 12.0218 15.0409L11.92 15.0477C11.5403 15.0477 11.2265 14.7656 11.1768 14.3995L11.17 14.2977V13C11.17 12.6772 11.3766 12.3906 11.6828 12.2885L11.8013 12.2453C11.8757 12.2165 11.9717 12.1771 12.0842 12.1271C12.3994 11.987 12.7143 11.8191 13.004 11.626C13.7543 11.1258 14.17 10.5715 14.17 9.99889C14.1716 8.90175 13.3817 7.9634 12.3004 7.77792C11.219 7.59244 10.1616 8.21392 9.7975 9.24888C9.66005 9.63962 9.23186 9.84495 8.84112 9.7075C8.45038 9.57005 8.24505 9.14186 8.3825 8.75112Z", fill: "currentColor" }),
        React.createElement("path", { d: "M13 16.9934C13 16.4447 12.5553 16 12.0066 16L11.8586 16.0091C11.3737 16.0748 11 16.4905 11 16.9934C11 17.542 11.4447 17.9868 11.9934 17.9868L12.1414 17.9777C12.6263 17.9119 13 17.4963 13 16.9934Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5C16.6944 20.5 20.5 16.6944 20.5 12Z", fill: "currentColor" })));
});

const HistoryIcon = withDefaultSvgProps((props) => (React.createElement("svg", Object.assign({ stroke: "currentColor", fill: "currentColor", strokeWidth: "0", viewBox: "0 0 20 20", width: 20, height: 20 }, props),
    React.createElement("path", { fillRule: "evenodd", d: "M1.922 10.897c-.031-.296-.047-.596-.047-.897 0-.345.28-.625.625-.625s.625.28.625.625c0 .258.013.514.04.768.036.343-.213.65-.557.686-.343.036-.65-.213-.686-.557zm1.122 3.418c-.152-.259-.29-.525-.414-.798-.142-.315-.002-.685.312-.827.315-.143.685-.003.827.312.106.233.223.46.353.68.174.298.075.681-.223.856-.298.175-.68.075-.855-.223zm1.724 2.093c.225.198.46.384.705.558.28.2.671.134.871-.147.2-.281.134-.672-.147-.872-.209-.148-.41-.307-.602-.477-.259-.228-.654-.203-.882.056-.228.258-.203.653.055.882zm3.976 1.97c-.295-.059-.586-.133-.872-.222-.33-.103-.514-.453-.41-.783.102-.33.452-.513.782-.41.244.076.492.139.743.189.339.067.559.396.491.735-.067.338-.396.558-.734.49z", clipRule: "evenodd" }),
    React.createElement("path", { d: "M18.958 10c0 4.717-3.824 8.542-8.541 8.542-1.996 0-3.887-.688-5.4-1.924-.268-.218-.308-.612-.09-.88.219-.267.613-.306.88-.088 1.292 1.056 2.905 1.642 4.61 1.642 4.027 0 7.291-3.265 7.291-7.292 0-4.027-3.264-7.292-7.291-7.292-1.961 0-3.797.777-5.156 2.136-.48.48-.89 1.023-1.22 1.614h1.792c.317 0 .578.235.62.54l.005.085c0 .317-.235.578-.54.62l-.085.005H2.5c-.316 0-.578-.235-.62-.54l-.005-.085V3.75c0-.345.28-.625.625-.625.316 0 .578.235.62.54l.005.085v1.799c.353-.577.773-1.11 1.252-1.589 1.59-1.591 3.744-2.502 6.04-2.502 4.717 0 8.541 3.824 8.541 8.542z" }),
    React.createElement("path", { d: "M10 5c.316 0 .578.235.62.54l.005.085v4.614l2.988 1.494c.283.141.412.468.312.76l-.033.078c-.141.283-.468.412-.76.312l-.078-.032-3.334-1.667c-.185-.093-.31-.27-.339-.471l-.006-.088v-5c0-.345.28-.625.625-.625z" }))));

const ImageIcon = React.forwardRef(function ImageIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 32 32", fill: "none", stroke: "currentColor", width: 32, height: 32 }, props, { ref: ref }),
        React.createElement("path", { d: "M5.33325 21.3333L11.4479 15.2186C11.948 14.7187 12.6261 14.4379 13.3333 14.4379C14.0404 14.4379 14.7185 14.7187 15.2186 15.2186L21.3333 21.3333M18.6666 18.6666L20.7813 16.552C21.2813 16.0521 21.9595 15.7712 22.6666 15.7712C23.3737 15.7712 24.0518 16.0521 24.5519 16.552L26.6666 18.6666M18.6666 10.6666H18.6799M7.99992 26.6666H23.9999C24.7072 26.6666 25.3854 26.3857 25.8855 25.8856C26.3856 25.3855 26.6666 24.7072 26.6666 24V7.99998C26.6666 7.29274 26.3856 6.61446 25.8855 6.11436C25.3854 5.61426 24.7072 5.33331 23.9999 5.33331H7.99992C7.29267 5.33331 6.6144 5.61426 6.1143 6.11436C5.6142 6.61446 5.33325 7.29274 5.33325 7.99998V24C5.33325 24.7072 5.6142 25.3855 6.1143 25.8856C6.6144 26.3857 7.29267 26.6666 7.99992 26.6666Z", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })));
});

const InboxIcon = React.forwardRef(function InboxIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M8.74432 11.9079C8.69783 11.5322 8.37859 11.25 8 11.25H5L4.89823 11.2568C4.53215 11.3065 4.25 11.6203 4.25 12L4.25685 12.1018C4.30651 12.4678 4.6203 12.75 5 12.75H7.362L7.3896 12.889C7.93893 15.4281 9.51364 16.7904 12 16.7904L12.2456 16.7859C14.5902 16.6983 16.0794 15.3435 16.6104 12.889L16.637 12.75H19L19.1018 12.7432C19.4678 12.6935 19.75 12.3797 19.75 12C19.75 11.5858 19.4142 11.25 19 11.25H16L15.8984 11.2569C15.5656 11.3021 15.2979 11.5663 15.2557 11.9079C14.9664 14.2452 13.9317 15.2904 12 15.2904C10.0683 15.2904 9.03356 14.2452 8.74432 11.9079Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M16.7604 3.25H7.2396C6.19605 3.25055 5.24273 3.84172 4.77835 4.77625L1.32937 11.6642C1.27718 11.7684 1.25 11.8834 1.25 12V18C1.25 19.5188 2.48122 20.75 4 20.75H20C21.5188 20.75 22.75 19.5188 22.75 18V12C22.75 11.8834 22.7228 11.7684 22.6706 11.6642L19.2206 4.7742C18.7573 3.84172 17.8039 3.25055 16.7604 3.25ZM7.24 4.75H16.76L16.9008 4.75802C17.3184 4.80555 17.6884 5.06144 17.8784 5.44375L21.25 12.177V18C21.25 18.6904 20.6904 19.25 20 19.25H4L3.87219 19.2435C3.24187 19.1795 2.75 18.6472 2.75 18V12.175L6.12063 5.4458C6.31152 5.06165 6.6816 4.80558 7.09951 4.75802L7.24 4.75Z", fill: "currentColor" })));
});

const InfoIcon = React.forwardRef(function InfoIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 16 16", fill: "none", width: 16, height: 16 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 15C11.866 15 15 11.866 15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15ZM8.01 4C8.56228 4 9.01 4.44772 9.01 5C9.01 5.51284 8.62396 5.93551 8.12662 5.99327L8 6C7.44772 6 7 5.55228 7 5C7 4.48716 7.38604 4.06449 7.88338 4.00673L8.01 4ZM8 7C8.51284 7 8.93551 7.38604 8.99327 7.88338L9 8V11C9 11.5523 8.55228 12 8 12C7.48716 12 7.06449 11.614 7.00673 11.1166L7 11V8C7 7.44772 7.44772 7 8 7Z", fill: "currentColor" })));
});

const InfoOutlineIcon = React.forwardRef(function InfoOutlineIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM12.7432 10.6482C12.6935 10.2822 12.3797 10 12 10C11.5858 10 11.25 10.3358 11.25 10.75V16.75L11.2568 16.8518C11.3065 17.2178 11.6203 17.5 12 17.5C12.4142 17.5 12.75 17.1642 12.75 16.75V10.75L12.7432 10.6482ZM12.0066 6.5C12.5553 6.5 13 6.94772 13 7.5C13 8.00626 12.6263 8.42465 12.1414 8.49087L11.9934 8.5C11.4447 8.5 11 8.05228 11 7.5C11 6.99374 11.3737 6.57535 11.8586 6.50913L12.0066 6.5ZM20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5C16.6944 20.5 20.5 16.6944 20.5 12Z", fill: "currentColor" })));
});

const InviteIcon = React.forwardRef(function InviteIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M22.75 12C22.75 6.06294 17.9371 1.25 12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C12.7597 22.75 13.5096 22.6711 14.2411 22.5159C14.6463 22.4299 14.9051 22.0318 14.8192 21.6266C14.7332 21.2214 14.335 20.9626 13.9299 21.0485C13.3005 21.1821 12.6549 21.25 12 21.25C9.81914 21.25 7.81455 20.4953 6.23318 19.2328C6.25708 19.1595 6.27 19.0812 6.27 19C6.27 18.312 6.3749 17.8294 6.63597 17.4068C7.14903 16.5761 8.3575 15.8663 10.6201 15.2365C11.2474 15.0618 11.3694 14.2256 10.8182 13.879C9.28116 12.9126 8.75 12.0596 8.75 10C8.75 8.20507 10.2051 6.75 12 6.75C13.7949 6.75 15.25 8.20507 15.25 10C15.25 12.0238 14.7636 12.8208 13.2545 13.8323C12.7122 14.1957 12.8609 15.0304 13.4953 15.1842C14.0929 15.3291 14.6172 15.4848 15.0735 15.6518C15.4625 15.7942 15.8932 15.5942 16.0356 15.2053L16.0641 15.1073C16.1433 14.7465 15.9456 14.3737 15.589 14.2432L15.278 14.134L15.3446 14.0699C16.3333 13.0706 16.75 11.8962 16.75 10C16.75 7.37665 14.6234 5.25 12 5.25C9.37665 5.25 7.25 7.37665 7.25 10L7.25315 10.2745C7.29519 12.0721 7.76057 13.232 8.76379 14.1935L8.812 14.238L8.67703 14.2874C7.00343 14.9153 5.94725 15.6674 5.35978 16.6185C5.11466 17.0154 4.95675 17.4299 4.86662 17.8892C3.54443 16.2894 2.75 14.2375 2.75 12C2.75 6.89137 6.89137 2.75 12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 12.2926 21.2364 12.5833 21.2095 12.8718C21.171 13.2842 21.4741 13.6497 21.8865 13.6883C22.2989 13.7268 22.6645 13.4237 22.703 13.0112C22.7343 12.6765 22.75 12.3391 22.75 12Z", fill: "currentColor" }),
        React.createElement("path", { d: "M22.75 18C22.75 17.5858 22.4142 17.25 22 17.25H19.75V15L19.7432 14.8982C19.6935 14.5322 19.3797 14.25 19 14.25C18.5858 14.25 18.25 14.5858 18.25 15V17.25H16L15.8982 17.2568C15.5322 17.3065 15.25 17.6203 15.25 18C15.25 18.4142 15.5858 18.75 16 18.75H18.25V21L18.2568 21.1018C18.3065 21.4678 18.6203 21.75 19 21.75C19.4142 21.75 19.75 21.4142 19.75 21V18.75H22L22.1018 18.7432C22.4678 18.6935 22.75 18.3797 22.75 18Z", fill: "currentColor" })));
});

const LanguageIcon = React.forwardRef(function LanguageIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M10.1922 2.39823C10.1425 2.03215 9.8287 1.75 9.449 1.75C9.03479 1.75 8.699 2.08579 8.699 2.5V3.622H2.5L2.39823 3.62885C2.03215 3.67851 1.75 3.9923 1.75 4.372C1.75 4.78621 2.08579 5.122 2.5 5.122H12.1399C11.6122 7.41274 10.8254 9.33806 9.78369 10.9013C9.3148 10.277 8.7269 9.44717 8.02115 8.41322L7.12358 7.08329C6.89344 6.73889 6.42768 6.64628 6.08329 6.87642C5.73889 7.10656 5.64628 7.57232 5.87642 7.91671C7.15642 9.83213 8.14326 11.238 8.84153 12.1396C7.64401 13.5445 5.89357 15.1191 3.5924 16.8541C3.26166 17.1035 3.1957 17.5738 3.44507 17.9045C3.69444 18.2353 4.16471 18.3012 4.49545 18.0519C6.76469 16.3409 8.53372 14.766 9.80439 13.319C10.2132 13.7992 10.6775 14.3244 11.1976 14.8951L11.8072 15.5572C12.0892 15.8607 12.5637 15.8781 12.8672 15.5961C13.1706 15.3142 13.188 14.8396 12.906 14.5362C12.0576 13.6232 11.3421 12.8194 10.76 12.126C10.8114 12.0547 10.8614 11.9836 10.9101 11.9129C12.1667 10.0883 13.0845 7.83592 13.6686 5.15994C13.6713 5.14729 13.6738 5.13464 13.6759 5.122H16.398L16.4998 5.11515C16.8658 5.06549 17.148 4.7517 17.148 4.372C17.148 3.95779 16.8122 3.622 16.398 3.622H10.199V2.5L10.1922 2.39823Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.7742 9.35742C17.4793 8.82311 16.6642 8.84885 16.4243 9.44759L12.2396 19.8934L12.2081 19.9904C12.1181 20.3487 12.3045 20.7273 12.6569 20.8685L12.7539 20.9C13.1122 20.99 13.4908 20.8037 13.632 20.4512L14.9537 17.1509H19.0918L20.3181 20.4346L20.3601 20.5276C20.5347 20.8532 20.9273 21.0077 21.2831 20.8749C21.6711 20.73 21.8682 20.298 21.7233 19.91L17.8231 9.46416L17.7742 9.35742ZM18.5316 15.6509H15.5544L17.095 11.804L18.5316 15.6509Z", fill: "currentColor" })));
});

const LifebuoyIcon = React.forwardRef(function LifebuoyIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M0.75 12C0.75 5.7868 5.7868 0.75 12 0.75C18.2132 0.75 23.25 5.7868 23.25 12C23.25 18.2132 18.2132 23.25 12 23.25C5.7868 23.25 0.75 18.2132 0.75 12ZM21.75 12C21.75 9.6243 20.9003 7.44706 19.4883 5.75558L16.2821 8.96179C16.8916 9.81931 17.25 10.8678 17.25 12C17.25 13.2262 16.8296 14.3542 16.1252 15.2477L19.3191 18.4417C20.8322 16.7238 21.75 14.4691 21.75 12ZM12 17.25C10.8678 17.25 9.81931 16.8916 8.96179 16.2821L5.75558 19.4883C7.44706 20.9003 9.6243 21.75 12 21.75C14.3757 21.75 16.5529 20.9003 18.2444 19.4883L15.0382 16.2821C14.1807 16.8916 13.1322 17.25 12 17.25ZM4.68087 18.4417L7.87482 15.2477C7.17035 14.3542 6.75 13.2262 6.75 12C6.75 10.8678 7.1084 9.81931 7.71793 8.96178L4.51172 5.75558C3.09968 7.44706 2.25 9.6243 2.25 12C2.25 14.4691 3.16781 16.7238 4.68087 18.4417ZM18.4417 4.68087L15.2477 7.87482C14.3542 7.17035 13.2262 6.75 12 6.75C10.7738 6.75 9.64585 7.17035 8.75228 7.87482L5.55833 4.68087C7.27615 3.16781 9.53089 2.25 12 2.25C14.4691 2.25 16.7238 3.16781 18.4417 4.68087ZM15.75 12C15.75 9.92893 14.0711 8.25 12 8.25C9.92893 8.25 8.25 9.92893 8.25 12C8.25 14.0711 9.92893 15.75 12 15.75C14.0711 15.75 15.75 14.0711 15.75 12Z", fill: "currentColor" })));
});

const LinkIcon = React.forwardRef(function LinkIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M19.3137 4.69441C17.4814 2.86217 14.5188 2.83643 12.655 4.63654L11.2713 6.01214L11.1984 6.09604C10.9797 6.38902 11.0027 6.80575 11.2682 7.07279C11.5602 7.36654 12.0351 7.36792 12.3288 7.07588L13.7048 5.70788L13.8544 5.57216C15.1271 4.487 17.0476 4.54965 18.253 5.75507C19.5066 7.0087 19.5243 9.03574 18.2926 10.311L15.9017 12.7017L15.7434 12.8498C15.0928 13.4192 14.2392 13.708 13.371 13.6458C12.4304 13.5784 11.5655 13.1059 11.0006 12.3508C10.7525 12.0191 10.2825 11.9513 9.95082 12.1994C9.61913 12.4475 9.55138 12.9176 9.79948 13.2492C10.625 14.3529 11.8891 15.0435 13.2638 15.142C14.6385 15.2404 15.9881 14.7369 16.9625 13.7622L19.3624 11.3623L19.5218 11.1891C21.1684 9.31073 21.0904 6.47112 19.3137 4.69441Z", fill: "currentColor" }),
        React.createElement("path", { d: "M10.7363 8.85806L10.4789 8.84662C9.1944 8.82438 7.95108 9.32399 7.03763 10.2378L4.63773 12.6377L4.4783 12.8109C2.83169 14.6893 2.90974 17.5289 4.68645 19.3056C6.51869 21.1378 9.48129 21.1636 11.3451 19.3635L12.7224 17.9863L12.795 17.9022C13.0129 17.6086 12.9887 17.1919 12.7224 16.9257C12.4295 16.6328 11.9546 16.6328 11.6617 16.9257L10.2937 18.2937L10.1444 18.4292C8.87301 19.513 6.95253 19.4504 5.74711 18.245C4.49348 16.9913 4.47586 14.9643 5.70752 13.689L8.09839 11.2983L8.2567 11.1502C8.90731 10.5808 9.76095 10.292 10.6292 10.3542C11.5697 10.4216 12.4347 10.8941 12.9995 11.6492C13.2476 11.9809 13.7176 12.0487 14.0493 11.8006C14.381 11.5525 14.4487 11.0825 14.2006 10.7508C13.3751 9.64717 12.111 8.95651 10.7363 8.85806Z", fill: "currentColor" })));
});

const ListIcon = React.forwardRef(function ListIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M4.75 6.5C4.75 6.08579 4.41421 5.75 4 5.75H3.5L3.39823 5.75685C3.03215 5.80651 2.75 6.1203 2.75 6.5C2.75 6.91421 3.08579 7.25 3.5 7.25H4L4.10177 7.24315C4.46785 7.19349 4.75 6.8797 4.75 6.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M21.25 6.5C21.25 6.08579 20.9142 5.75 20.5 5.75H7L6.89823 5.75685C6.53215 5.80651 6.25 6.1203 6.25 6.5C6.25 6.91421 6.58579 7.25 7 7.25H20.5L20.6018 7.24315C20.9678 7.19349 21.25 6.8797 21.25 6.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M20.5 11.25C20.9142 11.25 21.25 11.5858 21.25 12C21.25 12.3797 20.9678 12.6935 20.6018 12.7432L20.5 12.75H7C6.58579 12.75 6.25 12.4142 6.25 12C6.25 11.6203 6.53215 11.3065 6.89823 11.2568L7 11.25H20.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M4.75 12C4.75 11.5858 4.41421 11.25 4 11.25H3.5L3.39823 11.2568C3.03215 11.3065 2.75 11.6203 2.75 12C2.75 12.4142 3.08579 12.75 3.5 12.75H4L4.10177 12.7432C4.46785 12.6935 4.75 12.3797 4.75 12Z", fill: "currentColor" }),
        React.createElement("path", { d: "M20.5 16.75C20.9142 16.75 21.25 17.0858 21.25 17.5C21.25 17.8797 20.9678 18.1935 20.6018 18.2432L20.5 18.25H7C6.58579 18.25 6.25 17.9142 6.25 17.5C6.25 17.1203 6.53215 16.8065 6.89823 16.7568L7 16.75H20.5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M4.75 17.5C4.75 17.0858 4.41421 16.75 4 16.75H3.5L3.39823 16.7568C3.03215 16.8065 2.75 17.1203 2.75 17.5C2.75 17.9142 3.08579 18.25 3.5 18.25H4L4.10177 18.2432C4.46785 18.1935 4.75 17.8797 4.75 17.5Z", fill: "currentColor" })));
});

const LocationIcon = React.forwardRef(function LocationIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.25 10C3.25 5.16751 7.16751 1.25 12 1.25C16.8325 1.25 20.75 5.16751 20.75 10C20.75 13.2957 17.9123 17.4594 12.4976 22.2611C12.2114 22.5149 11.7801 22.5127 11.4965 22.2559C6.01726 17.2935 3.25 13.259 3.25 10ZM19.25 10C19.25 5.99594 16.0041 2.75 12 2.75C7.99594 2.75 4.75 5.99594 4.75 10C4.75 12.5816 7.0887 16.1086 11.8124 20.5095L12.005 20.687L12.185 20.5241C16.744 16.3526 19.1347 12.7913 19.2459 10.1887L19.25 10ZM14.5 10C14.5 11.3807 13.3807 12.5 12 12.5C10.6193 12.5 9.5 11.3807 9.5 10C9.5 8.61929 10.6193 7.5 12 7.5C13.3807 7.5 14.5 8.61929 14.5 10ZM16 10C16 12.2091 14.2091 14 12 14C9.79086 14 8 12.2091 8 10C8 7.79086 9.79086 6 12 6C14.2091 6 16 7.79086 16 10Z", fill: "currentColor" })));
});

const LockIcon = React.forwardRef(function LockIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M12 13.25C12.3797 13.25 12.6935 13.5322 12.7432 13.8982L12.75 14V16.8467C12.75 17.2609 12.4142 17.5967 12 17.5967C11.6203 17.5967 11.3065 17.3145 11.2568 16.9485L11.25 16.8467V14C11.25 13.5858 11.5858 13.25 12 13.25Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17.7459 6.80324C17.6373 4.18399 15.3695 1.75 12 1.75C8.54629 1.75 6.25 4.30721 6.25 7V9.25H5C4.0335 9.25 3.25 10.0335 3.25 11V19C3.25 20.5188 4.48122 21.75 6 21.75H18C19.5188 21.75 20.75 20.5188 20.75 19V11C20.75 10.0335 19.9665 9.25 19 9.25H17.75V7L17.7459 6.80324ZM16.25 9.25V7C16.25 5.1018 14.5871 3.25 12 3.25C9.49126 3.25 7.85163 4.99127 7.75456 6.82761L7.75 7V9.25H16.25ZM19 10.75C19.1381 10.75 19.25 10.8619 19.25 11V19C19.25 19.6904 18.6904 20.25 18 20.25H6C5.30964 20.25 4.75 19.6904 4.75 19V11C4.75 10.8619 4.86193 10.75 5 10.75H19Z", fill: "currentColor" })));
});

const LogoutIcon = React.forwardRef(function LogoutIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M10.75 4C10.75 3.58579 10.4142 3.25 10 3.25H6L5.83248 3.25502C4.3917 3.3416 3.25 4.53747 3.25 6V18L3.25504 18.1671C3.34192 19.6041 4.54187 20.75 6 20.75H10L10.1018 20.7432C10.4678 20.6935 10.75 20.3797 10.75 20C10.75 19.5858 10.4142 19.25 10 19.25H6L5.87295 19.2435C5.24589 19.179 4.75 18.6429 4.75 18V6L4.75645 5.87219C4.82047 5.24187 5.35279 4.75 6 4.75H10L10.1018 4.74315C10.4678 4.69349 10.75 4.3797 10.75 4ZM15.5303 6.46967L15.4462 6.39705L15.3442 6.3334C15.0619 6.18756 14.7064 6.23299 14.4697 6.46967L14.3971 6.55379C14.1792 6.8474 14.2034 7.26406 14.4697 7.53033L18.189 11.25H8L7.89823 11.2568C7.53215 11.3065 7.25 11.6203 7.25 12C7.25 12.4142 7.58579 12.75 8 12.75H18.189L14.4697 16.4697L14.3971 16.5538C14.1792 16.8474 14.2034 17.2641 14.4697 17.5303C14.7626 17.8232 15.2374 17.8232 15.5303 17.5303L20.5303 12.5303L20.6029 12.4462C20.8208 12.1526 20.7966 11.7359 20.5303 11.4697L15.5303 6.46967Z", fill: "currentColor" })));
});

const LorryIcon = React.forwardRef(function LorryIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: 24, height: 24, viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14.035 18.9984H9.96501C9.72376 20.6961 8.26434 22.0016 6.5 22.0016C4.73566 22.0016 3.27624 20.6961 3.03499 18.9984H3C1.89543 18.9984 1 18.103 1 16.9984V3.99841C1 2.89384 1.89543 1.99841 3 1.99841H15C16.1046 1.99841 17 2.89384 17 3.99841V5.99841H19.0055C19.7413 5.99841 20.4176 6.4024 20.7664 7.05023L22.761 10.7545C22.9179 11.0459 23 11.3717 23 11.7026V16.9984C23 18.103 22.1046 18.9984 21 18.9984H20.965C20.7238 20.6961 19.2643 22.0016 17.5 22.0016C15.7357 22.0016 14.2762 20.6961 14.035 18.9984ZM9 11.7115C8.58579 11.7115 8.25 12.0473 8.25 12.4615C8.25 12.8758 8.58579 13.2115 9 13.2115H15.5V15.6289C14.8591 16.076 14.3747 16.7321 14.1459 17.4984H9.85411C9.42283 16.0543 8.08435 15.0016 6.5 15.0016C4.91565 15.0016 3.57717 16.0543 3.14589 17.4984H3C2.72386 17.4984 2.5 17.2746 2.5 16.9984V13.2115H4C4.41421 13.2115 4.75 12.8758 4.75 12.4615C4.75 12.0473 4.41421 11.7115 4 11.7115H2.5V3.99841C2.5 3.72227 2.72386 3.49841 3 3.49841H15C15.2761 3.49841 15.5 3.72227 15.5 3.99841V11.7115H9ZM20.8541 17.4984H21C21.2762 17.4984 21.5 17.2746 21.5 16.9984V11.7026C21.5 11.6199 21.4795 11.5384 21.4403 11.4656L19.4457 7.76137C19.3585 7.59941 19.1894 7.49841 19.0055 7.49841H17V15.037C17.1633 15.0137 17.3302 15.0016 17.5 15.0016C19.0844 15.0016 20.4228 16.0543 20.8541 17.4984ZM6.5 20.5016C7.60457 20.5016 8.5 19.6062 8.5 18.5016C8.5 17.397 7.60457 16.5016 6.5 16.5016C5.39543 16.5016 4.5 17.397 4.5 18.5016C4.5 19.6062 5.39543 20.5016 6.5 20.5016ZM19.5 18.5016C19.5 19.6062 18.6046 20.5016 17.5 20.5016C16.3954 20.5016 15.5 19.6062 15.5 18.5016C15.5 17.397 16.3954 16.5016 17.5 16.5016C18.6046 16.5016 19.5 17.397 19.5 18.5016Z", fill: "currentColor" })));
});

const LoudspeakerIcon = React.forwardRef(function LoudspeakerIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.943 5.58299L17.667 4.28199L19.8159 2.87815C20.3937 2.50033 21.1684 2.66245 21.5462 3.24024C21.6792 3.44362 21.75 3.68134 21.75 3.92434V18.0756C21.75 18.766 21.1904 19.3256 20.5 19.3256C20.257 19.3256 20.0193 19.2548 19.8159 19.1218L17.661 17.713L8.948 16.25L7.90762 16.2496L9.44491 20.0928C9.47529 20.1688 9.54019 20.2238 9.61728 20.2428L9.67703 20.25H10.25V18C10.25 17.6203 10.5322 17.3065 10.8982 17.2568L11 17.25C11.3797 17.25 11.6935 17.5321 11.7432 17.8982L11.75 18V21C11.75 21.3797 11.4678 21.6935 11.1018 21.7431L11 21.75H9.67703C9.01256 21.75 8.41025 21.3743 8.11482 20.7887L8.0522 20.6499L6.30364 16.2785C6.29514 16.2573 6.28769 16.2359 6.28124 16.2144C3.31972 15.8479 1.75 14.0085 1.75 10.9125C1.75 7.74706 3.39127 5.89766 6.4827 5.5963C6.57138 5.58765 6.66043 5.58332 6.74954 5.58332L8.943 5.58299ZM20.25 17.614V4.38499L18.3518 5.6277C18.2613 5.68691 18.159 5.72583 18.052 5.74178L9.75 6.97977V13C9.75 13.4142 9.41421 13.75 9 13.75C8.6203 13.75 8.30651 13.4678 8.25685 13.1018L8.25 13V7.0838L6.68883 7.0848L6.62826 7.08922C4.3124 7.31497 3.25 8.5121 3.25 10.9125C3.25 13.3129 4.31264 14.513 6.62785 14.7438C6.66906 14.7479 6.71037 14.75 6.75188 14.75H9.00773L9.13192 14.7603L18.0656 16.2603C18.1677 16.2775 18.2652 16.3156 18.3518 16.3723L20.25 17.614Z", fill: "currentColor" })));
});

const MailIcon = React.forwardRef(function MailIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20 3.25H4C2.48122 3.25 1.25 4.48122 1.25 6V18C1.25 19.5188 2.48122 20.75 4 20.75H20C21.5188 20.75 22.75 19.5188 22.75 18V6C22.75 4.48122 21.5188 3.25 20 3.25ZM4 4.75H20C20.6904 4.75 21.25 5.30964 21.25 6V18C21.25 18.6904 20.6904 19.25 20 19.25H4C3.30964 19.25 2.75 18.6904 2.75 18V6C2.75 5.30964 3.30964 4.75 4 4.75ZM5.37441 7.3976C5.09885 7.17939 4.70741 7.20308 4.45697 7.46902C4.18149 7.76155 4.18094 8.23642 4.45574 8.52967L11.4838 15.0297L11.5628 15.1024C11.8383 15.3206 12.2298 15.2969 12.4802 15.031L19.543 8.53098L19.6113 8.44695C19.8163 8.15361 19.7941 7.73692 19.5443 7.47032L19.4653 7.3976C19.1898 7.17939 18.7983 7.20308 18.5479 7.46902L11.9831 13.439L5.45335 7.47032L5.37441 7.3976Z", fill: "currentColor" })));
});

const MapIcon = React.forwardRef(function MapIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.58397 1.37596C7.80792 1.22667 8.09468 1.20882 8.33541 1.32918L15.9461 5.13454L21.584 1.37596C21.8141 1.22254 22.11 1.20823 22.3539 1.33875C22.5978 1.46926 22.75 1.72341 22.75 2V18C22.75 18.2508 22.6247 18.4849 22.416 18.624L16.416 22.624C16.1921 22.7733 15.9053 22.7912 15.6646 22.6708L8.05388 18.8655L2.41603 22.624C2.18588 22.7775 1.88997 22.7918 1.64611 22.6613C1.40224 22.5307 1.25 22.2766 1.25 22V6C1.25 5.74924 1.37533 5.51506 1.58397 5.37596L7.58397 1.37596ZM2.75 6.40139L7.25 3.40139V17.5986L2.75 20.5986V6.40139ZM15.9461 21.1345L8.75 17.5365V3.21353L15.25 6.46353V19C15.25 19.4142 15.5858 19.75 16 19.75C16.4142 19.75 16.75 19.4142 16.75 19V6.40139L21.25 3.40139V17.5986L15.9461 21.1345Z", fill: "currentColor" })));
});

const MastercardIcon = React.forwardRef(function MastercardIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "42", height: "30", viewBox: "0 0 42 30", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M0 4C0 1.79086 1.79086 0 4 0H38C40.2091 0 42 1.79086 42 4V26C42 28.2091 40.2091 30 38 30H4C1.79086 30 0 28.2091 0 26V4Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.6 0H38.4C40.3882 0 42 1.87416 42 4.18605V25.814C42 28.1258 40.3882 30 38.4 30H3.6C1.61177 30 0 28.1258 0 25.814V4.18605C0 1.87416 1.61177 0 3.6 0Z", fill: "white" }),
        React.createElement("path", { d: "M3.6 0.5H38.4C40.044 0.5 41.5 2.07688 41.5 4.18605V25.814C41.5 27.9231 40.044 29.5 38.4 29.5H3.6C1.95602 29.5 0.5 27.9231 0.5 25.814V4.18605C0.5 2.07688 1.95602 0.5 3.6 0.5Z", stroke: "black", strokeOpacity: "0.08" }),
        React.createElement("ellipse", { cx: "16.2383", cy: "15", rx: "8", ry: "8", fill: "#EB001B" }),
        React.createElement("circle", { cx: "25.7188", cy: "15", r: "8", fill: "#F79E1B" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20.9792 21.4457C22.9566 19.9892 24.2396 17.6445 24.2396 15.0002C24.2396 12.3559 22.9566 10.0112 20.9792 8.55469C19.0017 10.0112 17.7188 12.3559 17.7188 15.0002C17.7188 17.6445 19.0017 19.9892 20.9792 21.4457Z", fill: "#FF5F00" }),
        React.createElement("path", { d: "M4 1H38V-1H4V1ZM41 4V26H43V4H41ZM38 29H4V31H38V29ZM1 26V4H-1V26H1ZM4 29C2.34315 29 1 27.6569 1 26H-1C-1 28.7614 1.23858 31 4 31V29ZM41 26C41 27.6569 39.6569 29 38 29V31C40.7614 31 43 28.7614 43 26H41ZM38 1C39.6569 1 41 2.34315 41 4H43C43 1.23858 40.7614 -1 38 -1V1ZM4 -1C1.23858 -1 -1 1.23858 -1 4H1C1 2.34315 2.34315 1 4 1V-1Z", fill: "black", fillOpacity: "0.08" })));
});

const MembershipIcon = React.forwardRef(function MembershipIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9.896 9.105L6.23373 9.3983L6.12662 9.41417C5.54117 9.54259 5.32724 10.3056 5.80574 10.7155L8.596 13.105L7.74376 16.6801L7.72632 16.7813C7.6629 17.3806 8.32487 17.8236 8.86423 17.4942L11.999 15.579L15.1358 17.4942L15.2267 17.542C15.777 17.7875 16.4029 17.2948 16.2562 16.6801L15.403 13.105L18.1943 10.7155L18.2716 10.6397C18.6697 10.1917 18.3943 9.44865 17.7663 9.3983L14.103 9.105L12.6925 5.71198C12.4363 5.09601 11.5637 5.09601 11.3075 5.71198L9.896 9.105ZM12 7.953L12.8945 10.1037L12.9412 10.1971C13.0642 10.4053 13.2818 10.5436 13.5271 10.5633L15.848 10.749L14.0799 12.2648L14.0056 12.338C13.8455 12.5193 13.7812 12.769 13.8383 13.0084L14.379 15.274L12.3909 14.0599L12.2847 14.0061C12.0659 13.9163 11.8148 13.9343 11.6091 14.0599L9.62 15.274L10.1617 13.0084L10.1788 12.9054C10.2017 12.6647 10.107 12.4248 9.92006 12.2648L8.151 10.749L10.4729 10.5633C10.7533 10.5408 10.9975 10.3634 11.1055 10.1037L12 7.953Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 1.25C8.88993 1.25 5.78709 2.08273 2.69944 3.74003C1.80691 4.2191 1.25001 5.15006 1.25 6.16304V11C1.25 11.1657 1.25599 11.3316 1.2678 11.4965L1.25632 11.6575C1.25348 11.7232 1.25162 11.799 1.25069 11.8786L1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 11.7938 22.7442 11.5931 22.7325 11.3987L22.7436 11.2781C22.7464 11.2261 22.7483 11.1643 22.7493 11.0993L22.75 6.16305C22.75 5.15007 22.1931 4.21911 21.3006 3.74003C18.2129 2.08273 15.1101 1.25 12 1.25ZM12 2.75C14.8538 2.75 17.7149 3.51784 20.5912 5.06168C20.9969 5.27944 21.25 5.70261 21.25 6.16305V11C21.25 11.1304 21.2453 11.2602 21.2358 11.3891L21.2494 11.8939C21.2498 11.9296 21.25 11.9647 21.25 12C21.25 17.1086 17.1086 21.25 12 21.25C6.89137 21.25 2.75 17.1086 2.75 12C2.75 11.8243 2.75489 11.6532 2.76461 11.4866L2.75057 11.0787L2.75 6.16304C2.75 5.7026 3.00314 5.27944 3.40884 5.06168C6.28509 3.51784 9.14616 2.75 12 2.75Z", fill: "currentColor" })));
});

const MenuIcon = React.forwardRef(function MenuIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20.25 5.75C20.6642 5.75 21 6.08579 21 6.5C21 6.91421 20.6642 7.25 20.25 7.25H3.75C3.33579 7.25 3 6.91421 3 6.5C3 6.08579 3.33579 5.75 3.75 5.75H20.25ZM20.25 11.25C20.6642 11.25 21 11.5858 21 12C21 12.4142 20.6642 12.75 20.25 12.75H3.75C3.33579 12.75 3 12.4142 3 12C3 11.5858 3.33579 11.25 3.75 11.25H20.25ZM21 17.5C21 17.0858 20.6642 16.75 20.25 16.75H3.75C3.33579 16.75 3 17.0858 3 17.5C3 17.9142 3.33579 18.25 3.75 18.25H20.25C20.6642 18.25 21 17.9142 21 17.5Z", fill: "currentColor" })));
});

const MesraCardIcon = React.forwardRef(function MesraCardIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20 3.25H4C2.48122 3.25 1.25 4.48122 1.25 6V18C1.25 19.5188 2.48122 20.75 4 20.75H20C21.5188 20.75 22.75 19.5188 22.75 18V6C22.75 4.48122 21.5188 3.25 20 3.25ZM6.64297 10.2717L8.88105 9.946L9.88249 7.91808C10.1576 7.36064 10.9525 7.36064 11.2276 7.91808L12.228 9.946L14.4671 10.2717C15.0461 10.3559 15.2977 11.0305 14.9542 11.4713L14.8828 11.551L13.263 13.129L13.6454 15.3593C13.7446 15.9379 13.1765 16.3868 12.6496 16.1912L12.5572 16.1499L10.555 15.097L8.55291 16.1499C8.03326 16.4231 7.43078 16.0215 7.45397 15.4599L7.4647 15.3593L7.84605 13.129L6.22731 11.551C5.80836 11.1426 6.0013 10.4489 6.53831 10.2942L6.64297 10.2717ZM11.0581 10.9639L10.555 9.943L10.052 10.9639C9.94278 11.1852 9.73161 11.3387 9.48732 11.3742L8.36305 11.537L9.17644 12.331C9.32795 12.4787 9.40886 12.6821 9.40261 12.8905L9.39214 12.9948L9.20005 14.114L10.206 13.5862C10.3933 13.4877 10.6118 13.4736 10.8081 13.544L10.9041 13.5862L11.909 14.114L11.718 12.9948C11.6822 12.7863 11.7363 12.5741 11.8639 12.4092L11.9337 12.331L12.746 11.537L11.6228 11.3742C11.4134 11.3437 11.2283 11.2267 11.1109 11.0545L11.0581 10.9639ZM20 4.75H4C3.30964 4.75 2.75 5.30964 2.75 6V18C2.75 18.6904 3.30964 19.25 4 19.25H20C20.6904 19.25 21.25 18.6904 21.25 18V6C21.25 5.30964 20.6904 4.75 20 4.75Z", fill: "currentColor" })));
});

const MessageIcon = React.forwardRef(function MessageIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M8.25 12C8.94036 12 9.5 11.4404 9.5 10.75C9.5 10.0596 8.94036 9.5 8.25 9.5C7.55964 9.5 7 10.0596 7 10.75C7 11.4404 7.55964 12 8.25 12Z", fill: "currentColor" }),
        React.createElement("path", { d: "M12.25 12C12.9404 12 13.5 11.4404 13.5 10.75C13.5 10.0596 12.9404 9.5 12.25 9.5C11.5596 9.5 11 10.0596 11 10.75C11 11.4404 11.5596 12 12.25 12Z", fill: "currentColor" }),
        React.createElement("path", { d: "M17.5 10.75C17.5 11.4404 16.9404 12 16.25 12C15.5596 12 15 11.4404 15 10.75C15 10.0596 15.5596 9.5 16.25 9.5C16.9404 9.5 17.5 10.0596 17.5 10.75Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19 3.25H5C3.48122 3.25 2.25 4.48122 2.25 6V15L2.25502 15.1675C2.3416 16.6083 3.53747 17.75 5 17.75H7.25V21L7.25657 21.1021C7.3301 21.6654 8.00473 21.9567 8.46852 21.5857L13.262 17.75H19C20.5188 17.75 21.75 16.5188 21.75 15V6C21.75 4.48122 20.5188 3.25 19 3.25ZM5 4.75H19C19.6904 4.75 20.25 5.30964 20.25 6V15C20.25 15.6904 19.6904 16.25 19 16.25H13L12.8986 16.2569C12.7649 16.2751 12.6379 16.3292 12.5315 16.4143L8.75 19.439V17C8.75 16.5858 8.41421 16.25 8 16.25H5C4.30964 16.25 3.75 15.6904 3.75 15V6C3.75 5.30964 4.30964 4.75 5 4.75Z", fill: "currentColor" })));
});

const MeterIcon = React.forwardRef(function MeterIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M22.75 12.75C22.75 6.81294 17.9371 2 12 2C6.06294 2 1.25 6.81294 1.25 12.75C1.25 15.6021 2.36665 18.2798 4.32374 20.2758C4.4648 20.4197 4.65779 20.5007 4.85927 20.5007H6L6.10177 20.4939C6.46785 20.4442 6.75 20.1304 6.75 19.7507L6.74315 19.6489C6.69349 19.2829 6.3797 19.0007 6 19.0007L5.181 19L4.97186 18.7644C3.55025 17.1058 2.75 14.9919 2.75 12.75C2.75 7.64137 6.89137 3.5 12 3.5C17.1086 3.5 21.25 7.64137 21.25 12.75L21.2446 13.0694C21.1688 15.2983 20.3012 17.3837 18.818 19H18C17.5858 19 17.25 19.3358 17.25 19.75C17.25 20.1642 17.5858 20.5 18 20.5L19.1407 20.5007L19.2603 20.4911C19.4173 20.4658 19.5634 20.3909 19.6763 20.2758C21.6333 18.2798 22.75 15.6021 22.75 12.75ZM7.44621 7.14705C7.1526 6.9292 6.73594 6.9534 6.46967 7.21967C6.17678 7.51256 6.17678 7.98744 6.46967 8.28033L7.76256 9.57322L7.84668 9.64584C8.14029 9.8637 8.55696 9.83949 8.82322 9.57322C9.11612 9.28033 9.11612 8.80546 8.82322 8.51256L7.53033 7.21967L7.44621 7.14705ZM7 12C7.41421 12 7.75 12.3358 7.75 12.75C7.75 13.1297 7.46785 13.4435 7.10177 13.4932L7 13.5H5C4.58579 13.5 4.25 13.1642 4.25 12.75C4.25 12.3703 4.53215 12.0565 4.89823 12.0068L5 12H7ZM19.75 12.75C19.75 12.3358 19.4142 12 19 12H17L16.8982 12.0068C16.5322 12.0565 16.25 12.3703 16.25 12.75C16.25 13.1642 16.5858 13.5 17 13.5H19L19.1018 13.4932C19.4678 13.4435 19.75 13.1297 19.75 12.75ZM16.7332 6.90832C16.8116 6.54732 16.6131 6.17491 16.2563 6.04516C15.867 5.9036 15.4367 6.10442 15.2952 6.49369L12.2002 15.0053C12.1339 15.0018 12.0671 15 12 15C9.92893 15 8.25 16.6789 8.25 18.75C8.25 20.8211 9.92893 22.5 12 22.5C14.0711 22.5 15.75 20.8211 15.75 18.75C15.75 17.274 14.8972 15.9971 13.6575 15.3852L16.7048 7.00631L16.7332 6.90832ZM14.25 18.75C14.25 17.5074 13.2426 16.5 12 16.5C10.7574 16.5 9.75 17.5074 9.75 18.75C9.75 19.9926 10.7574 21 12 21C13.2426 21 14.25 19.9926 14.25 18.75ZM12.7432 5.64823C12.6935 5.28215 12.3797 5 12 5C11.5858 5 11.25 5.33579 11.25 5.75V7.75L11.2568 7.85177C11.3065 8.21785 11.6203 8.5 12 8.5C12.4142 8.5 12.75 8.16421 12.75 7.75V5.75L12.7432 5.64823Z", fill: "currentColor" })));
});

const MiniAscendingIcon = React.forwardRef(function MiniAscendingIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M12.4947 9.0052C12.2284 8.73894 11.8118 8.71473 11.5181 8.93258L11.434 9.0052L8.74989 11.689L8.74989 2.5C8.74989 2.08579 8.4141 1.75 7.99989 1.75C7.62019 1.75 7.3064 2.03215 7.25673 2.39823L7.24989 2.5L7.24989 11.689L4.56575 9.0052C4.29948 8.73894 3.88282 8.71473 3.58921 8.93258L3.50509 9.0052C3.23882 9.27147 3.21462 9.68813 3.43247 9.98174L3.50509 10.0659L7.46956 14.0303C7.73582 14.2966 8.15249 14.3208 8.4461 14.1029L8.53022 14.0303L12.4947 10.0659C12.7876 9.77297 12.7876 9.2981 12.4947 9.0052Z", fill: "currentColor" })));
});

const MiniChevronDownIcon = React.forwardRef(function MiniChevronDownIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M13.5303 5.21967C13.7966 5.48594 13.8208 5.9026 13.6029 6.19621L13.5303 6.28033L8.53033 11.2803C8.26406 11.5466 7.8474 11.5708 7.55379 11.3529L7.46967 11.2803L2.46967 6.28033C2.17678 5.98744 2.17678 5.51256 2.46967 5.21967C2.73594 4.9534 3.1526 4.9292 3.44621 5.14705L3.53033 5.21967L8 9.689L12.4697 5.21967C12.7359 4.9534 13.1526 4.9292 13.4462 5.14705L13.5303 5.21967Z", fill: "currentcolor" })));
});

const MiniCloseIcon = React.forwardRef(function MiniCloseIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M13.5303 2.46967C13.2374 2.17678 12.7626 2.17678 12.4697 2.46967L8 6.93934L3.53033 2.46967L3.44621 2.39705C3.1526 2.1792 2.73594 2.2034 2.46967 2.46967C2.17678 2.76256 2.17678 3.23744 2.46967 3.53033L6.93934 8L2.46967 12.4697L2.39705 12.5538C2.1792 12.8474 2.2034 13.2641 2.46967 13.5303C2.76256 13.8232 3.23744 13.8232 3.53033 13.5303L8 9.06066L12.4697 13.5303L12.5538 13.6029C12.8474 13.8208 13.2641 13.7966 13.5303 13.5303C13.8232 13.2374 13.8232 12.7626 13.5303 12.4697L9.06066 8L13.5303 3.53033L13.6029 3.44621C13.8208 3.1526 13.7966 2.73594 13.5303 2.46967Z", fill: "currentColor" })));
});

const MiniDescendingIcon = React.forwardRef(function MiniDescendingIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M3.50531 6.9948C3.77158 7.26106 4.18824 7.28527 4.48186 7.06742L4.56597 6.9948L7.25011 4.311L7.25011 13.5C7.25011 13.9142 7.5859 14.25 8.00011 14.25C8.37981 14.25 8.6936 13.9678 8.74327 13.6018L8.75011 13.5L8.75011 4.311L11.4342 6.9948C11.7005 7.26106 12.1172 7.28527 12.4108 7.06741L12.4949 6.9948C12.7612 6.72853 12.7854 6.31187 12.5675 6.01825L12.4949 5.93414L8.53044 1.96967C8.26417 1.7034 7.84751 1.6792 7.5539 1.89705L7.46978 1.96967L3.50531 5.93414C3.21242 6.22703 3.21242 6.7019 3.50531 6.9948Z", fill: "currentColor" })));
});

const MiniUnsortedIcon = React.forwardRef(function MiniUnsortedIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "16", height: "16", viewBox: "0 0 16 16", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M8.75507 4.83058C9.02134 5.09685 9.438 5.12106 9.73161 4.9032L9.81573 4.83058L11.0857 3.561L11.0857 13.75C11.0857 14.1642 11.4214 14.5 11.8357 14.5C12.2153 14.5 12.5291 14.2178 12.5788 13.8518L12.5857 13.75L12.5857 3.561L13.8556 4.83058C14.1218 5.09685 14.5385 5.12106 14.8321 4.9032L14.9162 4.83058C15.1825 4.56432 15.2067 4.14765 14.9889 3.85404L14.9162 3.76992L12.366 1.21967C12.0997 0.953402 11.6831 0.929196 11.3894 1.14705L11.3053 1.21967L8.75507 3.76992C8.46218 4.06282 8.46218 4.53769 8.75507 4.83058Z", fill: "currentColor" }),
        React.createElement("path", { d: "M7.24517 11.1694C6.97891 10.9031 6.56224 10.8789 6.26863 11.0968L6.18451 11.1694L4.91459 12.439L4.91459 2.25C4.91459 1.83579 4.5788 1.5 4.16459 1.5C3.78489 1.5 3.4711 1.78215 3.42144 2.14823L3.41459 2.25L3.41459 12.439L2.14467 11.1694C1.8784 10.9031 1.46174 10.8789 1.16812 11.0968L1.084 11.1694C0.817739 11.4357 0.793532 11.8523 1.01139 12.146L1.084 12.2301L3.63426 14.7803C3.90053 15.0466 4.31719 15.0708 4.6108 14.8529L4.69492 14.7803L7.24517 12.2301C7.53807 11.9372 7.53807 11.4623 7.24517 11.1694Z", fill: "currentColor" })));
});

const MinusIcon = React.forwardRef(function MinusIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 24 24", stroke: "currentColor" }, props, { ref: ref }),
        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M18 12H6" })));
});

const MoreIcon = React.forwardRef(function MoreIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2.25 12C2.25 10.4812 3.48122 9.25 5 9.25C6.51878 9.25 7.75 10.4812 7.75 12C7.75 13.5188 6.51878 14.75 5 14.75C3.48122 14.75 2.25 13.5188 2.25 12ZM6.25 12C6.25 11.3096 5.69036 10.75 5 10.75C4.30964 10.75 3.75 11.3096 3.75 12C3.75 12.6904 4.30964 13.25 5 13.25C5.69036 13.25 6.25 12.6904 6.25 12Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M9.25 12C9.25 10.4812 10.4812 9.25 12 9.25C13.5188 9.25 14.75 10.4812 14.75 12C14.75 13.5188 13.5188 14.75 12 14.75C10.4812 14.75 9.25 13.5188 9.25 12ZM13.25 12C13.25 11.3096 12.6904 10.75 12 10.75C11.3096 10.75 10.75 11.3096 10.75 12C10.75 12.6904 11.3096 13.25 12 13.25C12.6904 13.25 13.25 12.6904 13.25 12Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19 9.25C17.4812 9.25 16.25 10.4812 16.25 12C16.25 13.5188 17.4812 14.75 19 14.75C20.5188 14.75 21.75 13.5188 21.75 12C21.75 10.4812 20.5188 9.25 19 9.25ZM19 10.75C19.6904 10.75 20.25 11.3096 20.25 12C20.25 12.6904 19.6904 13.25 19 13.25C18.3096 13.25 17.75 12.6904 17.75 12C17.75 11.3096 18.3096 10.75 19 10.75Z", fill: "currentColor" })));
});

const OrderIcon = React.forwardRef(function OrderIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.53033 8.53033C7.23744 8.82322 6.76256 8.82322 6.46967 8.53033C6.17678 8.23744 6.17678 7.76256 6.46967 7.46967L11.4697 2.46967C11.7626 2.17678 12.2374 2.17678 12.5303 2.46967L17.5303 7.46967C17.8232 7.76256 17.8232 8.23744 17.5303 8.53033C17.2374 8.82322 16.7626 8.82322 16.4697 8.53033L12 4.06066L7.53033 8.53033ZM7.53033 15.4697C7.23744 15.1768 6.76256 15.1768 6.46967 15.4697C6.17678 15.7626 6.17678 16.2374 6.46967 16.5303L11.4697 21.5303C11.7626 21.8232 12.2374 21.8232 12.5303 21.5303L17.5303 16.5303C17.8232 16.2374 17.8232 15.7626 17.5303 15.4697C17.2374 15.1768 16.7626 15.1768 16.4697 15.4697L12 19.9393L7.53033 15.4697Z", fill: "currentColor" })));
});

const PetronasGreenIcon = React.forwardRef(function PetronasGreenIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 26 26", width: 26, height: 26 }, props, { ref: ref }),
        React.createElement("path", { d: "M14.1827169,10.08866 L20,0 L11.0229979,0 C8.51990837,0.0673171014 7.8638816,0.560422416 6.91051029,2.21135169 C6.91051029,2.21135169 1.13919281,12.2249969 1.13617868,12.2311853 C0.418964946,13.4578357 0,14.8988141 0,16.422505 C0,19.4400016 1.60909666,22.0792546 4.01467884,23.5298929 C4.01467884,23.5303457 4.01452814,23.5307985 4.01452814,23.5312513 C5.25996926,24.2801918 6.71881122,24.7133758 8.27772252,24.7133758 C12.8494138,24.7133758 16.5551436,21.0014263 16.5551436,16.422505 C16.5551436,14.8544391 16.1194502,13.3888583 15.3633541,12.1377564 L14.1827169,10.08866 Z", fill: "#FFFFFF" }),
        React.createElement("path", { d: "M20,0 L14.1827169,10.08866 L15.3633541,12.1377564 C16.1194502,13.3888583 16.5551436,14.8544391 16.5551436,16.422505 C16.5551436,21.0014263 12.8494138,24.7133758 8.27772252,24.7133758 C6.71881122,24.7133758 5.25996926,24.2801918 4.01452814,23.5312513 L4.01452814,23.5312513 C1.60909666,22.0792546 0,19.4400016 0,16.422505 C0,14.8988141 0.418964946,13.4578357 1.13617868,12.2311853 C1.13919281,12.2249969 6.91051029,2.21135169 6.91051029,2.21135169 C7.8638816,0.560422416 8.51990837,0.0673171014 11.0229979,0 L11.0229979,0 L20,0 Z M8.2751605,4.06272291 C8.25541791,4.10090952 2.95641559,13.2922598 2.95641559,13.2922598 C2.41658378,14.209493 2.10853905,15.2814348 2.10853905,16.422354 C2.10853905,18.3190058 2.96184103,20.0152157 4.30418664,21.1482863 L4.30418664,21.1482863 L4.52297948,21.3247365 C5.56300851,22.125265 6.86534848,22.6014904 8.27757181,22.6014904 C11.6841487,22.6014904 14.4464539,19.8343953 14.4464539,16.422505 C14.4464539,15.2815858 14.1369021,14.2111533 13.5979745,13.2954294 C13.597221,13.2924107 13.5964674,13.2909013 13.5955632,13.289392 L13.5955632,13.289392 L10.3461736,7.64924526 L10.34542,7.64758497 Z M8.28040527,11.8471338 C10.8132776,11.8471338 12.866242,13.9007338 12.866242,16.4322933 C12.866242,18.9655082 10.8132776,21.0191083 8.28040527,21.0191083 C5.74738244,21.0191083 3.69426752,18.9653577 3.69426752,16.4322933 C3.69426752,13.9007338 5.74738244,11.8471338 8.28040527,11.8471338 Z M16.3327004,2.1209415 L11.0689634,2.1209415 C10.4013322,2.1209415 9.98266872,2.16229775 9.67522681,2.26795239 L9.67522681,2.26795239 L11.811948,5.97401542 L11.811948,5.97401542 L12.9609368,7.96786947 L16.3327004,2.1209415 Z", fill: "#00A19C" })));
});

const PetronasWhiteIcon = React.forwardRef(function PetronasWhiteIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 26 26", width: 26, height: 26 }, props, { ref: ref }),
        React.createElement("path", { d: "M14.1827169,10.08866 L20,0 L11.0229979,0 C8.51990837,0.0673171014 7.8638816,0.560422416 6.91051029,2.21135169 C6.91051029,2.21135169 1.13919281,12.2249969 1.13617868,12.2311853 C0.418964946,13.4578357 0,14.8988141 0,16.422505 C0,19.4400016 1.60909666,22.0792546 4.01467884,23.5298929 C4.01467884,23.5303457 4.01452814,23.5307985 4.01452814,23.5312513 C5.25996926,24.2801918 6.71881122,24.7133758 8.27772252,24.7133758 C12.8494138,24.7133758 16.5551436,21.0014263 16.5551436,16.422505 C16.5551436,14.8544391 16.1194502,13.3888583 15.3633541,12.1377564 L14.1827169,10.08866 Z", fill: "#00A19C" }),
        React.createElement("path", { d: "M20,0 L14.1827169,10.08866 L15.3633541,12.1377564 C16.1194502,13.3888583 16.5551436,14.8544391 16.5551436,16.422505 C16.5551436,21.0014263 12.8494138,24.7133758 8.27772252,24.7133758 C6.71881122,24.7133758 5.25996926,24.2801918 4.01452814,23.5312513 L4.01452814,23.5312513 C1.60909666,22.0792546 0,19.4400016 0,16.422505 C0,14.8988141 0.418964946,13.4578357 1.13617868,12.2311853 C1.13919281,12.2249969 6.91051029,2.21135169 6.91051029,2.21135169 C7.8638816,0.560422416 8.51990837,0.0673171014 11.0229979,0 L11.0229979,0 L20,0 Z M8.2751605,4.06272291 C8.25541791,4.10090952 2.95641559,13.2922598 2.95641559,13.2922598 C2.41658378,14.209493 2.10853905,15.2814348 2.10853905,16.422354 C2.10853905,18.3190058 2.96184103,20.0152157 4.30418664,21.1482863 L4.30418664,21.1482863 L4.52297948,21.3247365 C5.56300851,22.125265 6.86534848,22.6014904 8.27757181,22.6014904 C11.6841487,22.6014904 14.4464539,19.8343953 14.4464539,16.422505 C14.4464539,15.2815858 14.1369021,14.2111533 13.5979745,13.2954294 C13.597221,13.2924107 13.5964674,13.2909013 13.5955632,13.289392 L13.5955632,13.289392 L10.3461736,7.64924526 L10.34542,7.64758497 Z M8.28040527,11.8471338 C10.8132776,11.8471338 12.866242,13.9007338 12.866242,16.4322933 C12.866242,18.9655082 10.8132776,21.0191083 8.28040527,21.0191083 C5.74738244,21.0191083 3.69426752,18.9653577 3.69426752,16.4322933 C3.69426752,13.9007338 5.74738244,11.8471338 8.28040527,11.8471338 Z M16.3327004,2.1209415 L11.0689634,2.1209415 C10.4013322,2.1209415 9.98266872,2.16229775 9.67522681,2.26795239 L9.67522681,2.26795239 L11.811948,5.97401542 L11.811948,5.97401542 L12.9609368,7.96786947 L16.3327004,2.1209415 Z", fill: "#FFFFFF" })));
});

const Phone1Icon = React.forwardRef(function Phone1Icon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { d: "M14 18.5C14 17.9477 13.5523 17.5 13 17.5H11L10.8834 17.5067C10.386 17.5645 10 17.9872 10 18.5C10 19.0523 10.4477 19.5 11 19.5H13L13.1166 19.4933C13.614 19.4355 14 19.0128 14 18.5Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M17 1.25H7C5.48122 1.25 4.25 2.48122 4.25 4V20C4.25 21.5188 5.48122 22.75 7 22.75H17C18.5188 22.75 19.75 21.5188 19.75 20V4C19.75 2.48122 18.5188 1.25 17 1.25ZM7 2.75H17C17.6904 2.75 18.25 3.30964 18.25 4V20C18.25 20.6904 17.6904 21.25 17 21.25H7C6.30964 21.25 5.75 20.6904 5.75 20V4C5.75 3.30964 6.30964 2.75 7 2.75Z", fill: "currentColor" })));
});

const PlusIcon = React.forwardRef(function PlusIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M12 3.25C12.3797 3.25 12.6935 3.53215 12.7432 3.89823L12.75 4V11.25H20C20.4142 11.25 20.75 11.5858 20.75 12C20.75 12.3797 20.4678 12.6935 20.1018 12.7432L20 12.75H12.75V20C12.75 20.4142 12.4142 20.75 12 20.75C11.6203 20.75 11.3065 20.4678 11.2568 20.1018L11.25 20V12.75H4C3.58579 12.75 3.25 12.4142 3.25 12C3.25 11.6203 3.53215 11.3065 3.89823 11.2568L4 11.25H11.25V4C11.25 3.58579 11.5858 3.25 12 3.25Z", fill: "currentColor" })));
});

const PreviewIcon = React.forwardRef(function PreviewIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2.25 10.5C2.25 5.94365 5.94365 2.25 10.5 2.25C15.0563 2.25 18.75 5.94365 18.75 10.5C18.75 12.5081 18.0325 14.3487 16.84 15.7793L20.658 19.5973C20.9509 19.8902 20.9509 20.3651 20.658 20.658C20.3917 20.9243 19.9751 20.9485 19.6814 20.7306L19.5973 20.658L15.7793 16.84C14.3487 18.0325 12.5081 18.75 10.5 18.75C5.94365 18.75 2.25 15.0563 2.25 10.5ZM15.4131 15.1286C15.3553 15.1613 15.3008 15.2023 15.2516 15.2516C15.2023 15.3008 15.1613 15.3553 15.1286 15.4131C13.9202 16.552 12.2916 17.25 10.5 17.25C6.77208 17.25 3.75 14.2279 3.75 10.5C3.75 6.77208 6.77208 3.75 10.5 3.75C14.2279 3.75 17.25 6.77208 17.25 10.5C17.25 12.2916 16.552 13.9202 15.4131 15.1286ZM10.5 6.25C10.8797 6.25 11.1935 6.53215 11.2432 6.89823L11.25 7V9.75H14C14.4142 9.75 14.75 10.0858 14.75 10.5C14.75 10.8797 14.4678 11.1935 14.1018 11.2432L14 11.25H11.25V14C11.25 14.4142 10.9142 14.75 10.5 14.75C10.1203 14.75 9.80651 14.4678 9.75685 14.1018L9.75 14V11.25H7C6.58579 11.25 6.25 10.9142 6.25 10.5C6.25 10.1203 6.53215 9.80651 6.89823 9.75685L7 9.75H9.75V7C9.75 6.58579 10.0858 6.25 10.5 6.25Z", fill: "#788494" })));
});

const PrinterIcon = React.forwardRef(function PrinterIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: 24, height: 24, viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M10.5001 8.00415C10.0858 8.00415 9.75006 8.33994 9.75006 8.75415C9.75006 9.16836 10.0858 9.50415 10.5001 9.50415H13.5001C13.9143 9.50415 14.2501 9.16836 14.2501 8.75415C14.2501 8.33994 13.9143 8.00415 13.5001 8.00415H10.5001Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18 19.25V19.5C18 20.6046 17.1046 21.5 16 21.5H8C6.89543 21.5 6 20.6046 6 19.5V19.25H4.15C2.96259 19.25 2 18.2874 2 17.1V10.1542C2 8.96674 2.96259 8.00415 4.15 8.00415H6V4.5C6 3.39543 6.89543 2.5 8 2.5H16C17.1046 2.5 18 3.39543 18 4.5V8.00415H19.8501C21.0375 8.00415 22.0001 8.96674 22.0001 10.1542V17.1C22.0001 18.2874 21.0375 19.25 19.8501 19.25H18ZM16 4H8C7.72386 4 7.5 4.22386 7.5 4.5V8.00415H8.00006C8.41427 8.00415 8.75006 8.33994 8.75006 8.75415C8.75006 9.16836 8.41427 9.50415 8.00006 9.50415H4.15C3.79101 9.50415 3.5 9.79517 3.5 10.1542V17.1C3.5 17.459 3.79101 17.75 4.15 17.75H6V15C6 13.8954 6.89543 13 8 13H16C17.1046 13 18 13.8954 18 15V17.75H19.8501C20.209 17.75 20.5001 17.459 20.5001 17.1V10.1542C20.5001 9.79516 20.209 9.50415 19.8501 9.50415H16.0001C15.5858 9.50415 15.2501 9.16836 15.2501 8.75415C15.2501 8.33994 15.5858 8.00415 16.0001 8.00415H16.5V4.5C16.5 4.22386 16.2761 4 16 4ZM8 14.5H16C16.2761 14.5 16.5 14.7239 16.5 15V19.5C16.5 19.7761 16.2761 20 16 20H8C7.72386 20 7.5 19.7761 7.5 19.5V15C7.5 14.7239 7.72386 14.5 8 14.5Z", fill: "currentColor" })));
});

const PumpIcon = React.forwardRef(function PumpIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M11.5 5.25H6.5C5.5335 5.25 4.75 6.0335 4.75 7V10C4.75 10.9665 5.5335 11.75 6.5 11.75H11.5C12.4665 11.75 13.25 10.9665 13.25 10V7C13.25 6.0335 12.4665 5.25 11.5 5.25ZM6.5 6.75H11.5C11.6381 6.75 11.75 6.86193 11.75 7V10C11.75 10.1381 11.6381 10.25 11.5 10.25H6.5C6.36193 10.25 6.25 10.1381 6.25 10V7C6.25 6.86193 6.36193 6.75 6.5 6.75Z", fill: "currentColor" }),
        React.createElement("path", { d: "M13.25 17.5C13.25 17.0858 12.9142 16.75 12.5 16.75H5.5L5.39823 16.7568C5.03215 16.8065 4.75 17.1203 4.75 17.5C4.75 17.9142 5.08579 18.25 5.5 18.25H12.5L12.6018 18.2432C12.9678 18.1935 13.25 17.8797 13.25 17.5Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.4306 2.25H5.56944C3.47284 2.25 1.75 3.84031 1.75 5.83333V20C1.75 20.9665 2.5335 21.75 3.5 21.75H14.5C15.4665 21.75 16.25 20.9665 16.25 20V12.75H16.5L16.6278 12.7565C17.2581 12.8205 17.75 13.3528 17.75 14V18.0455L17.7552 18.1995C17.8344 19.3703 18.8091 20.2955 20 20.2955C21.2426 20.2955 22.25 19.2881 22.25 18.0455V7.41421L22.2414 7.24125C22.2017 6.84063 22.0246 6.46394 21.7374 6.17678L18.0303 2.46967L17.9462 2.39705C17.6526 2.1792 17.2359 2.2034 16.9697 2.46967C16.6768 2.76256 16.6768 3.23744 16.9697 3.53033L17.75 4.31066V7.86631L17.7574 8.06762C17.8015 8.66871 18.0422 9.2408 18.4452 9.69393L20.75 12.2853V18.0455L20.7432 18.1472C20.6935 18.5133 20.3797 18.7955 20 18.7955C19.5858 18.7955 19.25 18.4597 19.25 18.0455V14L19.245 13.8325C19.1584 12.3917 17.9625 11.25 16.5 11.25H16.25V5.83333C16.25 3.84031 14.5272 2.25 12.4306 2.25ZM20.75 7.41421V10.0282L19.566 8.69704L19.4844 8.59504C19.3326 8.38351 19.25 8.12883 19.25 7.86631V5.81066L20.6768 7.23744L20.7166 7.28937C20.7383 7.32701 20.75 7.37001 20.75 7.41421ZM5.56944 3.75H12.4306C13.7244 3.75 14.75 4.69674 14.75 5.83333V20C14.75 20.1381 14.6381 20.25 14.5 20.25H3.5C3.36193 20.25 3.25 20.1381 3.25 20V5.83333C3.25 4.69674 4.27564 3.75 5.56944 3.75Z", fill: "currentColor" })));
});

const ReceiptIcon = React.forwardRef(function ReceiptIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M16.75 7C16.75 6.58579 16.4142 6.25 16 6.25H8L7.89823 6.25685C7.53215 6.30651 7.25 6.6203 7.25 7C7.25 7.41421 7.58579 7.75 8 7.75H16L16.1018 7.74315C16.4678 7.69349 16.75 7.3797 16.75 7Z", fill: "currentColor" }),
        React.createElement("path", { d: "M16 10.25C16.4142 10.25 16.75 10.5858 16.75 11C16.75 11.3797 16.4678 11.6935 16.1018 11.7432L16 11.75H8C7.58579 11.75 7.25 11.4142 7.25 11C7.25 10.6203 7.53215 10.3065 7.89823 10.2568L8 10.25H16Z", fill: "currentColor" }),
        React.createElement("path", { d: "M16.75 15C16.75 14.5858 16.4142 14.25 16 14.25H8L7.89823 14.2568C7.53215 14.3065 7.25 14.6203 7.25 15C7.25 15.4142 7.58579 15.75 8 15.75H16L16.1018 15.7432C16.4678 15.6935 16.75 15.3797 16.75 15Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19 2.25H5C4.0335 2.25 3.25 3.0335 3.25 4V21L3.25655 21.1027C3.33411 21.7012 4.08272 21.9779 4.53033 21.5303L6 20.061L7.46967 21.5303L7.55379 21.6029C7.8474 21.8208 8.26406 21.7966 8.53033 21.5303L10 20.061L11.4697 21.5303L11.5538 21.6029C11.8474 21.8208 12.2641 21.7966 12.5303 21.5303L14 20.061L15.4697 21.5303L15.5538 21.6029C15.8474 21.8208 16.2641 21.7966 16.5303 21.5303L18 20.061L19.4697 21.5303C19.9421 22.0028 20.75 21.6682 20.75 21V4C20.75 3.0335 19.9665 2.25 19 2.25ZM19 3.75L19.0573 3.7566C19.1678 3.78251 19.25 3.88165 19.25 4V19.189L18.5303 18.4697L18.4462 18.3971C18.1526 18.1792 17.7359 18.2034 17.4697 18.4697L16 19.939L14.5303 18.4697L14.4462 18.3971C14.1526 18.1792 13.7359 18.2034 13.4697 18.4697L12 19.939L10.5303 18.4697L10.4462 18.3971C10.1526 18.1792 9.73594 18.2034 9.46967 18.4697L8 19.939L6.53033 18.4697L6.44621 18.3971C6.1526 18.1792 5.73594 18.2034 5.46967 18.4697L4.75 19.188V4C4.75 3.86193 4.86193 3.75 5 3.75H19Z", fill: "currentColor" })));
});

const ReloadIcon = React.forwardRef(function ReloadIcon(providedProps, ref) {
    const _a = useDefaultSvgProps(providedProps), { width = 20 } = _a, props = __rest(_a, ["width"]);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 20 20", width: width }, props, { ref: ref }),
        React.createElement("path", { fill: "currentColor", d: "M15.74 5.722V3.585c0-.316.235-.578.54-.62l.084-.005c.317 0 .578.235.62.54l.005.085v4.01c0 .317-.235.578-.54.62l-.085.005h-4.01c-.345 0-.625-.28-.625-.625 0-.316.236-.578.54-.62l.085-.005H15.1c-1.445-2.232-4.2-3.275-6.786-2.519-2.73.799-4.536 3.388-4.343 6.226.193 2.837 2.334 5.159 5.146 5.58 2.813.422 5.54-1.17 6.557-3.826.123-.322.485-.484.807-.36.322.123.484.485.36.807-1.226 3.204-4.516 5.124-7.909 4.615-3.393-.508-5.975-3.308-6.208-6.731-.233-3.423 1.947-6.547 5.24-7.51 2.89-.846 5.955.173 7.776 2.47z" })));
});

/**
 * @alias ReloadIcon
 */
const ReplayIcon = ReloadIcon;

const RewardsIcon = React.forwardRef(function RewardsIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.25 4.75C5.25 3.21921 6.5563 2 8.14286 2C9.76223 2 10.984 2.91227 11.8426 4.36923C11.8972 4.46196 11.9497 4.55575 12 4.65039C12.0503 4.55575 12.1028 4.46196 12.1574 4.36923C13.016 2.91227 14.2378 2 15.8571 2C17.4437 2 18.75 3.21921 18.75 4.75C18.75 6.28079 17.4437 7.5 15.8571 7.5H13.6875L14.85 9.05C15.0985 9.38137 15.0314 9.85147 14.7 10.1C14.3988 10.3259 13.9829 10.291 13.723 10.0338L13.65 9.95L11.999 7.75L10.35 9.95C10.1241 10.2512 9.71501 10.3341 9.39354 10.16L9.3 10.1C8.99875 9.87407 8.91587 9.46501 9.09001 9.14354L9.15 9.05L10.3125 7.5H8.14286C6.5563 7.5 5.25 6.28079 5.25 4.75ZM10.5503 5.13077C9.93563 4.08773 9.14849 3.5 8.14286 3.5C7.36248 3.5 6.75 4.07165 6.75 4.75C6.75 5.42835 7.36248 6 8.14286 6H10.972L10.8618 5.73568C10.8153 5.63206 10.7661 5.52949 10.7142 5.4285L10.5503 5.13077ZM15.8571 3.5C16.6375 3.5 17.25 4.07165 17.25 4.75L17.2429 4.87583C17.1728 5.49757 16.5887 6 15.8571 6H13.027L13.1382 5.73568C13.2312 5.52844 13.335 5.32543 13.4497 5.13077C14.0644 4.08773 14.8515 3.5 15.8571 3.5Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4.75 6.75C4.75 6.33579 4.41421 6 4 6H3L2.85892 6.00604C1.93043 6.08607 1.25 6.9502 1.25 7.95V12.75L1.25685 12.8518C1.30651 13.2178 1.6203 13.5 2 13.5H2.25V18.75C2.25 20.2688 3.48122 21.5 5 21.5H19C20.5188 21.5 21.75 20.2688 21.75 18.75V13.5H22L22.1018 13.4932C22.4678 13.4435 22.75 13.1297 22.75 12.75V7.95L22.745 7.80113C22.678 6.81652 21.9522 6 21 6H20L19.8982 6.00685C19.5322 6.05651 19.25 6.3703 19.25 6.75C19.25 7.16421 19.5858 7.5 20 7.5H21L21.0408 7.50803C21.1388 7.54516 21.25 7.70877 21.25 7.95V12H2.75V7.95L2.75645 7.85151C2.78568 7.63345 2.90835 7.5 3 7.5H4L4.10177 7.49315C4.46785 7.44349 4.75 7.1297 4.75 6.75ZM12.75 20H19C19.6904 20 20.25 19.4404 20.25 18.75V13.5H12.75V20ZM11.25 13.5V20H5L4.87219 19.9935C4.24187 19.9295 3.75 19.3972 3.75 18.75V13.5H11.25Z", fill: "currentColor" })));
});

const ScanIcon = React.forwardRef(function ScanIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.5 3.25C4.25736 3.25 3.25 4.25736 3.25 5.5V8C3.25 8.41421 3.58579 8.75 4 8.75C4.41421 8.75 4.75 8.41421 4.75 8V5.5C4.75 5.08579 5.08579 4.75 5.5 4.75H8C8.41421 4.75 8.75 4.41421 8.75 4C8.75 3.58579 8.41421 3.25 8 3.25H5.5ZM16 3.25C15.5858 3.25 15.25 3.58579 15.25 4C15.25 4.41421 15.5858 4.75 16 4.75H18.5C18.9142 4.75 19.25 5.08579 19.25 5.5V8C19.25 8.41421 19.5858 8.75 20 8.75C20.4142 8.75 20.75 8.41421 20.75 8V5.5C20.75 4.25736 19.7426 3.25 18.5 3.25H16ZM4.75 16C4.75 15.5858 4.41421 15.25 4 15.25C3.58579 15.25 3.25 15.5858 3.25 16V18.5C3.25 19.7426 4.25736 20.75 5.5 20.75H8C8.41421 20.75 8.75 20.4142 8.75 20C8.75 19.5858 8.41421 19.25 8 19.25H5.5C5.08579 19.25 4.75 18.9142 4.75 18.5V16ZM20.75 16C20.75 15.5858 20.4142 15.25 20 15.25C19.5858 15.25 19.25 15.5858 19.25 16V18.5C19.25 18.9142 18.9142 19.25 18.5 19.25H16C15.5858 19.25 15.25 19.5858 15.25 20C15.25 20.4142 15.5858 20.75 16 20.75H18.5C19.7426 20.75 20.75 19.7426 20.75 18.5V16ZM1.75 11.5C1.33579 11.5 1 11.8358 1 12.25C1 12.6642 1.33579 13 1.75 13H22.25C22.6642 13 23 12.6642 23 12.25C23 11.8358 22.6642 11.5 22.25 11.5H1.75Z", fill: "currentColor" })));
});

const SearchIcon = React.forwardRef(function SearchIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M10.5 2.25C5.94365 2.25 2.25 5.94365 2.25 10.5C2.25 15.0563 5.94365 18.75 10.5 18.75C12.5081 18.75 14.3487 18.0325 15.7793 16.84L19.5973 20.658L19.6814 20.7306C19.9751 20.9485 20.3917 20.9243 20.658 20.658C20.9509 20.3651 20.9509 19.8902 20.658 19.5973L16.84 15.7793C18.0325 14.3487 18.75 12.5081 18.75 10.5C18.75 5.94365 15.0563 2.25 10.5 2.25ZM15.4131 15.1286C16.552 13.9202 17.25 12.2916 17.25 10.5C17.25 6.77208 14.2279 3.75 10.5 3.75C6.77208 3.75 3.75 6.77208 3.75 10.5C3.75 14.2279 6.77208 17.25 10.5 17.25C12.2916 17.25 13.9202 16.552 15.1286 15.4131C15.1613 15.3553 15.2023 15.3008 15.2516 15.2516C15.3008 15.2023 15.3553 15.1613 15.4131 15.1286Z", fill: "currentColor" })));
});

const SetelDefaultIcon = React.forwardRef(function SetelDefaultIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "130", height: "140", viewBox: "0 0 130 140", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M42.5359 33.8579C43.417 32.5516 44.7854 31.6534 46.3343 31.3646L90.5746 23.1151C93.1399 22.6367 95.0113 25.4877 93.5521 27.6511L54.225 85.9559L26.9155 67.5354C24.2222 65.7188 23.5116 62.0627 25.3282 59.3694L42.5359 33.8579Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M87.624 105.868C86.7429 107.174 85.3745 108.072 83.8256 108.361L39.5853 116.611C37.02 117.089 35.1486 114.238 36.6078 112.075L75.9349 53.77L108.121 75.4798L87.624 105.868Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M75.9351 53.7698L108.121 75.4796L54.2252 85.9559L75.9351 53.7698Z", fill: "#2F48FA" })));
});

const SetelIcon = React.forwardRef(function SetelIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.90489 4.26375L18.3613 2.26333C19.0176 2.13778 19.4981 2.86839 19.1228 3.42122L15.1844 9.22265L20.5276 12.873C20.61 12.9293 20.6776 12.999 20.7295 13.0773C20.7776 13.1496 20.8138 13.2316 20.8347 13.3208C20.8372 13.3313 20.8394 13.3418 20.8414 13.3524C20.8601 13.4506 20.8587 13.5482 20.8399 13.6407C20.8212 13.734 20.7842 13.8252 20.7281 13.9091C20.7265 13.9115 20.7248 13.9139 20.7232 13.9163L16.4162 20.2261C16.3006 20.3956 16.1206 20.5102 15.9182 20.5434L6.50722 22.0877C5.86385 22.1932 5.40146 21.4843 5.75744 20.938L9.09003 15.8246L3.54944 11.6604C3.23134 11.4213 3.15533 10.9754 3.37627 10.6444L7.42203 4.58398C7.53359 4.41687 7.70754 4.30151 7.90489 4.26375ZM18.2179 13.1112L14.3641 10.478L11.7958 14.4193L18.2179 13.1112ZM10.6457 16.1844L18.4421 14.5963L15.3581 19.115L7.94305 20.332L10.6457 16.1844ZM9.80005 14.482L16.8641 4.07597L8.49405 5.67797L5.01705 10.887L9.80005 14.482Z", fill: "currentColor" })));
});

const SetelLightIcon = React.forwardRef(function SetelLightIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "120", height: "120", viewBox: "0 0 120 120", fill: "none" }, props, { ref: ref }),
        React.createElement("circle", { cx: "60.0001", cy: "60", r: "58.5748", fill: "white" }),
        React.createElement("path", { d: "M41.4906 30.0704C42.2238 28.9835 43.3624 28.2361 44.6512 27.9958L81.4634 21.1314C83.598 20.7334 85.1552 23.1057 83.941 24.9058L51.2171 73.421L28.4929 58.0934C26.2518 56.5818 25.6605 53.5396 27.1721 51.2985L41.4906 30.0704Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M79.0082 89.9896C78.275 91.0766 77.1364 91.824 75.8475 92.0643L39.0353 98.9287C36.9008 99.3267 35.3436 96.9544 36.5578 95.1543L69.2817 46.639L96.0637 64.7037L79.0082 89.9896Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M69.2817 46.639L96.0637 64.7037L51.217 73.4211L69.2817 46.639Z", fill: "#2F48FA" })));
});

const SetelShareIcon = React.forwardRef(function SetelShareIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "42", height: "30", viewBox: "0 0 42 30", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.6 0H38.4C40.3882 0 42 1.87416 42 4.18605V25.814C42 28.1258 40.3882 30 38.4 30H3.6C1.61177 30 0 28.1258 0 25.814V4.18605C0 1.87416 1.61177 0 3.6 0Z", fill: "white" }),
        React.createElement("path", { d: "M3.6 0.5H38.4C40.044 0.5 41.5 2.07688 41.5 4.18605V25.814C41.5 27.9231 40.044 29.5 38.4 29.5H3.6C1.95602 29.5 0.5 27.9231 0.5 25.814V4.18605C0.5 2.07688 1.95602 0.5 3.6 0.5Z", stroke: "black", strokeOpacity: "0.08" }),
        React.createElement("path", { d: "M10.2476 9.5313C10.3825 9.33127 10.5921 9.19374 10.8292 9.14951L17.6035 7.88631C17.9963 7.81306 18.2829 8.24962 18.0595 8.58088L12.0375 17.5088L7.85574 14.6882C7.44333 14.41 7.3345 13.8502 7.61268 13.4377L10.2476 9.5313Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M17.1518 20.5578C17.0169 20.7578 16.8074 20.8954 16.5702 20.9396L9.79588 22.2028C9.40307 22.2761 9.11651 21.8395 9.33996 21.5082L15.3619 12.5803L20.2904 15.9046L17.1518 20.5578Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M15.3618 12.5803L20.2903 15.9046L12.0375 17.5088L15.3618 12.5803Z", fill: "#2F48FA" }),
        React.createElement("path", { d: "M32.3992 12.0469C32.3992 13.1342 31.5177 14.0156 30.4304 14.0156C29.3431 14.0156 28.4617 13.1342 28.4617 12.0469C28.4617 10.9596 29.3431 10.0781 30.4304 10.0781C31.5177 10.0781 32.3992 10.9596 32.3992 12.0469Z", fill: "#237CCC" }),
        React.createElement("path", { d: "M26.9851 18.4453C26.9851 16.5425 28.5276 15 30.4304 15C32.3332 15 33.8757 16.5425 33.8757 18.4453V19.725C33.8757 20.3774 33.3469 20.9062 32.6945 20.9062H28.1664C27.514 20.9062 26.9851 20.3774 26.9851 19.725V18.4453Z", fill: "#237CCC" }),
        React.createElement("path", { d: "M27.4773 11.0625C27.4773 12.1498 26.5959 13.0312 25.5085 13.0312C24.4212 13.0312 23.5398 12.1498 23.5398 11.0625C23.5398 9.97519 24.4212 9.09375 25.5085 9.09375C26.5959 9.09375 27.4773 9.97519 27.4773 11.0625Z", fill: "#1E68AB" }),
        React.createElement("path", { d: "M27.6405 14.7543C27.0541 14.2917 26.3136 14.0156 25.5085 14.0156C23.6056 14.0156 22.0632 15.5582 22.0632 17.4609V18.7406C22.0632 19.393 22.5922 19.9219 23.2444 19.9219H25.8121C25.8068 19.8569 25.8039 19.7913 25.8039 19.725V18.4453C25.8039 16.9379 26.5246 15.599 27.6405 14.7543Z", fill: "#1E68AB" })));
});

const SetelWalletIcon = React.forwardRef(function SetelWalletIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "42", height: "31", viewBox: "0 0 42 31", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.6 0.5H38.4C40.3882 0.5 42 2.37416 42 4.68605V26.314C42 28.6258 40.3882 30.5 38.4 30.5H3.6C1.61177 30.5 0 28.6258 0 26.314V4.68605C0 2.37416 1.61177 0.5 3.6 0.5Z", fill: "white" }),
        React.createElement("path", { d: "M3.6 1H38.4C40.044 1 41.5 2.57688 41.5 4.68605V26.314C41.5 28.4231 40.044 30 38.4 30H3.6C1.95602 30 0.5 28.4231 0.5 26.314V4.68605C0.5 2.57688 1.95602 1 3.6 1Z", stroke: "black", strokeOpacity: "0.08" }),
        React.createElement("path", { d: "M17.6305 9.92873C17.7626 9.73279 17.9679 9.59806 18.2002 9.55474L24.8363 8.31731C25.221 8.24556 25.5018 8.67321 25.2829 8.99772L19.3838 17.7434L15.2874 14.9804C14.8834 14.7079 14.7768 14.1595 15.0493 13.7555L17.6305 9.92873Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M24.3935 20.7302C24.2613 20.9261 24.0561 21.0609 23.8237 21.1042L17.1877 22.3416C16.8029 22.4134 16.5222 21.9857 16.7411 21.6612L22.6401 12.9155L27.468 16.172L24.3935 20.7302Z", fill: "#00B0FF" }),
        React.createElement("path", { d: "M22.6401 12.9155L27.4681 16.172L19.3837 17.7435L22.6401 12.9155Z", fill: "#2F48FA" })));
});

const SettlementsIcon = React.forwardRef(function SettlementsIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M10.6656 4.96511C11.0798 4.96511 11.4156 5.3009 11.4156 5.71511V6.41217H12.9613C13.3755 6.41217 13.7113 6.74796 13.7113 7.16217C13.7113 7.57638 13.3755 7.91217 12.9613 7.91217H11.4156V9.88536H11.8133C12.4581 9.88536 13.0634 10.1626 13.5002 10.6346C13.9351 11.1046 14.1704 11.7304 14.1704 12.372C14.1704 13.0135 13.9351 13.6393 13.5002 14.1093C13.0634 14.5813 12.4581 14.8586 11.8133 14.8586H11.4156V15.5558C11.4156 15.97 11.0798 16.3058 10.6656 16.3058C10.2514 16.3058 9.91559 15.97 9.91559 15.5558V14.8586H7.9104C7.49619 14.8586 7.1604 14.5228 7.1604 14.1086C7.1604 13.6943 7.49619 13.3586 7.9104 13.3586H9.91559V11.3854H9.51749C8.87277 11.3854 8.2674 11.1081 7.83065 10.6361C7.39571 10.1661 7.1604 9.5403 7.1604 8.89877C7.1604 8.25723 7.39571 7.63139 7.83065 7.1614C8.2674 6.68945 8.87277 6.41217 9.51749 6.41217H9.91559V5.71511C9.91559 5.3009 10.2514 4.96511 10.6656 4.96511ZM9.91559 7.91217H9.51749C9.30976 7.91217 9.09759 8.00081 8.93156 8.18022C8.76373 8.36157 8.6604 8.61916 8.6604 8.89877C8.6604 9.17838 8.76373 9.43596 8.93156 9.61732C9.09759 9.79672 9.30976 9.88536 9.51749 9.88536H9.91559V7.91217ZM11.4156 11.3854V13.3586H11.8133C12.0211 13.3586 12.2332 13.2699 12.3993 13.0905C12.5671 12.9092 12.6704 12.6516 12.6704 12.372C12.6704 12.0924 12.5671 11.8348 12.3993 11.6534C12.2332 11.474 12.0211 11.3854 11.8133 11.3854H11.4156Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.33112 1.55405C2.9169 1.55405 2.58112 1.88983 2.58112 2.30405V18.9669C2.58112 19.3811 2.9169 19.7169 3.33112 19.7169H17.9997C18.4139 19.7169 18.7497 19.3811 18.7497 18.9669V2.30405C18.7497 1.88983 18.4139 1.55405 17.9997 1.55405H3.33112ZM4.08112 18.2169V3.05405H17.2497V18.2169H4.08112Z", fill: "currentColor" }),
        React.createElement("path", { d: "M21.4189 5.30405C21.4189 4.88983 21.0831 4.55405 20.6689 4.55405C20.2547 4.55405 19.9189 4.88983 19.9189 5.30405V20.9461H6.53802C6.12381 20.9461 5.78802 21.2818 5.78802 21.6961C5.78802 22.1103 6.12381 22.4461 6.53802 22.4461H20.6689C21.0831 22.4461 21.4189 22.1103 21.4189 21.6961V5.30405Z", fill: "currentColor" })));
});

const ShareIcon = React.forwardRef(function ShareIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14.9537 7.49873C15.6404 8.26665 16.6388 8.75 17.75 8.75C19.8211 8.75 21.5 7.07107 21.5 5C21.5 2.92893 19.8211 1.25 17.75 1.25C15.6789 1.25 14 2.92893 14 5C14 5.42095 14.0694 5.8257 14.1973 6.20345L8.54625 9.50125C7.85957 8.73334 6.86123 8.25 5.75 8.25C3.67893 8.25 2 9.92893 2 12C2 14.0711 3.67893 15.75 5.75 15.75C6.86138 15.75 7.85984 15.2665 8.54654 14.4984L14.1988 17.7921C14.0699 18.1711 14 18.5774 14 19C14 21.0711 15.6789 22.75 17.75 22.75C19.8211 22.75 21.5 21.0711 21.5 19C21.5 16.9289 19.8211 15.25 17.75 15.25C16.6404 15.25 15.6434 15.7319 14.9569 16.4978L9.30286 13.2031C9.43069 12.8254 9.5 12.4208 9.5 12C9.5 11.579 9.43064 11.1743 9.30272 10.7965L14.9537 7.49873ZM17.75 2.75C16.5074 2.75 15.5 3.75736 15.5 5C15.5 5.39485 15.6017 5.76594 15.7804 6.08851C15.7899 6.10258 15.799 6.11708 15.8077 6.13199C15.8164 6.14677 15.8244 6.16173 15.8319 6.17684C16.2278 6.82067 16.9388 7.25 17.75 7.25C18.9926 7.25 20 6.24264 20 5C20 3.75736 18.9926 2.75 17.75 2.75ZM7.66766 10.8225C7.67527 10.8378 7.68344 10.853 7.6922 10.868C7.70103 10.8832 7.7103 10.8979 7.72 10.9121C7.89843 11.2345 8 11.6054 8 12C8 12.3946 7.89841 12.7655 7.71995 13.088C7.71019 13.1023 7.70085 13.1172 7.69196 13.1324C7.68331 13.1472 7.67523 13.1623 7.66771 13.1774C7.27177 13.8209 6.56099 14.25 5.75 14.25C4.50736 14.25 3.5 13.2426 3.5 12C3.5 10.7574 4.50736 9.75 5.75 9.75C6.56096 9.75 7.27171 10.179 7.66766 10.8225ZM15.5 19C15.5 18.6252 15.5916 18.2719 15.7537 17.9611C15.7771 17.932 15.7986 17.9008 15.818 17.8676C15.8367 17.8356 15.8527 17.8027 15.8662 17.7692C16.268 17.1555 16.9616 16.75 17.75 16.75C18.9926 16.75 20 17.7574 20 19C20 20.2426 18.9926 21.25 17.75 21.25C16.5074 21.25 15.5 20.2426 15.5 19Z", fill: "currentColor" })));
});

const ShoppingBagIcon = React.forwardRef(function ShoppingBagIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.4 2.55C5.54164 2.36115 5.76393 2.25 6 2.25H18C18.2361 2.25 18.4584 2.36115 18.6 2.55L21.6 5.55C21.6974 5.67982 21.75 5.83772 21.75 6L20.75 19C20.75 19.7293 20.4603 20.4288 19.9445 20.9445C19.4288 21.4603 18.7293 21.75 18 21.75H6C5.27065 21.75 4.57118 21.4603 4.05546 20.9445C3.53973 20.4288 3.25 19.7293 3.25 19L2.25 6C2.25 5.83772 2.30263 5.67982 2.4 5.55L5.4 2.55ZM6.375 3.75L3.75 6.25L4.75 19C4.75 19.3315 4.8817 19.6495 5.11612 19.8839C5.35054 20.1183 5.66848 20.25 6 20.25H18C18.3315 20.25 18.6495 20.1183 18.8839 19.8839C19.1183 19.6495 19.25 19.3315 19.25 19L20.25 6.25L17.625 3.75H6.375Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5.25 6C5.25 5.58579 5.58579 5.25 6 5.25H18C18.4142 5.25 18.75 5.58579 18.75 6C18.75 6.41421 18.4142 6.75 18 6.75H6C5.58579 6.75 5.25 6.41421 5.25 6Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8 9.25C8.41421 9.25 8.75 9.58579 8.75 10C8.75 10.862 9.09241 11.6886 9.7019 12.2981C10.3114 12.9076 11.138 13.25 12 13.25C12.862 13.25 13.6886 12.9076 14.2981 12.2981C14.9076 11.6886 15.25 10.862 15.25 10C15.25 9.58579 15.5858 9.25 16 9.25C16.4142 9.25 16.75 9.58579 16.75 10C16.75 11.2598 16.2496 12.468 15.3588 13.3588C14.468 14.2496 13.2598 14.75 12 14.75C10.7402 14.75 9.53204 14.2496 8.64124 13.3588C7.75044 12.468 7.25 11.2598 7.25 10C7.25 9.58579 7.58579 9.25 8 9.25Z", fill: "currentColor" })));
});

const SmartPayDarkIcon = React.forwardRef(function SmartPayDarkIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "42", height: "30", viewBox: "0 0 42 30", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M0 4C0 1.79086 1.79086 0 4 0H38C40.2091 0 42 1.79086 42 4V26C42 28.2091 40.2091 30 38 30H4C1.79086 30 0 28.2091 0 26V4Z", fill: "#1B1A19" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18.5695 11.1454C18.5695 11.4797 18.3138 11.6952 17.894 11.6952H17.1933V10.7612C17.1933 10.6611 17.277 10.5753 17.3765 10.5753H17.889C18.3138 10.5753 18.5695 10.7686 18.5695 11.1314V11.1454ZM19.741 11.0909V11.0814C19.741 10.1413 19.051 9.58691 17.9806 9.58691H16.3164C16.1584 9.58691 16.0156 9.71352 16.0156 9.86253V13.8311H17.1934V12.7573C17.1934 12.6876 17.2593 12.6179 17.3254 12.6179H17.7427L18.5627 13.8311H19.9205L18.9425 12.4062C19.4385 12.1644 19.741 11.7216 19.741 11.0909Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23.4132 11.7198C23.4132 12.332 22.9825 12.8547 22.3289 12.8547C21.6906 12.8547 21.247 12.3187 21.247 11.7097V11.6953C21.247 11.0816 21.6761 10.5625 22.3182 10.5625C22.9694 10.5625 23.4132 11.0977 23.4132 11.7097V11.7198ZM22.329 9.5C21.0225 9.5 20.043 10.4894 20.043 11.7097V11.7198C20.043 12.9423 21.012 13.9178 22.3183 13.9178C23.628 13.9178 24.6125 12.9262 24.6125 11.7097V11.6953C24.6125 10.4784 23.6404 9.5 22.329 9.5Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M27.9472 9.85922V11.8318L26.2041 9.58691H25.4075C25.2403 9.58691 25.1055 9.72088 25.1055 9.88567V13.8311H26.2691V11.5032L28.0839 13.8311H29.1119V9.58691H28.2506C28.0902 9.58691 27.9472 9.70518 27.9472 9.85922Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.0234 9.8746V10.6166H13.2929V13.8311H14.4736V10.7528C14.4736 10.6799 14.533 10.6166 14.6211 10.6166H15.7473V9.58691H12.3239C12.1638 9.58691 12.0234 9.72964 12.0234 9.8746Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6.9067 11.1454C6.9067 11.4797 6.65406 11.6952 6.23713 11.6952H5.67631V10.5753H6.22771C6.65406 10.5753 6.9067 10.7686 6.9067 11.1314V11.1454ZM6.31749 9.58691H4.80214C4.64173 9.58691 4.5 9.71113 4.5 9.86253V13.8311H5.67635V12.7573C5.67635 12.6916 5.74676 12.6179 5.81403 12.6179H6.25857C7.31004 12.6179 8.08284 12.0913 8.08284 11.0909V11.0814C8.08284 10.1413 7.39244 9.58691 6.31749 9.58691Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.33595 9.87699V13.8311H11.7489V12.8273H9.50064V12.3178C9.50064 12.236 9.57197 12.1564 9.64626 12.1564H11.4843V11.2299H9.50064V10.7475C9.50064 10.6725 9.56461 10.5861 9.63759 10.5861H11.7489V9.58691H8.63917C8.47529 9.58691 8.3343 9.72088 8.33595 9.87699Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M36.0938 11.1868C35.5016 11.0548 35.3562 10.9691 35.3562 10.7775V10.7631C35.3562 10.6114 35.4936 10.4897 35.7809 10.4897C36.0389 10.4897 36.3196 10.5591 36.5963 10.6921C36.5963 10.6921 36.6586 10.7226 36.6874 10.7346C36.765 10.7724 36.8595 10.8023 36.9707 10.7782C37.0568 10.7617 37.1155 10.6887 37.1874 10.598L37.5536 10.0801C37.0977 9.70963 36.5356 9.5127 35.8101 9.5127C34.7935 9.5127 34.1667 10.0838 34.1667 10.874V10.8827C34.1667 11.7586 34.8669 12.0115 35.7709 12.2198C36.3447 12.3511 36.4827 12.4476 36.4827 12.6251V12.6359C36.4827 12.8193 36.3118 12.9265 35.9994 12.9265C35.6266 12.9265 35.2722 12.8268 34.9392 12.6338C34.8421 12.5873 34.7235 12.522 34.5714 12.5555C34.5105 12.5705 34.4609 12.6124 34.4096 12.6736L33.9648 13.2063C34.4891 13.6648 35.2058 13.9033 35.9561 13.9033C36.9892 13.9033 37.6718 13.3874 37.6718 12.522V12.5116C37.6718 11.7174 37.0622 11.3975 36.0938 11.1868Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M31.7166 10.9807L32.1865 12.1885H31.2348L31.7166 10.9807ZM30.6005 13.8311L30.8163 13.2932C30.8467 13.2068 30.9204 13.1203 31.0089 13.106H32.5246L32.823 13.8311H34.0838L32.2898 9.58691H31.3277C31.183 9.60262 31.1271 9.677 31.0738 9.77749L29.3633 13.8311H30.6005Z", fill: "white" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M7.26078 17.1852C7.04806 16.8897 6.7717 16.7329 6.39319 16.7329C6.0975 16.7329 5.74643 16.8713 5.74643 17.2036C5.74643 17.9978 8.13804 17.3513 8.13804 19.0784C8.13804 20.1307 7.13145 20.5002 6.22642 20.5002C5.54312 20.5002 4.95231 20.3244 4.5 19.8165L5.23867 19.1242C5.5247 19.4381 5.82048 19.6689 6.29105 19.6689C6.61427 19.6689 7.02963 19.5118 7.02963 19.1611C7.02963 18.247 4.63843 18.9672 4.63843 17.2776C4.63843 16.2895 5.5247 15.8467 6.42038 15.8467C7.01161 15.8467 7.63921 16.0309 7.99937 16.5203L7.26078 17.1852Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M8.58594 15.9573H9.63873V16.6499H9.65675C9.85104 16.2342 10.2755 15.8467 11.014 15.8467C11.6974 15.8467 12.1683 16.1144 12.3994 16.668C12.7223 16.1049 13.1932 15.8467 13.8582 15.8467C15.0402 15.8467 15.4555 16.6867 15.4555 17.7486V20.3894H14.3475V17.8779C14.3475 17.3238 14.1814 16.8435 13.5262 16.8435C12.8328 16.8435 12.575 17.4163 12.575 17.9884V20.3894H11.4666V17.7486C11.4666 17.2036 11.2452 16.8435 10.7096 16.8435C9.97997 16.8435 9.69369 17.3788 9.69369 17.9703V20.3894H8.58594V15.9573Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18.77 18.4037H18.5396C17.9302 18.4037 16.8772 18.4507 16.8772 19.0877C16.8772 19.4933 17.2931 19.6689 17.653 19.6689C18.4103 19.6689 18.77 19.272 18.77 18.653V18.4037ZM18.8347 19.7798H18.8075C18.4937 20.2782 17.9762 20.5002 17.3853 20.5002C16.5637 20.5002 15.7695 20.0478 15.7695 19.1702C15.7695 17.7298 17.45 17.6286 18.5583 17.6286H18.8347V17.5084C18.8347 16.9638 18.4103 16.6773 17.8197 16.6773C17.3578 16.6773 16.9332 16.862 16.6374 17.139L16.0556 16.5575C16.5452 16.0587 17.2376 15.8467 17.9394 15.8467C19.8322 15.8467 19.8322 17.2133 19.8322 17.8404V20.3894H18.8347V19.7798Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20.4141 15.9573H21.5221V16.6593H21.5408C21.7807 16.1508 22.2701 15.8467 22.8519 15.8467C22.9902 15.8467 23.1198 15.8741 23.2491 15.911V16.9822C23.0639 16.9359 22.8887 16.8991 22.7131 16.8991C21.6701 16.8991 21.5221 17.7764 21.5221 18.0162V20.3894H20.4141V15.9573Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M23.7422 16.8985V15.9566H24.6562V14.8994H25.7643V15.9566H26.7002V16.8985H25.7643V18.8372C25.7643 19.2807 25.6672 19.5573 26.1565 19.5573C26.3502 19.5573 26.6181 19.5205 26.7568 19.4193V20.3516C26.526 20.4624 26.1378 20.4996 25.8795 20.4996C24.7066 20.4996 24.6562 19.973 24.6562 18.9296V16.8985H23.7422Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M29.8231 17.4972C29.8231 17.0711 29.5447 16.7268 28.9056 16.7268H28.1191V18.2589H28.9056C29.5447 18.2589 29.8231 17.9065 29.8231 17.4972ZM30.5955 17.5105C30.5955 18.4695 29.9607 19.1043 28.7814 19.1043H28.1188V20.2495C28.1188 20.4083 28.0431 20.4498 27.9185 20.4498H27.4975C27.3729 20.4498 27.2969 20.4083 27.2969 20.2495V16.1105C27.2969 15.9859 27.3384 15.9102 27.4975 15.9102H28.7881C29.9749 15.9102 30.5955 16.5587 30.5955 17.5105Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M33.1547 18.6635L32.5075 16.9354L31.8604 18.6635H33.1547ZM34.5308 20.4475H33.8944C33.7424 20.4475 33.6874 20.406 33.6321 20.2405L33.3518 19.4346H31.6626L31.3822 20.2405C31.3276 20.406 31.2721 20.4475 31.1207 20.4475H30.4836C30.3318 20.4475 30.3318 20.3369 30.3663 20.2546L31.897 16.1242C31.9523 15.9792 32.028 15.9102 32.1798 15.9102H32.842C32.9934 15.9102 33.0624 15.9792 33.1244 16.1242L34.6484 20.2546C34.6829 20.3369 34.6829 20.4475 34.5308 20.4475Z", fill: "#FFFFFE" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M37.6535 16.1131L36.317 18.4977V20.2656C36.317 20.4241 36.2413 20.4656 36.1171 20.4656H35.6002C35.4692 20.4656 35.3999 20.4241 35.3999 20.2656V18.4977L34.0082 16.1131C33.974 16.0371 33.9874 15.9199 34.119 15.9199H34.6523C34.8111 15.9199 34.8804 15.9407 34.9772 16.1269L35.8722 17.6996L36.7404 16.1269C36.8299 15.9407 36.8992 15.9199 37.0583 15.9199H37.5427C37.674 15.9199 37.688 16.0371 37.6535 16.1131Z", fill: "#FFFFFE" })));
});

const SmartPayLightIcon = React.forwardRef(function SmartPayLightIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "42", height: "30", viewBox: "0 0 42 30", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.6 0H38.4C40.3882 0 42 1.87416 42 4.18605V25.814C42 28.1258 40.3882 30 38.4 30H3.6C1.61177 30 0 28.1258 0 25.814V4.18605C0 1.87416 1.61177 0 3.6 0Z", fill: "white" }),
        React.createElement("path", { d: "M3.6 0.5H38.4C40.044 0.5 41.5 2.07688 41.5 4.18605V25.814C41.5 27.9231 40.044 29.5 38.4 29.5H3.6C1.95602 29.5 0.5 27.9231 0.5 25.814V4.18605C0.5 2.07688 1.95602 0.5 3.6 0.5Z", stroke: "black", strokeOpacity: "0.08" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19.9723 11.7922C19.9723 10.5765 20.9397 9.59082 22.2301 9.59082C23.5252 9.59082 24.4853 10.5655 24.4853 11.7778V11.7922C24.4853 13.0041 23.513 13.9919 22.2195 13.9919C20.9294 13.9919 19.9723 13.0201 19.9723 11.8022V11.7922ZM22.2303 12.9328C22.8758 12.9328 23.3012 12.4121 23.3012 11.8022V11.7922C23.3012 11.1825 22.8629 10.6493 22.2197 10.6493C21.5856 10.6493 21.1618 11.1665 21.1618 11.7779V11.7922C21.1618 12.3989 21.5999 12.9328 22.2303 12.9328Z", fill: "#00A19C" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M19.6725 11.1665V11.1758C19.6725 11.8042 19.3738 12.2453 18.8839 12.4862L19.8498 13.9057H18.5088L17.6989 12.6971H17.2868C17.2215 12.6971 17.1564 12.7665 17.1564 12.8359V13.9057H15.9932V9.95217C15.9932 9.80373 16.1342 9.6776 16.2903 9.6776H17.9339C18.9911 9.6776 19.6725 10.2299 19.6725 11.1665ZM17.8488 11.7778C18.2634 11.7778 18.516 11.5632 18.516 11.2301V11.2161C18.516 10.8548 18.2634 10.6622 17.8438 10.6622H17.3377C17.2394 10.6622 17.1567 10.7477 17.1567 10.8474V11.7778H17.8488Z", fill: "#00A19C" }),
        React.createElement("path", { d: "M27.7766 9.94887V11.9139L26.0552 9.6776H25.2684C25.1033 9.6776 24.9701 9.81105 24.9701 9.97522V13.9057H26.1193V11.5866L27.9117 13.9057H28.9269V9.6776H28.0764C27.9179 9.6776 27.7766 9.79541 27.7766 9.94887Z", fill: "#00A19C" }),
        React.createElement("path", { d: "M12.0528 10.7033V9.96419C12.0528 9.81978 12.1914 9.6776 12.3495 9.6776H15.7305V10.7033H14.6183C14.5313 10.7033 14.4727 10.7664 14.4727 10.8391V13.9057H13.3066V10.7033H12.0528Z", fill: "#00A19C" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6.4161 9.6776H4.9195C4.76107 9.6776 4.62109 9.80134 4.62109 9.95217V13.9057H5.78289V12.8359C5.78289 12.7705 5.85243 12.6971 5.91887 12.6971H6.35791C7.39637 12.6971 8.15961 12.1725 8.15961 11.1758V11.1665C8.15961 10.2299 7.47775 9.6776 6.4161 9.6776ZM6.99827 11.2301C6.99827 11.5632 6.74876 11.7778 6.33698 11.7778H5.7831V10.6622H6.32767C6.74876 10.6622 6.99827 10.8548 6.99827 11.2161V11.2301Z", fill: "#00A19C" }),
        React.createElement("path", { d: "M8.41027 13.9057V9.96657C8.40864 9.81105 8.54789 9.6776 8.70974 9.6776H11.781V10.673H9.6958C9.62373 10.673 9.56056 10.7591 9.56056 10.8338V11.3143H11.5196V12.2373H9.70437C9.631 12.2373 9.56056 12.3167 9.56056 12.3981V12.9057H11.781V13.9057H8.41027Z", fill: "#00A19C" }),
        React.createElement("path", { d: "M35.8225 11.2709C35.2377 11.1394 35.0941 11.0541 35.0941 10.8631V10.8488C35.0941 10.6977 35.2297 10.5765 35.5135 10.5765C35.7683 10.5765 36.0455 10.6456 36.3188 10.7781C36.3188 10.7781 36.3803 10.8085 36.4088 10.8204C36.4855 10.8581 36.5788 10.8878 36.6886 10.8639C36.7736 10.8474 36.8315 10.7747 36.9026 10.6843L37.2643 10.1684C36.814 9.79936 36.2589 9.60317 35.5423 9.60317C34.5383 9.60317 33.9193 10.1721 33.9193 10.9593V10.968C33.9193 11.8406 34.6108 12.0925 35.5037 12.3001C36.0704 12.4308 36.2067 12.527 36.2067 12.7038V12.7145C36.2067 12.8973 36.0379 13.0041 35.7293 13.0041C35.3611 13.0041 35.0111 12.9047 34.6822 12.7124L34.6625 12.7028C34.5703 12.6578 34.459 12.6033 34.319 12.6344C34.2588 12.6494 34.2098 12.6911 34.1592 12.7521L33.7199 13.2828C34.2376 13.7395 34.9455 13.9771 35.6866 13.9771C36.7068 13.9771 37.381 13.4632 37.381 12.6011V12.5907C37.381 11.7995 36.7789 11.4808 35.8225 11.2709Z", fill: "#00A19C" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M30.6115 13.3699L30.3984 13.9057H29.1765L30.8659 9.86745C30.9185 9.76734 30.9737 9.69324 31.1166 9.6776H32.0668L33.8386 13.9057H32.5934L32.2987 13.1833H30.8017C30.7144 13.1976 30.6416 13.2837 30.6115 13.3699ZM31.9648 12.2693L31.5007 11.0661L31.0249 12.2693H31.9648Z", fill: "#00A19C" }),
        React.createElement("path", { d: "M23.6231 16.9622V16.0239H24.5259V14.9707H25.6202V16.0239H26.5446V16.9622H25.6202V18.8936C25.6202 18.9604 25.618 19.0235 25.616 19.0824C25.6045 19.4124 25.5975 19.6109 26.0076 19.6109C26.1988 19.6109 26.4635 19.5743 26.6004 19.4735V20.4022C26.3725 20.5126 25.9891 20.5496 25.734 20.5496C24.5756 20.5496 24.5259 20.025 24.5259 18.9856V16.9622H23.6231Z", fill: "black" }),
        React.createElement("path", { d: "M6.49087 16.7966C6.86469 16.7966 7.13763 16.9528 7.34772 17.2472L8.07717 16.5848C7.72147 16.0972 7.10164 15.9137 6.51772 15.9137C5.63311 15.9137 4.75781 16.3549 4.75781 17.3393C4.75781 18.3091 5.54184 18.4819 6.20641 18.6284C6.69524 18.7361 7.11943 18.8296 7.11943 19.2156C7.11943 19.565 6.7092 19.7214 6.38998 19.7214C5.92523 19.7214 5.63311 19.4916 5.35062 19.1789L4.62109 19.8685C5.06781 20.3745 5.65131 20.5497 6.32616 20.5497C7.21999 20.5497 8.21413 20.1816 8.21413 19.1332C8.21413 18.1079 7.37537 17.9223 6.69749 17.7723C6.23781 17.6706 5.8521 17.5852 5.8521 17.2655C5.8521 16.9345 6.19883 16.7966 6.49087 16.7966Z", fill: "black" }),
        React.createElement("path", { d: "M9.69664 16.0239H8.65687V20.4393H9.75092V18.0293C9.75092 17.4401 10.0337 16.9068 10.7543 16.9068C11.2832 16.9068 11.5019 17.2655 11.5019 17.8084V20.4393H12.5966V18.0473C12.5966 17.4774 12.8512 16.9068 13.536 16.9068C14.1831 16.9068 14.3472 17.3853 14.3472 17.9372V20.4393H15.4415V17.8084C15.4415 16.7506 15.0313 15.9137 13.8639 15.9137C13.2072 15.9137 12.7421 16.171 12.4232 16.7319C12.1949 16.1804 11.7298 15.9137 11.0549 15.9137C10.3255 15.9137 9.90633 16.2998 9.71444 16.7139H9.69664V16.0239Z", fill: "black" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M18.777 19.8319H18.7502C18.4403 20.3284 17.9292 20.5497 17.3456 20.5497C16.5341 20.5497 15.7498 20.0989 15.7498 19.2246C15.7498 17.7897 17.4095 17.6889 18.5041 17.6889H18.777V17.5691C18.777 17.0266 18.3579 16.7412 17.7746 16.7412C17.3184 16.7412 16.899 16.9252 16.6069 17.2011L16.0323 16.6219C16.5159 16.125 17.1997 15.9137 17.8928 15.9137C19.7622 15.9137 19.7622 17.2751 19.7622 17.8999V20.4393H18.777V19.8319ZM18.7128 18.4611H18.4852C17.8834 18.4611 16.8434 18.5079 16.8434 19.1425C16.8434 19.5465 17.2542 19.7214 17.6096 19.7214C18.3575 19.7214 18.7128 19.3261 18.7128 18.7094V18.4611Z", fill: "black" }),
        React.createElement("path", { d: "M21.4313 16.0239H20.337V20.4393H21.4313V18.0751C21.4313 17.8361 21.5774 16.9622 22.6076 16.9622C22.7809 16.9622 22.954 16.9988 23.137 17.0449V15.9778C23.0092 15.9411 22.8813 15.9137 22.7446 15.9137C22.17 15.9137 21.6867 16.2167 21.4498 16.7233H21.4313V16.0239Z", fill: "black" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M28.6015 19.1591C29.7663 19.1591 30.3932 18.5268 30.3932 17.5714C30.3932 16.6231 29.7802 15.9771 28.6081 15.9771H27.3335C27.1764 15.9771 27.1354 16.0525 27.1354 16.1766V20.3C27.1354 20.4581 27.2105 20.4996 27.3335 20.4996H27.7493C27.8724 20.4996 27.9472 20.4581 27.9472 20.3V19.1591H28.6015ZM28.7242 16.7907C29.3553 16.7907 29.6303 17.1337 29.6303 17.5581C29.6303 17.9659 29.3553 18.3169 28.7242 18.3169H27.9475V16.7907H28.7242Z", fill: "black" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M34.2797 20.4973H33.6512C33.501 20.4973 33.4467 20.4558 33.3921 20.291L33.1153 19.4881H31.4469L31.1701 20.291C31.1161 20.4558 31.0613 20.4973 30.9117 20.4973H30.2825C30.1327 20.4973 30.1327 20.3871 30.1667 20.305L31.6785 16.1903C31.7331 16.0458 31.8079 15.9771 31.9577 15.9771H32.6117C32.7613 15.9771 32.8294 16.0458 32.8906 16.1903L34.3958 20.305C34.4299 20.3871 34.4299 20.4973 34.2797 20.4973ZM32.9206 18.72L32.2814 16.9985L31.6423 18.72H32.9206Z", fill: "black" }),
        React.createElement("path", { d: "M36.0448 18.5545L37.3648 16.179C37.3989 16.1032 37.385 15.9865 37.2553 15.9865H36.7769C36.6198 15.9865 36.5513 16.0072 36.463 16.1926L35.6055 17.7594L34.7215 16.1926C34.626 16.0072 34.5575 15.9865 34.4007 15.9865H33.874C33.7441 15.9865 33.7308 16.1032 33.7645 16.179L35.139 18.5545V20.3157C35.139 20.4736 35.2075 20.5149 35.3369 20.5149H35.8473C35.97 20.5149 36.0448 20.4736 36.0448 20.3157V18.5545Z", fill: "black" })));
});

const SparklesIcon = React.forwardRef(function SparklesIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M5 2.25C5.41421 2.25 5.75 2.58579 5.75 3V4.25H7C7.41421 4.25 7.75 4.58579 7.75 5C7.75 5.41421 7.41421 5.75 7 5.75H5.75V7C5.75 7.41421 5.41421 7.75 5 7.75C4.58579 7.75 4.25 7.41421 4.25 7V5.75H3C2.58579 5.75 2.25 5.41421 2.25 5C2.25 4.58579 2.58579 4.25 3 4.25H4.25V3C4.25 2.58579 4.58579 2.25 5 2.25ZM13 2.25C13.3228 2.25 13.6094 2.45656 13.7115 2.7628L15.8844 9.2804L21.2634 11.2978C21.5561 11.4075 21.75 11.6874 21.75 12C21.75 12.3126 21.5561 12.5925 21.2634 12.7022L15.8844 14.7196L13.7115 21.2372C13.6094 21.5434 13.3228 21.75 13 21.75C12.6772 21.75 12.3906 21.5434 12.2885 21.2372L10.1156 14.7196L4.73663 12.7022C4.44392 12.5925 4.25 12.3126 4.25 12C4.25 11.6874 4.44392 11.4075 4.73663 11.2978L10.1156 9.2804L12.2885 2.7628C12.3906 2.45656 12.6772 2.25 13 2.25ZM13 5.3714L11.4255 10.0942C11.3539 10.3089 11.1893 10.4798 10.9774 10.5592L7.13578 12L10.9774 13.4408C11.1893 13.5202 11.3539 13.6911 11.4255 13.9058L13 18.6286L14.5745 13.9058C14.6461 13.6911 14.8107 13.5202 15.0226 13.4408L18.8642 12L15.0226 10.5592C14.8107 10.4798 14.6461 10.3089 14.5745 10.0942L13 5.3714ZM6 16.25C6.41421 16.25 6.75 16.5858 6.75 17V18.25H8C8.41421 18.25 8.75 18.5858 8.75 19C8.75 19.4142 8.41421 19.75 8 19.75H6.75V21C6.75 21.4142 6.41421 21.75 6 21.75C5.58579 21.75 5.25 21.4142 5.25 21V19.75H4C3.58579 19.75 3.25 19.4142 3.25 19C3.25 18.5858 3.58579 18.25 4 18.25H5.25V17C5.25 16.5858 5.58579 16.25 6 16.25Z", fill: "currentColor" })));
});

const SpinIcon = React.forwardRef(function SpinIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 24 24", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }),
        React.createElement("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })));
});

const StarIcon = React.forwardRef(function StarIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: "2", d: "M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" })));
});

const SyncIcon = React.forwardRef(function SyncIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "48", height: "48", viewBox: "0 0 48 48", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M29.8691 11.2897C27.3553 10.1289 24.5536 9.73956 21.8185 10.171C19.0834 10.6025 16.5376 11.8353 14.5032 13.7136C12.4687 15.5919 11.0369 18.0313 10.3888 20.7233C10.1303 21.7972 9.05017 22.4582 7.97627 22.1996C6.90239 21.9411 6.24141 20.861 6.49993 19.7871C7.33317 16.3259 9.17406 13.1895 11.7898 10.7746C14.4055 8.35964 17.6787 6.77459 21.1952 6.21987C24.7118 5.66515 28.3139 6.16567 31.546 7.65814C34.0642 8.82095 36.2697 10.5452 38 12.6863V12C38 10.8954 38.8954 10 40 10C41.1046 10 42 10.8954 42 12V18C42 19.1046 41.1046 20 40 20H33C31.8954 20 31 19.1046 31 18C31 16.8954 31.8954 16 33 16H35.4891C34.0704 13.9626 32.1321 12.3346 29.8691 11.2897ZM18.1309 36.7103C20.6447 37.8712 23.4464 38.2605 26.1815 37.829C28.9166 37.3976 31.4624 36.1647 33.4968 34.2864C35.5313 32.4082 36.9631 29.9688 37.6112 27.2767C37.8697 26.2028 38.9498 25.5419 40.0237 25.8004C41.0976 26.0589 41.7586 27.1391 41.5001 28.2129C40.6668 31.6741 38.8259 34.8105 36.2102 37.2254C33.5945 39.6404 30.3213 41.2254 26.8048 41.7801C23.2882 42.3349 19.6861 41.8343 16.454 40.3419C13.9358 39.1791 11.7303 37.4548 10 35.3137V36C10 37.1046 9.10457 38 8 38C6.89543 38 6 37.1046 6 36V30C6 28.8954 6.89543 28 8 28H15C16.1046 28 17 28.8954 17 30C17 31.1046 16.1046 32 15 32H12.5109C13.9296 34.0374 15.8679 35.6654 18.1309 36.7103Z", fill: "currentColor" })));
});

const ToggleIcon = withDefaultSvgProps(function ToggleIcon(_a) {
    var { icon } = _a, props = __rest(_a, ["icon"]);
    return (React.createElement("svg", Object.assign({ fill: "none", viewBox: "0 0 24 24", width: 24, height: 24, stroke: "currentColor" }, props),
        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M18 12H6" }),
        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, className: cx('transition-transform transform duration-150 origin-bottom', icon === 'plus' ? 'scale-y-100' : 'scale-y-0'), d: "M12 6V18" })));
});

const TopupIcon = React.forwardRef(function TopupIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3 6C3 5.27245 3.55055 4.75 4.14873 4.75H19.1013C19.6995 4.75 20.25 5.27245 20.25 6V6.5C20.25 6.91421 20.5858 7.25 21 7.25C21.4142 7.25 21.75 6.91421 21.75 6.5V6C21.75 4.51841 20.6004 3.25 19.1013 3.25H4.14873C2.64964 3.25 1.5 4.51841 1.5 6V11.25C1.5 11.6642 1.83579 12 2.25 12C2.66421 12 3 11.6642 3 11.25V6ZM21.75 17.5C21.75 17.0858 21.4142 16.75 21 16.75C20.5858 16.75 20.25 17.0858 20.25 17.5V18C20.25 18.7276 19.6995 19.25 19.1013 19.25H9.5C9.08579 19.25 8.75 19.5858 8.75 20C8.75 20.4142 9.08579 20.75 9.5 20.75H19.1013C20.6004 20.75 21.75 19.4816 21.75 18V17.5ZM4 13.75C4.41421 13.75 4.75 14.0858 4.75 14.5V16.75H7C7.41421 16.75 7.75 17.0858 7.75 17.5C7.75 17.9142 7.41421 18.25 7 18.25H4.75V20.5C4.75 20.9142 4.41421 21.25 4 21.25C3.58579 21.25 3.25 20.9142 3.25 20.5V18.25H1C0.585786 18.25 0.25 17.9142 0.25 17.5C0.25 17.0858 0.585786 16.75 1 16.75H3.25V14.5C3.25 14.0858 3.58579 13.75 4 13.75ZM12 12C12 9.92893 13.6789 8.25 15.75 8.25H20.75C21.7165 8.25 22.5 9.0335 22.5 10V14C22.5 14.9665 21.7165 15.75 20.75 15.75H15.75C13.6789 15.75 12 14.0711 12 12ZM15.75 9.75C14.5074 9.75 13.5 10.7574 13.5 12C13.5 13.2426 14.5074 14.25 15.75 14.25H20.75C20.8881 14.25 21 14.1381 21 14V10C21 9.86193 20.8881 9.75 20.75 9.75H15.75ZM15.25 11.25C14.8358 11.25 14.5 11.5858 14.5 12C14.5 12.4142 14.8358 12.75 15.25 12.75H16.25C16.6642 12.75 17 12.4142 17 12C17 11.5858 16.6642 11.25 16.25 11.25H15.25Z", fill: "currentColor" })));
});

const TransmissionIcon = React.forwardRef(function TransmissionIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: 24, height: 24 }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6.75 5C6.75 3.48122 5.51878 2.25 4 2.25C2.48122 2.25 1.25 3.48122 1.25 5C1.25 6.25878 2.09575 7.32002 3.25 7.64648V12V16.3535C2.09575 16.68 1.25 17.7412 1.25 19C1.25 20.5188 2.48122 21.75 4 21.75C5.51878 21.75 6.75 20.5188 6.75 19C6.75 17.7412 5.90425 16.68 4.75 16.3535V12.75H11.25V16.3535C10.0957 16.68 9.25 17.7412 9.25 19C9.25 20.5188 10.4812 21.75 12 21.75C13.5188 21.75 14.75 20.5188 14.75 19C14.75 17.7412 13.9043 16.68 12.75 16.3535V12.75H17L17.1992 12.7448C19.1776 12.6413 20.75 11.0043 20.75 9V7.64648C21.9043 7.32002 22.75 6.25878 22.75 5C22.75 3.48122 21.5188 2.25 20 2.25C18.4812 2.25 17.25 3.48122 17.25 5C17.25 6.25878 18.0957 7.32002 19.25 7.64648V9L19.2448 9.15405C19.1656 10.3248 18.1909 11.25 17 11.25H12.75V7.64648C13.9043 7.32002 14.75 6.25878 14.75 5C14.75 3.48122 13.5188 2.25 12 2.25C10.4812 2.25 9.25 3.48122 9.25 5C9.25 6.25878 10.0957 7.32002 11.25 7.64648V11.25H4.75V7.64648C5.90425 7.32002 6.75 6.25878 6.75 5ZM12 6.25C12.6904 6.25 13.25 5.69036 13.25 5C13.25 4.30964 12.6904 3.75 12 3.75C11.3096 3.75 10.75 4.30964 10.75 5C10.75 5.69036 11.3096 6.25 12 6.25ZM20 6.25C20.6904 6.25 21.25 5.69036 21.25 5C21.25 4.30964 20.6904 3.75 20 3.75C19.3096 3.75 18.75 4.30964 18.75 5C18.75 5.69036 19.3096 6.25 20 6.25ZM12 17.75C11.3096 17.75 10.75 18.3096 10.75 19C10.75 19.6904 11.3096 20.25 12 20.25C12.6904 20.25 13.25 19.6904 13.25 19C13.25 18.3096 12.6904 17.75 12 17.75ZM4 17.75C3.30964 17.75 2.75 18.3096 2.75 19C2.75 19.6904 3.30964 20.25 4 20.25C4.69036 20.25 5.25 19.6904 5.25 19C5.25 18.3096 4.69036 17.75 4 17.75ZM4 6.25C4.69036 6.25 5.25 5.69036 5.25 5C5.25 4.30964 4.69036 3.75 4 3.75C3.30964 3.75 2.75 4.30964 2.75 5C2.75 5.69036 3.30964 6.25 4 6.25Z", fill: "currentColor" })));
});

const TrashIcon = React.forwardRef(function TrashIcon(providedProps, ref) {
    const _a = useDefaultSvgProps(providedProps), { width = 24 } = _a, props = __rest(_a, ["width"]);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", fill: "none", width: width }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 2.25C15.4625 2.25 16.6584 3.3917 16.745 4.83248L16.75 5V5.25H19H21C21.4142 5.25 21.75 5.58579 21.75 6C21.75 6.3797 21.4678 6.69349 21.1018 6.74315L21 6.75H19.75V19C19.75 20.4625 18.6083 21.6584 17.1675 21.745L17 21.75H7C5.53747 21.75 4.3416 20.6083 4.25502 19.1675L4.25 19V6.75H3C2.58579 6.75 2.25 6.41421 2.25 6C2.25 5.6203 2.53215 5.30651 2.89823 5.25685L3 5.25H5H7.25V5C7.25 3.53747 8.3917 2.3416 9.83248 2.25502L10 2.25H14ZM8 6.75H5.75V19C5.75 19.6472 6.24187 20.1795 6.87219 20.2435L7 20.25H17C17.6472 20.25 18.1795 19.7581 18.2435 19.1278L18.25 19V6.75H16H8ZM15.25 5.25H8.75V5L8.75645 4.87219C8.82047 4.24187 9.35279 3.75 10 3.75H14L14.1278 3.75645C14.7581 3.82047 15.25 4.35279 15.25 5V5.25ZM10.7432 9.89823C10.6935 9.53215 10.3797 9.25 10 9.25C9.58579 9.25 9.25 9.58579 9.25 10V17L9.25685 17.1018C9.30651 17.4678 9.6203 17.75 10 17.75C10.4142 17.75 10.75 17.4142 10.75 17V10L10.7432 9.89823ZM14 9.25C14.3797 9.25 14.6935 9.53215 14.7432 9.89823L14.75 10V17C14.75 17.4142 14.4142 17.75 14 17.75C13.6203 17.75 13.3065 17.4678 13.2568 17.1018L13.25 17V10C13.25 9.58579 13.5858 9.25 14 9.25Z", fill: "currentColor" })));
});

const UploadIcon = React.forwardRef(function UploadIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12.5303 3.46967L12.4462 3.39705C12.1526 3.1792 11.7359 3.2034 11.4697 3.46967L6.46967 8.46967L6.39705 8.55379C6.1792 8.8474 6.2034 9.26406 6.46967 9.53033L6.55379 9.60295C6.8474 9.8208 7.26406 9.7966 7.53033 9.53033L11.25 5.811V15.5L11.2568 15.6018C11.3065 15.9678 11.6203 16.25 12 16.25C12.4142 16.25 12.75 15.9142 12.75 15.5V5.811L16.4697 9.53033L16.5538 9.60295C16.8474 9.8208 17.2641 9.7966 17.5303 9.53033C17.8232 9.23744 17.8232 8.76256 17.5303 8.46967L12.5303 3.46967ZM21.7432 13.8982C21.6935 13.5322 21.3797 13.25 21 13.25C20.5858 13.25 20.25 13.5858 20.25 14V18L20.2435 18.1278C20.1795 18.7581 19.6472 19.25 19 19.25H5L4.87295 19.2435C4.24589 19.179 3.75 18.6429 3.75 18V14L3.74315 13.8982C3.69349 13.5322 3.3797 13.25 3 13.25C2.58579 13.25 2.25 13.5858 2.25 14V18L2.25504 18.1671C2.34192 19.6041 3.54187 20.75 5 20.75H19L19.1675 20.745C20.6083 20.6584 21.75 19.4625 21.75 18V14L21.7432 13.8982Z", fill: "currentColor" })));
});

const UserCircleIcon = React.forwardRef(function UserCircleIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M12 1.25C6.06294 1.25 1.25 6.06294 1.25 12C1.25 17.9371 6.06294 22.75 12 22.75C17.9371 22.75 22.75 17.9371 22.75 12C22.75 6.06294 17.9371 1.25 12 1.25ZM12 2.75C17.1086 2.75 21.25 6.89137 21.25 12C21.25 14.2316 20.4598 16.2786 19.1439 17.8765C18.8537 16.1513 17.7979 15.0202 15.491 14.2052L15.279 14.132L15.3446 14.0699C16.3333 13.0706 16.75 11.8962 16.75 10C16.75 7.37665 14.6234 5.25 12 5.25C9.37665 5.25 7.25 7.37665 7.25 10L7.25315 10.2745C7.29519 12.0721 7.76057 13.232 8.76379 14.1935L8.81 14.236L8.50439 14.3513C6.25297 15.2247 5.18369 16.3275 4.87103 17.8945C3.5462 16.294 2.75 14.24 2.75 12C2.75 6.89137 6.89137 2.75 12 2.75ZM6.23318 19.2328C7.81455 20.4953 9.81914 21.25 12 21.25C14.1809 21.25 16.1854 20.4953 17.7668 19.2328C17.7429 19.1595 17.73 19.0813 17.73 19C17.73 16.937 16.9025 16.0102 13.4953 15.1842C12.8609 15.0304 12.7122 14.1957 13.2545 13.8323C14.7636 12.8208 15.25 12.0238 15.25 10C15.25 8.20507 13.7949 6.75 12 6.75C10.2051 6.75 8.75 8.20507 8.75 10C8.75 12.0596 9.28116 12.9126 10.8182 13.879C11.3694 14.2256 11.2474 15.0618 10.6201 15.2365L10.0517 15.4018C7.06053 16.3126 6.27 17.2664 6.27 19C6.27 19.0812 6.25708 19.1595 6.23318 19.2328Z", fill: "currentColor" })));
});

const UserIcon = React.forwardRef(function UserIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M11.9966 2.75C15.0829 2.75 17.5998 5.14262 17.5998 8.11357C17.5998 9.86276 16.8917 11.9968 15.831 13.4862L15.626 13.759L15.7429 13.7815C20.2428 14.6594 21.7716 16.1972 21.7488 18.8115L21.745 18.9873L21.7244 19.3883L21.6726 20.1658C21.6662 20.2924 21.6637 20.3916 21.6637 20.5C21.6637 20.9142 21.328 21.25 20.9137 21.25C20.4995 21.25 20.1637 20.9142 20.1637 20.5L20.1735 20.1075L20.2427 19.0197L20.2459 18.9361C20.3189 16.7999 19.106 15.7354 13.9196 14.9977C13.2303 14.8997 13.035 13.9968 13.6223 13.6227C14.9306 12.7891 16.0998 10.0844 16.0998 8.11357C16.0998 5.98851 14.271 4.25 11.9966 4.25C9.72229 4.25 7.89351 5.98851 7.89351 8.11357C7.89351 10.1243 9.11329 12.8723 10.4632 13.6791C11.0631 14.0377 10.8896 14.9497 10.1999 15.0629L9.54252 15.1767C5.40829 15.931 3.95656 16.977 3.77533 18.4416C3.74456 18.6903 3.74164 18.9174 3.76326 19.2952L3.81501 20.0538C3.8257 20.2305 3.82954 20.353 3.82954 20.5C3.82954 20.9142 3.49375 21.25 3.07954 21.25C2.66533 21.25 2.32954 20.9142 2.32954 20.5L2.32529 20.2919C2.32224 20.2175 2.31735 20.1334 2.31 20.0248L2.29181 19.7707C2.23919 19.0595 2.23114 18.7064 2.28669 18.2574C2.53809 16.2256 4.17314 14.8625 7.95491 13.9757L8.46199 13.862L8.31523 13.6769C7.22094 12.2454 6.46476 10.1443 6.39828 8.3654L6.39351 8.11357C6.39351 5.14262 8.91037 2.75 11.9966 2.75Z", fill: "currentColor" })));
});

const VehicleIcon = React.forwardRef(function VehicleIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M8 11.75C8.41421 11.75 8.75 12.0858 8.75 12.5C8.75 12.8797 8.46785 13.1935 8.10177 13.2432L8 13.25H5C4.58579 13.25 4.25 12.9142 4.25 12.5C4.25 12.1203 4.53215 11.8065 4.89823 11.7568L5 11.75H8Z", fill: "currentColor" }),
        React.createElement("path", { d: "M19.75 12.5C19.75 12.0858 19.4142 11.75 19 11.75H16L15.8982 11.7568C15.5322 11.8065 15.25 12.1203 15.25 12.5C15.25 12.9142 15.5858 13.25 16 13.25H19L19.1018 13.2432C19.4678 13.1935 19.75 12.8797 19.75 12.5Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M6.48806 2.25H17.5119C18.7264 2.25 19.797 3.04659 20.146 4.20979L22.7184 12.7845L22.75 13V19C22.75 20.5188 21.5188 21.75 20 21.75H19C17.4812 21.75 16.25 20.5188 16.25 19V18.75H7.75V19C7.75 20.4625 6.6083 21.6584 5.16752 21.745L5 21.75H4C2.48122 21.75 1.25 20.5188 1.25 19V13L1.28163 12.7845L3.85404 4.20979C4.203 3.04659 5.27364 2.25 6.48806 2.25ZM17.638 3.75634L17.5119 3.75H6.48806C5.93605 3.75 5.4494 4.11209 5.29078 4.64082L2.75 13.108V19C2.75 19.6472 3.24187 20.1795 3.87219 20.2435L4 20.25H5C5.69036 20.25 6.25 19.6904 6.25 19V18C6.25 17.5858 6.58579 17.25 7 17.25H17C17.4142 17.25 17.75 17.5858 17.75 18V19C17.75 19.6904 18.3096 20.25 19 20.25H20C20.6904 20.25 21.25 19.6904 21.25 19V13.112L18.7092 4.64082C18.5628 4.15276 18.1369 3.80669 17.638 3.75634Z", fill: "currentColor" })));
});

const VerifiedIcon = React.forwardRef(function VerifiedIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M8.0302 12.036C7.73731 11.7431 7.26243 11.7431 6.96954 12.036C6.67665 12.3289 6.67665 12.8038 6.96954 13.0966L9.85389 15.981C10.1468 16.2739 10.6217 16.2739 10.9146 15.981L17.0627 9.83281C17.3556 9.53992 17.3556 9.06505 17.0627 8.77215C16.7698 8.47926 16.295 8.47926 16.0021 8.77215L10.3842 14.39L8.0302 12.036Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M13.2792 2.27109C12.5873 1.53008 11.4126 1.53008 10.7208 2.27109L9.41918 3.66529C9.36042 3.72823 9.27341 3.7565 9.18888 3.74012L7.31635 3.37727C6.3211 3.18441 5.37073 3.8749 5.2466 4.88103L5.01305 6.77404C5.0025 6.8595 4.94873 6.93351 4.87072 6.96994L3.14252 7.77703C2.224 8.206 1.86099 9.32324 2.35195 10.2102L3.27569 11.8789C3.31739 11.9543 3.31739 12.0457 3.27569 12.1211L2.35195 13.7898C1.86099 14.6768 2.224 15.794 3.14252 16.223L4.87072 17.0301C4.94873 17.0665 5.0025 17.1405 5.01305 17.226L5.2466 19.119C5.37073 20.1251 6.3211 20.8156 7.31635 20.6227L9.18888 20.2599C9.27341 20.2435 9.36042 20.2718 9.41918 20.3347L10.7208 21.7289C11.4126 22.4699 12.5873 22.4699 13.2792 21.7289L14.5808 20.3347C14.6395 20.2718 14.7266 20.2435 14.8111 20.2599L16.6836 20.6227C17.6789 20.8156 18.6292 20.1251 18.7534 19.119L18.9869 17.226C18.9975 17.1405 19.0512 17.0665 19.1292 17.0301L20.8574 16.223C21.776 15.794 22.139 14.6768 21.648 13.7898L20.7243 12.1211C20.6826 12.0457 20.6826 11.9543 20.7243 11.8789L21.648 10.2102C22.139 9.32324 21.776 8.206 20.8574 7.77703L19.1292 6.96994C19.0512 6.93351 18.9975 6.8595 18.9869 6.77404L18.7534 4.88103C18.6292 3.8749 17.6789 3.18441 16.6836 3.37727L14.8111 3.74012C14.7266 3.7565 14.6395 3.72823 14.5808 3.66529L13.2792 2.27109ZM11.8172 3.29473C11.9161 3.18887 12.0839 3.18887 12.1827 3.29473L13.4843 4.68893C13.8957 5.12949 14.5047 5.32739 15.0964 5.21273L16.969 4.84987C17.1112 4.82232 17.2469 4.92096 17.2647 5.0647L17.4982 6.95771C17.572 7.55589 17.9484 8.074 18.4945 8.32904L20.2227 9.13613C20.3539 9.19741 20.4058 9.35701 20.3357 9.48372L19.4119 11.1525C19.12 11.6798 19.12 12.3202 19.4119 12.8475L20.3357 14.5163C20.4058 14.643 20.3539 14.8026 20.2227 14.8639L18.4945 15.671C17.9484 15.926 17.572 16.4441 17.4982 17.0423L17.2647 18.9353C17.2469 19.079 17.1112 19.1777 16.969 19.1501L15.0964 18.7873C14.5047 18.6726 13.8957 18.8705 13.4843 19.3111L12.1827 20.7053C12.0839 20.8111 11.9161 20.8111 11.8172 20.7053L10.5156 19.3111C10.1043 18.8705 9.49523 18.6726 8.90352 18.7873L7.03099 19.1501C6.88881 19.1777 6.75304 19.079 6.73531 18.9353L6.50176 17.0423C6.42796 16.4441 6.05153 15.926 5.50543 15.671L3.77724 14.8639C3.64602 14.8026 3.59416 14.643 3.6643 14.5163L4.58804 12.8475C4.87994 12.3202 4.87994 11.6798 4.58804 11.1525L3.6643 9.48372C3.59416 9.35701 3.64602 9.19741 3.77724 9.13613L5.50543 8.32904C6.05153 8.074 6.42796 7.55589 6.50176 6.95771L6.73531 5.0647C6.75304 4.92096 6.88881 4.82232 7.03099 4.84987L8.90352 5.21273C9.49523 5.32739 10.1043 5.12949 10.5156 4.68893L11.8172 3.29473Z", fill: "currentColor" })));
});

const VisaIcon = React.forwardRef(function VisaIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    const id = useId(providedProps.id);
    return (React.createElement("svg", Object.assign({ width: "42", height: "30", viewBox: "0 0 42 30", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M3.6 0H38.4C40.3882 0 42 1.87416 42 4.18605V25.814C42 28.1258 40.3882 30 38.4 30H3.6C1.61177 30 0 28.1258 0 25.814V4.18605C0 1.87416 1.61177 0 3.6 0Z", fill: "white" }),
        React.createElement("path", { d: "M3.6 0.5H38.4C40.044 0.5 41.5 2.07688 41.5 4.18605V25.814C41.5 27.9231 40.044 29.5 38.4 29.5H3.6C1.95602 29.5 0.5 27.9231 0.5 25.814V4.18605C0.5 2.07688 1.95602 0.5 3.6 0.5Z", stroke: "black", strokeOpacity: "0.08" }),
        React.createElement("g", { clipPath: `url(#${id}-clippath)` },
            React.createElement("path", { d: "M21.5776 13.042C21.56 14.5434 22.9166 15.3799 23.9386 15.8792C24.9886 16.3899 25.341 16.7177 25.337 17.1745C25.3291 17.874 24.499 18.1823 23.7229 18.1944C22.3674 18.2156 21.5806 17.8281 20.9535 17.536L20.4654 19.8202C21.0949 20.1091 22.258 20.3612 23.4639 20.374C26.2969 20.374 28.1494 18.9751 28.1584 16.8074C28.1702 14.0567 24.3545 13.9049 24.3806 12.6757C24.3893 12.3021 24.7451 11.9046 25.5242 11.8026C25.9103 11.7525 26.976 11.7134 28.1825 12.269L28.6566 10.0586C28.0073 9.82323 27.173 9.5966 26.1336 9.5966C23.4687 9.5966 21.5927 11.0136 21.5776 13.042ZM33.2122 9.7873C32.6944 9.7873 32.2578 10.0882 32.0643 10.5518L28.018 20.211H30.8491L31.4117 18.6535H34.8707L35.1966 20.211H37.6903L35.5135 9.7873H33.2122ZM33.6073 12.6038L34.4242 16.5176H32.1877L33.6073 12.6038ZM18.1471 9.7873L15.9166 20.211H18.6132L20.843 9.7873H18.1471ZM14.1576 9.7873L11.3511 16.8818L10.2158 10.8498C10.0829 10.1764 9.55656 9.7873 8.97215 9.7873H4.38375L4.32031 10.0895C5.26184 10.2944 6.33232 10.6246 6.98043 10.9767C7.37735 11.192 7.49053 11.3802 7.6209 11.8922L9.77091 20.211H12.6211L16.9898 9.7873H14.1576Z", fill: "#1434CB" })),
        React.createElement("defs", null,
            React.createElement("clipPath", { id: `${id}-clippath` },
                React.createElement("rect", { width: "33.4327", height: "10.67", fill: "white", transform: "translate(4.28906 9.65039)" })))));
});

const VoidSalesIcon = React.forwardRef(function VoidSalesIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ width: "24", height: "24", viewBox: "0 0 24 24", fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M14 1.25C14.4142 1.25 14.75 1.58579 14.75 2V7.25H20C20.4142 7.25 20.75 7.58579 20.75 8C20.75 8.41421 20.4142 8.75 20 8.75H14C13.5858 8.75 13.25 8.41421 13.25 8V2C13.25 1.58579 13.5858 1.25 14 1.25Z", fill: "currentColor" }),
        React.createElement("path", { d: "M5 2.75C4.86193 2.75 4.75 2.86193 4.75 3V20.5C4.75 20.9142 4.41421 21.25 4 21.25C3.58579 21.25 3.25 20.9142 3.25 20.5V3C3.25 2.0335 4.0335 1.25 5 1.25H14C14.1989 1.25 14.3897 1.32902 14.5303 1.46967L20.5303 7.46967C20.671 7.61032 20.75 7.80109 20.75 8V21C20.75 21.9665 19.9665 22.75 19 22.75H5.5C5.08579 22.75 4.75 22.4142 4.75 22C4.75 21.5858 5.08579 21.25 5.5 21.25H19C19.1381 21.25 19.25 21.1381 19.25 21V8.31066L13.6893 2.75H5Z", fill: "currentColor" }),
        React.createElement("path", { d: "M15.5303 11.4697C15.8232 11.7626 15.8232 12.2374 15.5303 12.5303L13.0607 15L15.5303 17.4697C15.8232 17.7626 15.8232 18.2374 15.5303 18.5303C15.2374 18.8232 14.7626 18.8232 14.4697 18.5303L12 16.0607L9.53033 18.5303C9.23744 18.8232 8.76256 18.8232 8.46967 18.5303C8.17678 18.2374 8.17678 17.7626 8.46967 17.4697L10.9393 15L8.46967 12.5303C8.17678 12.2374 8.17678 11.7626 8.46967 11.4697C8.76256 11.1768 9.23744 11.1768 9.53033 11.4697L12 13.9393L14.4697 11.4697C14.7626 11.1768 15.2374 11.1768 15.5303 11.4697Z", fill: "currentColor" })));
});

const VoucherIcon = React.forwardRef(function VoucherIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M8.5 7.25C8.8797 7.25 9.19349 7.53215 9.24315 7.89823L9.25 8V10.5C9.25 10.9142 8.91421 11.25 8.5 11.25C8.1203 11.25 7.80651 10.9678 7.75685 10.6018L7.75 10.5V8C7.75 7.58579 8.08579 7.25 8.5 7.25Z", fill: "currentColor" }),
        React.createElement("path", { d: "M9.24315 13.3982C9.19349 13.0322 8.8797 12.75 8.5 12.75C8.08579 12.75 7.75 13.0858 7.75 13.5V16L7.75685 16.1018C7.80651 16.4678 8.1203 16.75 8.5 16.75C8.91421 16.75 9.25 16.4142 9.25 16V13.5L9.24315 13.3982Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M4 4.25H20C21.5188 4.25 22.75 5.48122 22.75 7V10C22.75 10.4142 22.4142 10.75 22 10.75L21.8722 10.7565C21.2419 10.8205 20.75 11.3528 20.75 12C20.75 12.6904 21.3096 13.25 22 13.25L22.1018 13.2568C22.4678 13.3065 22.75 13.6203 22.75 14V17C22.75 18.5188 21.5188 19.75 20 19.75H4C2.48122 19.75 1.25 18.5188 1.25 17V14C1.25 13.5858 1.58579 13.25 2 13.25L2.12781 13.2435C2.75813 13.1795 3.25 12.6472 3.25 12C3.25 11.3096 2.69036 10.75 2 10.75L1.89823 10.7432C1.53215 10.6935 1.25 10.3797 1.25 10V7C1.25 5.48122 2.48122 4.25 4 4.25ZM20.1278 5.75645L20 5.75H4C3.30964 5.75 2.75 6.30964 2.75 7V9.354L2.80819 9.37068C3.88146 9.70018 4.67505 10.6688 4.74498 11.8325L4.75 12C4.75 13.2375 3.93257 14.2841 2.80819 14.6293L2.75 14.645V17C2.75 17.6472 3.24187 18.1795 3.87219 18.2435L4 18.25H20C20.6904 18.25 21.25 17.6904 21.25 17V14.645L21.1918 14.6293C20.1185 14.2998 19.3249 13.3312 19.255 12.1675L19.25 12C19.25 10.7625 20.0674 9.71587 21.1918 9.37068L21.25 9.354V7C21.25 6.35279 20.7581 5.82047 20.1278 5.75645Z", fill: "currentColor" })));
});

const WalletIcon = React.forwardRef(function WalletIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { d: "M21.745 5.83248C21.6584 4.3917 20.4625 3.25 19 3.25H4.25L4.08248 3.25502C2.6417 3.3416 1.5 4.53747 1.5 6V18L1.50502 18.1675C1.5916 19.6083 2.78747 20.75 4.25 20.75H19L19.1675 20.745C20.6083 20.6584 21.75 19.4625 21.75 18V17.5L21.7432 17.3982C21.6935 17.0322 21.3797 16.75 21 16.75C20.5858 16.75 20.25 17.0858 20.25 17.5V18L20.2435 18.1278C20.1795 18.7581 19.6472 19.25 19 19.25H4.25L4.12219 19.2435C3.49187 19.1795 3 18.6472 3 18V6L3.00645 5.87219C3.07047 5.24187 3.60279 4.75 4.25 4.75H19L19.1278 4.75645C19.7581 4.82047 20.25 5.35279 20.25 6V6.5L20.2568 6.60177C20.3065 6.96785 20.6203 7.25 21 7.25C21.4142 7.25 21.75 6.91421 21.75 6.5V6L21.745 5.83248Z", fill: "currentColor" }),
        React.createElement("path", { d: "M17.5 12C17.5 11.5858 17.1642 11.25 16.75 11.25H15.75L15.6482 11.2568C15.2822 11.3065 15 11.6203 15 12C15 12.4142 15.3358 12.75 15.75 12.75H16.75L16.8518 12.7432C17.2178 12.6935 17.5 12.3797 17.5 12Z", fill: "currentColor" }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M20.75 8.25H15.75C13.6789 8.25 12 9.92893 12 12C12 14.0711 13.6789 15.75 15.75 15.75H20.75C21.7165 15.75 22.5 14.9665 22.5 14V10C22.5 9.0335 21.7165 8.25 20.75 8.25ZM15.75 9.75H20.75C20.8881 9.75 21 9.86193 21 10V14C21 14.1381 20.8881 14.25 20.75 14.25H15.75C14.5074 14.25 13.5 13.2426 13.5 12C13.5 10.7574 14.5074 9.75 15.75 9.75Z", fill: "currentColor" }),
        React.createElement("path", { d: "M5.75 7.25C6.1297 7.25 6.44349 7.53215 6.49315 7.89823L6.5 8V16C6.5 16.4142 6.16421 16.75 5.75 16.75C5.3703 16.75 5.05651 16.4678 5.00685 16.1018L5 16V8C5 7.58579 5.33579 7.25 5.75 7.25Z", fill: "currentColor" })));
});

const WarningIcon = React.forwardRef(function WarningIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ viewBox: "0 0 24 24", width: 24, height: 24, fill: "none" }, props, { ref: ref }),
        React.createElement("path", { fillRule: "evenodd", clipRule: "evenodd", d: "M2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12ZM20.5 12C20.5 7.30558 16.6944 3.5 12 3.5C7.30558 3.5 3.5 7.30558 3.5 12C3.5 16.6944 7.30558 20.5 12 20.5C16.6944 20.5 20.5 16.6944 20.5 12ZM12 6.5C12.3797 6.5 12.6935 6.78215 12.7432 7.14823L12.75 7.25V13.25C12.75 13.6642 12.4142 14 12 14C11.6203 14 11.3065 13.7178 11.2568 13.3518L11.25 13.25V7.25C11.25 6.83579 11.5858 6.5 12 6.5ZM13 16.5C13 15.9477 12.5553 15.5 12.0066 15.5L11.8586 15.5091C11.3737 15.5753 11 15.9937 11 16.5C11 17.0523 11.4447 17.5 11.9934 17.5L12.1414 17.4909C12.6263 17.4247 13 17.0063 13 16.5Z", fill: "currentColor" })));
});

const ZoomInIcon = React.forwardRef(function ZoomInIcon(providedProps, ref) {
    const props = useDefaultSvgProps(providedProps);
    return (React.createElement("svg", Object.assign({ fill: "none", width: 24, height: 24, viewBox: "0 0 24 24", stroke: "currentColor" }, props, { ref: ref }),
        React.createElement("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" })));
});

let tempId = -Date.now();
function getId(number = false) {
    return number ? tempId++ : `setel-${tempId++}`;
}

// @overview: Type guards allows you to check the type of variable while allowing TypeScript to infer the type correctly
const isDefined = (value) => typeof value !== 'undefined';
const isNil = (value) => typeof value === 'undefined' || value === null;
const isPrimitive = (value) => /^(b|st|n)/.test(typeof value);
const isEmptyObject = (value) => !!value && Object.keys(value).length === 0 && value.constructor === Object;
function isEmptyValue(value) {
    return (isNil(value) ||
        ((isString(value) || Array.isArray(value)) && value.length === 0) ||
        isEmptyObject(value));
}

const useAjaxOperations = ({ operation: uploadOperation, onChange: providedOnChange, autoRemoveDelayWhenSuccess, }) => {
    const onChange = useStableCallback(providedOnChange);
    const cancelMap = useConstant(() => new Map());
    const runUploadMap = useConstant(() => new Map());
    const autoRemoveMap = useConstant(() => new Map());
    const [state, dispatch] = React.useReducer(ajaxOperationReducer, {
        items: [],
    });
    const stateValue = state;
    const cleanup = useStableCallback(function cleanup() {
        cancelMap.forEach((cancel) => cancel());
        cancelMap.clear();
        autoRemoveMap.forEach((timerId) => window.clearTimeout(timerId));
        autoRemoveMap.clear();
        runUploadMap.clear();
    });
    React.useEffect(() => cleanup, [cleanup]);
    const removeItem = ({ key, }) => {
        const cancel = cancelMap.get(key);
        if (cancel) {
            cancel();
            cancelMap.delete(key);
        }
        const autoRemoveTimer = autoRemoveMap.get(key);
        if (isDefined(autoRemoveTimer)) {
            window.clearTimeout(autoRemoveTimer);
            autoRemoveMap.delete(key);
        }
        runUploadMap.delete(key);
        dispatch({
            type: 'remove',
            key,
        });
    };
    const add = useStableCallback((input, addOptions = {}) => {
        const key = getId();
        const runUpload = () => uploadOperation(input, {
            onProgress: (progress) => dispatch({
                type: 'progress',
                progress,
                key,
            }),
            onComplete: (result) => {
                dispatch({
                    type: 'complete',
                    key,
                    result,
                });
                cancelMap.delete(key);
                runUploadMap.delete(key);
                if (isDefined(addOptions.autoRemoveDelayWhenSuccess)) {
                    const providedDelay = addOptions.autoRemoveDelayWhenSuccess;
                    if (providedDelay !== false) {
                        const autoRemoveTimerId = window.setTimeout(() => {
                            dispatch({
                                type: 'remove',
                                key,
                            });
                            autoRemoveMap.delete(key);
                        }, providedDelay);
                        autoRemoveMap.set(key, autoRemoveTimerId);
                    }
                }
                else if (isDefined(autoRemoveDelayWhenSuccess)) {
                    const autoRemoveTimerId = window.setTimeout(() => {
                        dispatch({
                            type: 'remove',
                            key,
                        });
                        autoRemoveMap.delete(key);
                    }, autoRemoveDelayWhenSuccess);
                    autoRemoveMap.set(key, autoRemoveTimerId);
                }
                if (isFunction(addOptions.onComplete)) {
                    addOptions.onComplete(result);
                }
            },
            onError: (errorMsg) => dispatch({
                type: 'error',
                errorMessage: errorMsg || undefined,
                key,
            }),
        });
        const { cancel } = runUpload();
        cancelMap.set(key, cancel);
        runUploadMap.set(key, runUpload);
        dispatch({
            type: 'add',
            input,
            key,
        });
    });
    const records = [];
    const items = [];
    stateValue.items.forEach((item) => {
        switch (item.status) {
            case 'success':
                {
                    records.push(item);
                    items.push(Object.assign(Object.assign({}, item), { onRemove: () => removeItem(item), onRetry: undefined }));
                }
                break;
            case 'error':
                {
                    items.push(Object.assign(Object.assign({}, item), { onRemove: () => removeItem(item), onRetry: () => {
                            const key = item.key;
                            dispatch({
                                type: 'retry',
                                key,
                            });
                            const runUpload = runUploadMap.get(key);
                            if (runUpload) {
                                const { cancel } = runUpload();
                                cancelMap.set(key, cancel);
                            }
                        } }));
                }
                break;
            case 'pending':
                {
                    items.push(Object.assign(Object.assign({}, item), { onRemove: () => removeItem(item), onRetry: undefined }));
                }
                break;
        }
    });
    const previousResults = usePrevious(records);
    React.useEffect(() => {
        if (previousResults &&
            !isSimilarArray(previousResults, records)) {
            onChange(records.map((r) => r.result), {
                records: records.map((r) => ({
                    input: r.input,
                    result: r.result,
                })),
            });
        }
    });
    return {
        add,
        results: records.map((r) => r.result),
        items,
        hasPendingRequest: stateValue.items.some((item) => item.status === 'pending'),
    };
};
const ajaxOperationReducer = (state, action) => {
    switch (action.type) {
        case 'add':
            return Object.assign(Object.assign({}, state), { items: state.items.concat({
                    input: action.input,
                    key: action.key,
                    status: 'pending',
                    progress: 0,
                }) });
        case 'progress':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.key === action.key && item.status === 'pending'
                    ? Object.assign(Object.assign({}, item), { progress: action.progress }) : item) });
        case 'complete':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.key === action.key && item.status === 'pending'
                    ? Object.assign(Object.assign({}, item), { progress: 100, status: 'success', result: action.result }) : item) });
        case 'error':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.key === action.key && item.status === 'pending'
                    ? Object.assign(Object.assign({}, item), { status: 'error', errorMessage: action.errorMessage }) : item) });
        case 'remove':
            return Object.assign(Object.assign({}, state), { items: state.items.filter((item) => item.key !== action.key) });
        case 'retry':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.key === action.key && item.status === 'error'
                    ? Object.assign(Object.assign({}, item), { status: 'pending', progress: 0, errorMessage: undefined }) : item) });
        default:
            throw new Error(`Unsupported action: ${JSON.stringify(action)}`);
    }
};

/**
 * A custom hook that converts a callback to a ref to avoid triggering re-renders when passed as a
 * prop or avoid re-executing effects when passed as a dependency
 */
function useCallbackRef(callback) {
    const callbackRef = React.useRef(callback);
    React.useEffect(() => {
        callbackRef.current = callback;
    });
    // https://github.com/facebook/react/issues/19240
    return React.useMemo(() => ((...args) => { var _a; return (_a = callbackRef.current) === null || _a === void 0 ? void 0 : _a.call(callbackRef, ...args); }), []);
}

function useControllableState({ prop, defaultProp, onChange = () => { }, }) {
    const [uncontrolledProp, setUncontrolledProp] = useUncontrolledState({
        defaultProp,
        onChange,
    });
    const isControlled = prop !== undefined;
    const value = isControlled ? prop : uncontrolledProp;
    const handleChange = useCallbackRef(onChange);
    const setValue = React.useCallback((nextValue) => {
        if (isControlled) {
            const setter = nextValue;
            const value = typeof nextValue === 'function' ? setter(prop) : nextValue;
            if (value !== prop)
                handleChange(value);
        }
        else {
            setUncontrolledProp(nextValue);
        }
    }, [isControlled, prop, setUncontrolledProp, handleChange]);
    return [value, setValue];
}
function useUncontrolledState({ defaultProp, onChange, }) {
    const uncontrolledState = React.useState(defaultProp);
    const [value] = uncontrolledState;
    const prevValueRef = React.useRef(value);
    const handleChange = useCallbackRef(onChange);
    React.useEffect(() => {
        if (prevValueRef.current !== value) {
            handleChange(value);
            prevValueRef.current = value;
        }
    }, [value, prevValueRef, handleChange]);
    return uncontrolledState;
}

/**
 * `useDebounce` allows you to debounce the update of a variable/state value.
 *
 * @param value value that you wish to debounce, e.g. the input value of a search field
 * @param wait wait time before the debounced value is updated
 */
const useDebounce = (value, wait = 500) => {
    const [dValue, setDValue] = React.useState(value);
    React.useEffect(() => {
        const timerId = window.setTimeout(() => setDValue(value), wait);
        return () => window.clearTimeout(timerId);
    }, [value, wait]);
    return dValue;
};

const useDelayedCallback = (cb, delay = 3000) => {
    const latestCallback = useCallbackRef(cb);
    const latestTimerId = React.useRef();
    React.useEffect(() => {
        return function cleanup() {
            if (latestTimerId.current) {
                clearTimeout(latestTimerId.current);
            }
        };
    }, []);
    return React.useCallback(function cb(...args) {
        latestTimerId.current = setTimeout(() => {
            console.log('calling after delay');
            latestCallback(...args);
        }, delay);
    }, []);
};

const useDescendantState = (initialState) => {
    const stateRef = React.useRef(initialState);
    const hasChangeRef = React.useRef(false);
    const forceUpdate = useForceUpdate();
    React.useEffect(() => {
        if (hasChangeRef.current) {
            forceUpdate();
            hasChangeRef.current = false;
        }
    });
    const setState = React.useCallback((value) => {
        if (isFunction(value)) {
            const nextValue = value(stateRef.current);
            if (nextValue !== stateRef.current) {
                stateRef.current = nextValue;
                hasChangeRef.current = true;
            }
        }
        else {
            if (value !== stateRef.current) {
                stateRef.current = value;
                hasChangeRef.current = true;
            }
        }
    }, []);
    return [stateRef.current, setState];
};
const createDescendantContext = (name, defaultValue) => {
    const Context = createNamedContext(name, [defaultValue, noop]);
    const useDescendantValue = (value) => {
        const [val, setValue] = React.useContext(Context);
        setValue(value);
        return val;
    };
    return {
        Provider: Context.Provider,
        useDescendantValue,
    };
};

function pick(oriObject, keysToPick) {
    const result = {};
    keysToPick.forEach((key) => {
        const value = oriObject[key];
        if (isDefined(value)) {
            result[key] = value;
        }
    });
    return result;
}
function omit(oriObject, keysToOmit) {
    const result = Object.assign({}, oriObject);
    keysToOmit.forEach((key) => delete result[key]);
    return result;
}
/**
 * Combination of `pick` and `omit` operation.
 * @param oriObject
 * @param keys keys to included in the `picked` property and excluded from the `omitted` property in the result.
 */
function pickAndOmit(oriObject, keys) {
    const pick = {};
    const omit = Object.assign({}, oriObject);
    keys.forEach((key) => {
        const value = oriObject[key];
        if (isDefined(value)) {
            pick[key] = value;
        }
        delete omit[key];
    });
    return {
        picked: pick,
        omitted: omit,
    };
}

const useFileUploads = ({ uploadOperation, validateBeforeUpload, onChange, maxCount, removeWhenComplete, }) => {
    const cancelMap = useConstant(() => new WeakMap());
    const runUploadMap = useConstant(() => new WeakMap());
    const [state, dispatch] = React.useReducer(fileUploadReducer, {
        items: [],
    });
    const stateValue = state;
    const cancelInflightRequests = useStableCallback(() => {
        state.items.forEach((item) => {
            const cancel = cancelMap.get(item.file);
            if (cancel) {
                cancel();
            }
        });
    });
    React.useEffect(() => cancelInflightRequests, [cancelInflightRequests]);
    const removeItemByFile = (file) => {
        const cancel = cancelMap.get(file);
        if (cancel) {
            cancel();
        }
        cancelMap.delete(file);
        runUploadMap.delete(file);
        dispatch({
            type: 'remove',
            file,
        });
    };
    const removeItem = (item) => {
        removeItemByFile(item.file);
    };
    const addFile = useStableCallback((file) => {
        if (maxCount &&
            maxCount === stateValue.items.length &&
            stateValue.items.length > 0) {
            removeItem(stateValue.items[0]);
        }
        const validationResult = validateBeforeUpload && validateBeforeUpload(file);
        if (validationResult) {
            dispatch({
                type: 'addFile',
                file,
                key: getId(),
                errorMessage: validationResult,
            });
            return;
        }
        if (uploadOperation === 'local') {
            dispatch({
                type: 'addLocalFile',
                file,
                key: getId(),
            });
            return;
        }
        const runUpload = () => uploadOperation(file, {
            onProgress: (uploadProgress) => dispatch({
                type: 'fileUploadProgress',
                uploadProgress,
                file,
            }),
            onComplete: (result) => {
                if (removeWhenComplete) {
                    removeItemByFile(file);
                    if (onChange) {
                        onChange([]);
                    }
                }
                else {
                    dispatch({
                        type: 'completeUpload',
                        file,
                        result,
                    });
                    cancelMap.delete(file);
                }
            },
            onError: (errorMsg) => dispatch({
                type: 'error',
                errorMessage: errorMsg || undefined,
                file,
            }),
        });
        const { cancel } = runUpload();
        cancelMap.set(file, cancel);
        runUploadMap.set(file, runUpload);
        dispatch({
            type: 'addFile',
            file,
            key: getId(),
        });
    });
    const uploadResults = [];
    const items = [];
    stateValue.items.forEach((item) => {
        switch (item.uploadStatus) {
            case 'completed':
                {
                    const { picked: { result }, omitted: itemResult, } = pickAndOmit(item, ['result']);
                    if (result) {
                        uploadResults.push(result);
                    }
                    items.push(Object.assign(Object.assign({}, itemResult), { onRemove: () => removeItem(item), onRetry: undefined }));
                }
                break;
            case 'error':
                {
                    const { picked: { noRetry }, omitted: itemResult, } = pickAndOmit(item, ['noRetry']);
                    items.push(Object.assign(Object.assign({}, itemResult), { onRemove: () => removeItem(item), onRetry: noRetry
                            ? undefined
                            : () => {
                                const file = item.file;
                                dispatch({
                                    type: 'retry',
                                    file,
                                });
                                const runUpload = runUploadMap.get(file);
                                if (runUpload) {
                                    const { cancel } = runUpload();
                                    cancelMap.set(file, cancel);
                                }
                            } }));
                }
                break;
            case 'uploading':
                {
                    items.push(Object.assign(Object.assign({}, item), { onRemove: () => removeItem(item), onRetry: undefined }));
                }
                break;
        }
    });
    const results = uploadOperation === 'local'
        ? items
            .filter((item) => item.uploadStatus === 'completed')
            .map((item) => item.file)
        : uploadResults;
    const previousResults = usePrevious(results);
    React.useEffect(() => {
        if (onChange &&
            previousResults &&
            !isSimilarArray(previousResults, results)) {
            onChange(results);
        }
    });
    return {
        addFile,
        results: uploadOperation === 'local'
            ? items
                .filter((item) => !isDefined(item.uploadStatus))
                .map((item) => item.file)
            : uploadResults,
        items,
        hasPendingRequest: uploadOperation === 'local'
            ? false
            : stateValue.items.some((item) => item.uploadStatus === 'uploading'),
    };
};
const fileUploadReducer = (state, action) => {
    switch (action.type) {
        case 'addFile':
            return Object.assign(Object.assign({}, state), { items: state.items.concat(Object.assign({ file: action.file, uploadStatus: action.errorMessage ? 'error' : 'uploading', errorMessage: action.errorMessage, uploadProgress: 0, key: action.key }, (action.errorMessage ? { noRetry: true } : {}))) });
        case 'addLocalFile':
            return Object.assign(Object.assign({}, state), { items: state.items.concat({
                    file: action.file,
                    uploadStatus: 'completed',
                    key: action.key,
                }) });
        case 'fileUploadProgress':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.file === action.file && item.uploadStatus === 'uploading'
                    ? Object.assign(Object.assign({}, item), { uploadProgress: action.uploadProgress }) : item) });
        case 'completeUpload':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.file === action.file && item.uploadStatus === 'uploading'
                    ? Object.assign(Object.assign({}, item), { uploadProgress: 100, uploadStatus: 'completed', result: action.result }) : item) });
        case 'error':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.file === action.file && item.uploadStatus === 'uploading'
                    ? Object.assign(Object.assign({}, item), { uploadStatus: 'error', errorMessage: action.errorMessage }) : item) });
        case 'remove':
            return Object.assign(Object.assign({}, state), { items: state.items.filter((item) => item.file !== action.file) });
        case 'retry':
            return Object.assign(Object.assign({}, state), { items: state.items.map((item) => item.file === action.file && item.uploadStatus === 'error'
                    ? Object.assign(Object.assign({}, item), { uploadStatus: 'uploading', uploadProgress: 0, errorMessage: undefined }) : item) });
        default:
            throw new Error(`Unsupported action: ${JSON.stringify(action)}`);
    }
};

/**
 * `useImageDimensions` allows you to get the actual dimensions
 * of the image loaded in `img` element.
 */
const useImageDimensions = (imageRef, options) => {
    const [dimensions, setDimensions] = React.useState();
    const deps = options && options.dependencies;
    React.useEffect(() => {
        const img = imageRef.current;
        if (img) {
            const getImageDimensions = () => setDimensions({
                width: img.naturalWidth,
                height: img.naturalHeight,
            });
            if (img.complete) {
                getImageDimensions();
            }
            else {
                img.addEventListener('load', getImageDimensions);
                return () => img.removeEventListener('load', getImageDimensions);
            }
        }
    }, [deps]);
    return dimensions;
};
const useImageFileDimensions = (file, { enabled = true, } = {}) => {
    const [dimensions, setDimensions] = React.useState();
    React.useEffect(() => {
        if (enabled && file) {
            const url = URL.createObjectURL(file);
            const img = new Image();
            img.onload = function () {
                setDimensions({
                    width: img.width,
                    height: img.height,
                });
            };
            img.src = url;
            return () => {
                URL.revokeObjectURL(url);
            };
        }
    }, [enabled, file]);
    return dimensions;
};

/**
 * `useInterval` is based on [Dan Abromov's blog](https://overreacted.io/making-setinterval-declarative-with-react-hooks/)
 * with additional ability to reset the interval
 * @param callback the callback you want to invoke for each interval
 * @param interval pass down `null` to pause the interval
 * @returns function to reset the current interval
 */
function useInterval(callback, interval) {
    const savedCallback = React.useRef(null);
    const [resetCount, setResetCount] = React.useState(0);
    // Remember the latest function.
    React.useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);
    // Set up the interval.
    React.useEffect(() => {
        function tick() {
            if (savedCallback.current) {
                savedCallback.current();
            }
        }
        if (interval !== null) {
            let id = setInterval(tick, interval);
            return () => clearInterval(id);
        }
        return;
    }, [interval, resetCount]);
    return function reset() {
        setResetCount((lastCount) => lastCount + 1);
    };
}

/**
 * `React.createContext` with the use case where the consumer must be rendered within
 * a provider.
 */
const createRequiredContext = ({ contextName, providerName = `${contextName}Provider`, }) => {
    const Context = createNamedContext(contextName, undefined);
    const useRequiredContext = (componentName) => {
        const cValue = React.useContext(Context);
        if (!isDefined(cValue)) {
            throw new Error(`${componentName} must be rendered within ${providerName}`);
        }
        return cValue;
    };
    return Object.assign(Context, {
        useRequiredContext,
    });
};

const IsClientContext = createRequiredContext({
    contextName: 'IsClientContext',
    providerName: 'IsClientProvider',
});
const IsClientProvider = (props) => {
    const [isClient, setIsClient] = React.useState(false);
    React.useEffect(() => {
        setIsClient(true);
    }, []);
    return (React.createElement(IsClientContext.Provider, { value: isClient }, props.children));
};
const useIsClient = IsClientContext.useRequiredContext;

const useRef = React.useRef;
const useIsInViewport = (options) => {
    const [, forceUpdate] = React.useState();
    const observer = useRef();
    const inViewportRef = useRef(isBoolean$1(options.initialInViewport) ? options.initialInViewport : false);
    const intersected = useRef(false);
    function startObserver(observerInstance) {
        const targetRef = options.target.current;
        if (targetRef) {
            observerInstance.observe(targetRef);
        }
    }
    function stopObserver(observerInstance) {
        const targetRef = options.target.current;
        if (targetRef) {
            observerInstance.unobserve(targetRef);
        }
        observerInstance.disconnect();
        observer.current = undefined;
    }
    function handleIntersection(entries) {
        const entry = entries[0] || {};
        const { isIntersecting, intersectionRatio } = entry;
        const isInViewport = typeof isIntersecting !== 'undefined'
            ? isIntersecting
            : intersectionRatio > 0;
        // enter
        if (!intersected.current && isInViewport) {
            intersected.current = true;
            inViewportRef.current = isInViewport;
            forceUpdate(isInViewport);
            if (options.disconnectOnEnter && observer.current) {
                stopObserver(observer.current);
            }
            return;
        }
        // leave
        if (intersected.current && !isInViewport) {
            intersected.current = false;
            if (options.disconnectOnLeave && observer.current) {
                // disconnect obsever on leave
                observer.current.disconnect();
            }
            inViewportRef.current = isInViewport;
            forceUpdate(isInViewport);
        }
    }
    function initIntersectionObserver() {
        if (!observer.current) {
            // $FlowFixMe
            observer.current = new IntersectionObserver(handleIntersection, options.init);
            return observer.current;
        }
        return observer.current;
    }
    React.useEffect(() => {
        if (!('IntersectionObserver' in window) ||
            !('IntersectionObserverEntry' in window) ||
            !('intersectionRatio' in window.IntersectionObserverEntry.prototype)) {
            const { fallbackValueIfUnsupported: defaultValueIfUnsupported = true } = options;
            inViewportRef.current = defaultValueIfUnsupported;
            forceUpdate(defaultValueIfUnsupported);
            return;
        }
        const observerRef = initIntersectionObserver();
        startObserver(observerRef);
        return () => {
            stopObserver(observerRef);
        };
    }, [options.init, options.disconnectOnLeave]);
    return {
        inViewport: inViewportRef.current,
    };
};

/**
 * `useListState` declare an array state that allows you to append item that auto removed after some time.
 *
 * @param init initial value
 */
const useListState = (init = []) => {
    const [items, setItems] = React.useState(() => {
        const initValue = isFunction(init) ? init() : init;
        return initValue.map((value) => ({
            value,
            key: getId(),
        }));
    });
    const timerMap = useConstant(() => new Map());
    React.useEffect(() => function cleanupTimers() {
        timerMap.forEach((t) => window.clearTimeout(t));
        timerMap.clear();
    }, []);
    const removeItem = useStableCallback(function removeItem(item) {
        setItems((prevItems) => prevItems.filter((i) => i !== item));
        const timer = timerMap.get(item.key);
        if (isDefined(timer)) {
            window.clearTimeout(timer);
            timerMap.delete(item.key);
        }
    });
    return {
        items: React.useMemo(() => items.map((item) => (Object.assign(Object.assign({}, item), { update: (updatedItem) => {
                setItems((prevItems) => prevItems.map((prevItem) => prevItem === item
                    ? Object.assign(Object.assign({}, prevItem), { value: updatedItem }) : prevItem));
            }, remove: () => removeItem(item) }))), [items]),
        append: React.useCallback(function appendItem(item, { removeAfterMs } = {}) {
            const key = getId();
            const record = {
                value: item,
                key,
            };
            setItems((prevItems) => prevItems.concat(record));
            if (isNumber(removeAfterMs)) {
                const timer = window.setTimeout(() => {
                    removeItem(record);
                }, removeAfterMs);
                timerMap.set(key, timer);
            }
            return () => {
                removeItem(record);
            };
        }, [timerMap, removeItem]),
        clear: useStableCallback(function clearItems() {
            timerMap.forEach((t) => window.clearTimeout(t));
            timerMap.clear();
            setItems([]);
        }),
    };
};

/**
 * Listen for pointer down and focus outside an element.
 *
 * This customer hook returns props to be spreaded to element.
 */
const useOnInteractOutside = (options) => {
    const pointerDownProps = usePointerDownOutside(options.onInteractOutside);
    const focusOutsideProps = useFocusOutside(options.onInteractOutside);
    return Object.assign(Object.assign({}, pointerDownProps), focusOutsideProps);
};
/* -------------------------------------------------------------------------------------------------
 * Utility hooks
 * -----------------------------------------------------------------------------------------------*/
const POINTER_DOWN_OUTSIDE = 'dismissableLayer.pointerDownOutside';
const FOCUS_OUTSIDE = 'dismissableLayer.focusOutside';
/**
 * Sets up `pointerdown` listener which listens for events outside a react subtree.
 *
 * We use `pointerdown` rather than `pointerup` to mimic layer dismissing behaviour
 * present in OS which usually happens on `pointerdown`.
 *
 * Returns props to pass to the node we want to check for outside events.
 */
function usePointerDownOutside(onPointerDownOutside) {
    const handlePointerDownOutside = useCallbackRef(onPointerDownOutside);
    const isPointerInsideReactTreeRef = React.useRef(false);
    React.useEffect(() => {
        const handlePointerDown = (event) => {
            const target = event.target;
            if (target && !isPointerInsideReactTreeRef.current) {
                const pointerDownOutsideEvent = new CustomEvent(POINTER_DOWN_OUTSIDE, {
                    bubbles: false,
                    cancelable: true,
                    detail: { originalEvent: event },
                });
                target.addEventListener(POINTER_DOWN_OUTSIDE, handlePointerDownOutside, { once: true });
                target.dispatchEvent(pointerDownOutsideEvent);
            }
            isPointerInsideReactTreeRef.current = false;
        };
        /**
         * if this hook executes in a component that mounts via a `pointerdown` event, the event
         * would bubble up to the document and trigger a `pointerDownOutside` event. We avoid
         * this by delaying the event listener registration on the document.
         * This is not React specific, but rather how the DOM works, ie:
         * ```
         * button.addEventListener('pointerdown', () => {
         *   console.log('I will log');
         *   document.addEventListener('pointerdown', () => {
         *     console.log('I will also log');
         *   })
         * });
         */
        const timerId = window.setTimeout(() => {
            document.addEventListener('pointerdown', handlePointerDown);
        }, 0);
        return () => {
            window.clearTimeout(timerId);
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [handlePointerDownOutside]);
    return {
        // ensures we check React component tree (not just DOM tree)
        onPointerDownCapture: () => (isPointerInsideReactTreeRef.current = true),
    };
}
/**
 * Listens for when focus happens outside a react subtree.
 * Returns props to pass to the root (node) of the subtree we want to check.
 */
function useFocusOutside(onFocusOutside) {
    const handleFocusOutside = useCallbackRef(onFocusOutside);
    const isFocusInsideReactTreeRef = React.useRef(false);
    React.useEffect(() => {
        const handleFocus = (event) => {
            const target = event.target;
            if (target && !isFocusInsideReactTreeRef.current) {
                const focusOutsideEvent = new CustomEvent(FOCUS_OUTSIDE, {
                    bubbles: false,
                    cancelable: true,
                    detail: { originalEvent: event },
                });
                target.addEventListener(FOCUS_OUTSIDE, handleFocusOutside, {
                    once: true,
                });
                target.dispatchEvent(focusOutsideEvent);
            }
        };
        document.addEventListener('focusin', handleFocus);
        return () => document.removeEventListener('focusin', handleFocus);
    }, [handleFocusOutside]);
    return {
        onFocusCapture: () => (isFocusInsideReactTreeRef.current = true),
        onBlurCapture: () => (isFocusInsideReactTreeRef.current = false),
    };
}

/**
 * @private
 */
const featureCheck = {
    stringRepeat: !!String.prototype.repeat,
    stringPadStart: !!String.prototype.padStart,
    stringPadEnd: !!String.prototype.padEnd,
    stringReplaceAll: !!String.prototype.replaceAll,
    get matchMedia() {
        return typeof window !== 'undefined' && !!window.matchMedia;
    },
    get otpCredential() {
        return typeof window !== 'undefined' && 'OTPCredential' in window;
    },
    intlNumberFormat: !!(Intl &&
        typeof Intl === 'object' &&
        typeof Intl.NumberFormat === 'function'),
    get intersectionObserver() {
        return typeof window !== 'undefined' && 'IntersectionObserver' in window;
    },
};

/**
 * Use Web OTP to get the one-time-password.
 *
 * @returns unsubscribe function, probably because user already fill up the OTP thus this is no longer required. You don't
 * need to call this manually if the component that renders this hooks will be unmount instantly when OTP is no longer needed.
 *
 * @see https://web.dev/web-otp/
 */
const useOtp = (options) => {
    const { onSuccess, enabled = true } = options;
    const unsubscribeRef = React.useRef(noop);
    const onSuccessCb = useStableCallback(onSuccess);
    React.useEffect(() => {
        if (enabled) {
            try {
                if (featureCheck.otpCredential) {
                    const ac = new AbortController();
                    navigator.credentials
                        .get({
                        otp: {
                            transport: ['sms'],
                        },
                        signal: ac.signal,
                    })
                        .then((otp) => {
                        if (otp && otp.code) {
                            onSuccessCb(otp.code);
                        }
                    })
                        .catch(noop);
                    unsubscribeRef.current = () => ac.abort();
                    return () => {
                        ac.abort();
                        unsubscribeRef.current = noop;
                    };
                }
            }
            catch (err) {
                // no handling as user can still manually copied form sms
            }
        }
    }, [enabled]);
    return function stopListenForOtp() {
        unsubscribeRef.current();
    };
};

const usePaginationState = ({ initialPage, initialPerPage, initialIsLastPage, onChange = noop, } = {}) => {
    const [state, setState] = React.useState(() => ({
        page: initialPage !== null && initialPage !== void 0 ? initialPage : 1,
        perPage: initialPerPage !== null && initialPerPage !== void 0 ? initialPerPage : 20,
        isLastPage: initialIsLastPage !== null && initialIsLastPage !== void 0 ? initialIsLastPage : false,
    }));
    const setPage = useStableCallback(function setPage(page) {
        const latestState = Object.assign(Object.assign({}, state), { page, isLastPage: false });
        setState(latestState);
        onChange(latestState);
    });
    const setPerPage = useStableCallback(function setPerPage(perPage) {
        const latestState = Object.assign(Object.assign({}, state), { page: 1, perPage });
        setState(latestState);
        onChange(latestState);
    });
    const setIsLastPage = useStableCallback(function setIsLastPage(isLastPage) {
        const latestState = Object.assign(Object.assign({}, state), { isLastPage });
        setState(latestState);
        onChange(latestState);
    });
    return React.useMemo(() => ({
        page: state.page,
        perPage: state.perPage,
        isLastPage: state.isLastPage,
        setPage,
        setPerPage,
        setIsLastPage,
    }), [state, setPage, setPerPage, setIsLastPage]);
};

/**
 * Similar to `React.useRef`, except that this is an array of ref instead of single ref.
 *
 * Note that the returned array with have its identity change whenever the length change.
 *
 * @param length the number of needed refs
 * @returns an array of refs with the provided length
 */
const useRefs = (length) => {
    const refs = React.useRef([]);
    if (refs.current.length !== length) {
        refs.current = createArray(length).map((_, index) => refs.current[index] || React.createRef());
    }
    return refs.current;
};

const useSortState = (defaultSortCriteria) => {
    const [state, dispatch] = React.useReducer(sortKeyReducer, defaultSortCriteria);
    return Object.assign(Object.assign({}, state), { sort: React.useCallback(({ sortBy, initialOrder = 'asc', }) => dispatch({
            type: 'sort',
            sortBy,
            initialOrder,
        }), []) });
};
const sortKeyReducer = (state, action) => {
    if (state.sortBy === action.sortBy) {
        return Object.assign(Object.assign({}, state), { order: state.order === 'asc' ? 'desc' : 'asc' });
    }
    else {
        return {
            sortBy: action.sortBy,
            order: action.initialOrder,
        };
    }
};

const splitClass = (className, prefixes) => {
    if (!className) {
        return {
            matched: '',
            rest: '',
        };
    }
    const regex = new RegExp(prefixes.map((prefix) => `^${prefix}`).join('|'));
    const matchedClasses = [];
    const restClasses = [];
    className.split(' ').forEach((cs) => {
        if (regex.test(cs)) {
            matchedClasses.push(cs);
        }
        else {
            restClasses.push(cs);
        }
    });
    return {
        matched: matchedClasses.join(' '),
        rest: restClasses.join(' '),
    };
};

/**
 * This is memoized version of `splitClass` utility.
 */
const useSplitClass = (className, prefixes) => React.useMemo(() => splitClass(className, prefixes), [className, prefixes]);

/**
 * A custom hooks that works almost like React.useState, with the
 * difference that it will goes back to its steady state after certain restoration time.
 *
 * @param steadyState the initial state as well as the target state that will be restored to
 * @param restorationTime time taken before the state goes back to steadyState (Default to 1500)
 */
const useTransientState = (steadyState, restorationTime = 1500) => {
    const [state, setState] = React.useState(steadyState);
    const [calledTimes, setCallTimes] = React.useState(0); // to retrigger effect to reset timeout
    const setTemporaryState = React.useCallback(function setTemporaryState(newValue) {
        setState(newValue);
        setCallTimes((t) => t + 1);
    }, []);
    React.useEffect(() => {
        if (state !== steadyState && restorationTime) {
            const timeoutId = setTimeout(() => setState(steadyState), restorationTime);
            return () => clearTimeout(timeoutId);
        }
        return;
    }, [state, steadyState, restorationTime, calledTimes]);
    return [state, setTemporaryState];
};

function throttle(func, wait = 100, context = null) {
    let timer = null;
    return function throttledFunction(...args) {
        if (timer === null) {
            timer = window.setTimeout(() => {
                func.apply(context, args);
                timer = null;
            }, wait);
        }
    };
}
function callAll(...fns) {
    return function callAllFns() {
        const arg = arguments;
        fns.forEach((fn) => typeof fn === 'function' && fn.apply(null, arg));
    };
}

const createAjaxOperation = (exec, { selectError = (x) => x } = {}) => function ajaxOperation(input, { onComplete = noop, onError = noop, onProgress = noop } = {}) {
    const source = axios.CancelToken.source();
    const promise = exec(input, source.token, onProgress);
    promise.then(onComplete).catch((err) => {
        if (!err || !axios.isCancel(err)) {
            onError(selectError(err));
        }
    });
    function cancel() {
        source.cancel();
    }
    return {
        cancel,
    };
};
const createUploadFileOperation = (getRequestConfig, { axios: axiosInstance = axios, getErrorMessage = () => undefined, selectResult = (res) => res.data, } = {}) => createAjaxOperation((file, cancelToken, updateProgress) => {
    const requestConfig = getRequestConfig(file);
    return axiosInstance(Object.assign(Object.assign({}, requestConfig), { cancelToken, onUploadProgress: callAll(requestConfig.onUploadProgress, (event) => updateProgress(event.loaded / event.total)) })).then((res) => selectResult(res, file));
}, { selectError: getErrorMessage });
const ApiClientContext = createNamedContext('Axios', axios);
const ApiClientProvider = ApiClientContext.Provider;
const useApiClient = () => React.useContext(ApiClientContext);

const NOOP_MODIFIER = '_';
/**
 * Transform modifiers object to string format .eg `width_{width},quality_{quality}`
 */
function toCDNImageModifierString(modifiers) {
    if (!modifiers)
        return NOOP_MODIFIER;
    const modStr = Object.entries(modifiers).reduce((prev, [k, v]) => [prev, isEmptyValue(v) ? '' : `${k}_${v}`]
        .filter((s) => Boolean(s))
        .join(','), '');
    return isEmptyValue(modStr) ? NOOP_MODIFIER : modStr;
}
function toCDNImageUrl(cdnBaseUrl, imageUrl, modifiers) {
    try {
        const cdnUrl = new URL(cdnBaseUrl);
        const imgUrl = new URL(imageUrl);
        // image's url is already based on CDN url, skip.
        if (imgUrl.hostname === cdnUrl.hostname) {
            return imageUrl;
        }
        // remove query/hash
        imgUrl.search = '';
        imgUrl.hash = '';
        // parse
        const parsed = s3uri(imgUrl.toString());
        if (!parsed || !parsed.bucket || !parsed.key) {
            throw new Error(`Invalid S3 url: ${imageUrl}`);
        }
        // construct CDN url
        cdnUrl.pathname = [
            toCDNImageModifierString(modifiers),
            parsed.bucket,
            parsed.key,
        ].join('/');
        return cdnUrl.toString();
    }
    catch (err) {
        console.warn(err.message);
        // if error just return the original url
        return imageUrl;
    }
}
/**
 * Factory function to create CDN utilities
 */
function createCDNUtils(cdnBaseUrl) {
    const _toCDNImageUrl = (imageUrl, modifiers) => {
        return toCDNImageUrl(cdnBaseUrl, imageUrl, modifiers);
    };
    const nextImageCDNLoader = ({ src, width, quality, }) => {
        return _toCDNImageUrl(src, { width, quality });
    };
    const Image = React.forwardRef(function Image(_a, ref) {
        var { src, cdnModifiers } = _a, props = __rest(_a, ["src", "cdnModifiers"]);
        return React.createElement("img", Object.assign({ ref: ref, src: _toCDNImageUrl(src, cdnModifiers) }, props));
    });
    return {
        /**
         * Transform a valid S3 url to image CDN url format with optional modifiers.
         */
        toCDNImageUrl: _toCDNImageUrl,
        /**
         * Loader to transform to image CDN format, made to be used with `next/image` image component
         */
        nextImageCDNLoader,
        /**
         * Image component for conveniently passing to image CDN, use this for existing images in S3 buckets
         */
        Image,
    };
}

/**
 * create a pub-sub event bus.
 */
const createEventBus = () => {
    const listeners = [];
    return {
        listen: (listener) => {
            listeners.push(listener);
            return function unsubscribe() {
                listeners.splice(listeners.indexOf(listener), 1);
            };
        },
        emit: (...args) => listeners.forEach((l) => l(...args)),
    };
};

/**
 * Create a Fill-Slot pair that allows you to render React elements in arbitrary subtree.
 *
 * Limitation: the Fill and Slot can only be rendered once only.
 *
 * ```jsx static
 * import { createFillSlot, TextInput } from '@setel/portal-ui'; // or `@setel/web-ui`.
 *
 * const { Fill, Slot } = createFillSlot();
 *
 * const Content = () => {
 *   const [value, setValue] = React.useState('');
 *
 *   return (
 *     <div>
 *       <h2>Content</h2>
 *       <TextInput value={value} onChangeValue={setValue} />
 *       <Fill>
 *         Content in another place.
 *         <p>{value}</p>
 *         <p>Pretty cool?</p>
 *       </Fill>
 *     </div>
 *   );
 * };
 *
 * const Sidebar = () => {
 *   return (
 *     <div className="text-right">
 *       <p>Sidebar</p>
 *       <Slot />
 *     </div>
 *   );
 * };
 *
 * const App = () => {
 *   return (
 *     <div className="grid grid-cols-3 gap-4">
 *       <div className="col-span-2">
 *         <Content />
 *       </div>
 *       <Sidebar />
 *     </div>
 *   );
 * };
 *
 * <App />;
 * ```
 */
function createFillSlot() {
    const ref = React.createRef();
    const eventBus = createEventBus();
    function Slot(props) {
        React.useEffect(() => {
            if (ref.current) {
                eventBus.emit();
            }
        }, []);
        return React.createElement("div", Object.assign({}, props, { ref: ref }));
    }
    function Fill(props) {
        const forceUpdate = useForceUpdate();
        React.useLayoutEffect(() => eventBus.listen(forceUpdate), [forceUpdate]);
        return ref.current ? createPortal(props.children, ref.current) : null;
    }
    return {
        Slot,
        Fill,
    };
}

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */

function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}

var MILLISECONDS_IN_MINUTE = 60000;
/**
 * @name addMinutes
 * @category Minute Helpers
 * @summary Add the specified number of minutes to the given date.
 *
 * @description
 * Add the specified number of minutes to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of minutes to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the minutes added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 30 minutes to 10 July 2014 12:00:00:
 * const result = addMinutes(new Date(2014, 6, 10, 12, 0), 30)
 * //=> Thu Jul 10 2014 12:30:00
 */

function addMinutes(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, amount * MILLISECONDS_IN_MINUTE);
}

/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {*} value - the value to check
 * @returns {boolean} true if the given value is a date
 * @throws {TypeError} 1 arguments required
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */

function isDate(value) {
  requiredArgs(1, arguments);
  return value instanceof Date || typeof value === 'object' && Object.prototype.toString.call(value) === '[object Date]';
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - Now `isValid` doesn't throw an exception
 *   if the first argument is not an instance of Date.
 *   Instead, argument is converted beforehand using `toDate`.
 *
 *   Examples:
 *
 *   | `isValid` argument        | Before v2.0.0 | v2.0.0 onward |
 *   |---------------------------|---------------|---------------|
 *   | `new Date()`              | `true`        | `true`        |
 *   | `new Date('2016-01-01')`  | `true`        | `true`        |
 *   | `new Date('')`            | `false`       | `false`       |
 *   | `new Date(1488370835081)` | `true`        | `true`        |
 *   | `new Date(NaN)`           | `false`       | `false`       |
 *   | `'2016-01-01'`            | `TypeError`   | `false`       |
 *   | `''`                      | `TypeError`   | `false`       |
 *   | `1488370835081`           | `TypeError`   | `true`        |
 *   | `NaN`                     | `TypeError`   | `false`       |
 *
 *   We introduce this change to make *date-fns* consistent with ECMAScript behavior
 *   that try to coerce arguments to the expected type
 *   (which is also the case with other *date-fns* functions).
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */

function isValid(dirtyDate) {
  requiredArgs(1, arguments);

  if (!isDate(dirtyDate) && typeof dirtyDate !== 'number') {
    return false;
  }

  var date = toDate(dirtyDate);
  return !isNaN(Number(date));
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};

var formatDistance = function (token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale[token];

  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }

  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }

  return result;
};

const formatDistance$1 = formatDistance;

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};
const formatLong$1 = formatLong;

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};

var formatRelative = function (token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
};

const formatRelative$1 = formatRelative;

function buildLocalizeFn(args) {
  return function (dirtyIndex, dirtyOptions) {
    var options = dirtyOptions || {};
    var context = options.context ? String(options.context) : 'standalone';
    var valuesArray;

    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;

      var _width = options.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }

    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex; // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!

    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
}; // Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.

var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};

var ordinalNumber = function (dirtyNumber, _options) {
  var number = Number(dirtyNumber); // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  var rem100 = number % 100;

  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';

      case 2:
        return number + 'nd';

      case 3:
        return number + 'rd';
    }
  }

  return number + 'th';
};

var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function (quarter) {
      return quarter - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};
const localize$1 = localize;

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }

  return undefined;
}

function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }

  return undefined;
}

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function (value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function (index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};
const match$1 = match;

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */
var locale = {
  code: 'en-US',
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 0
    /* Sunday */
    ,
    firstWeekContainsDate: 1
  }
};
const defaultLocale = locale;

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * const result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */

function subMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount);
}

var MILLISECONDS_IN_DAY = 86400000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeek(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeWeekStartsOn = locale && locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, dirtyOptions);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, dirtyOptions);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate);
  var year = getUTCWeekYear(dirtyDate, dirtyOptions);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, dirtyOptions);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();

  while (output.length < targetLength) {
    output = '0' + output;
  }

  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

var formatters$2 = {
  // Year
  y: function (date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
    var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length);
  },
  // Month
  M: function (date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },
  // Day of the month
  d: function (date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length);
  },
  // AM or PM
  a: function (date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return dayPeriodEnumValue.toUpperCase();

      case 'aaa':
        return dayPeriodEnumValue;

      case 'aaaaa':
        return dayPeriodEnumValue[0];

      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.';
    }
  },
  // Hour [1-12]
  h: function (date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H: function (date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length);
  },
  // Minute
  m: function (date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length);
  },
  // Second
  s: function (date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length);
  },
  // Fraction of second
  S: function (date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(milliseconds * Math.pow(10, numberOfDigits - 3));
    return addLeadingZeros(fractionalSeconds, token.length);
  }
};
const lightFormatters = formatters$2;

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
};
/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */

var formatters = {
  // Era
  G: function (date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;

    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, {
          width: 'abbreviated'
        });
      // A, B

      case 'GGGGG':
        return localize.era(era, {
          width: 'narrow'
        });
      // Anno Domini, Before Christ

      case 'GGGG':
      default:
        return localize.era(era, {
          width: 'wide'
        });
    }
  },
  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, {
        unit: 'year'
      });
    }

    return lightFormatters.y(date, token);
  },
  // Local week-numbering year
  Y: function (date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear; // Two digit year

    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    } // Ordinal number


    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, {
        unit: 'year'
      });
    } // Padding


    return addLeadingZeros(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function (date, token) {
    var isoWeekYear = getUTCISOWeekYear(date); // Padding

    return addLeadingZeros(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length);
  },
  // Quarter
  Q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'QQ':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'Qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        });
      // 1st quarter, 2nd quarter, ...

      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone quarter
  q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'qq':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        });
      // 1st quarter, 2nd quarter, ...

      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Month
  M: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      case 'M':
      case 'MM':
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th

      case 'Mo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // J, F, ..., D

      case 'MMMMM':
        return localize.month(month, {
          width: 'narrow',
          context: 'formatting'
        });
      // January, February, ..., December

      case 'MMMM':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone month
  L: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1);
      // 01, 02, ..., 12

      case 'LL':
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th

      case 'Lo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // J, F, ..., D

      case 'LLLLL':
        return localize.month(month, {
          width: 'narrow',
          context: 'standalone'
        });
      // January, February, ..., December

      case 'LLLL':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Local week of year
  w: function (date, token, localize, options) {
    var week = getUTCWeek(date, options);

    if (token === 'wo') {
      return localize.ordinalNumber(week, {
        unit: 'week'
      });
    }

    return addLeadingZeros(week, token.length);
  },
  // ISO week of year
  I: function (date, token, localize) {
    var isoWeek = getUTCISOWeek(date);

    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, {
        unit: 'week'
      });
    }

    return addLeadingZeros(isoWeek, token.length);
  },
  // Day of the month
  d: function (date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), {
        unit: 'date'
      });
    }

    return lightFormatters.d(date, token);
  },
  // Day of year
  D: function (date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);

    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, {
        unit: 'dayOfYear'
      });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },
  // Day of week
  E: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();

    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'EEEE':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Local day of week
  e: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th

      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'eeee':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th

      case 'co':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // T

      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        });
      // Tu

      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        });
      // Tuesday

      case 'cccc':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // ISO day of week
  i: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek);
      // 02

      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd

      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, {
          unit: 'day'
        });
      // Tue

      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'iiii':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM or PM
  a: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }

    switch (token) {
      case 'b':
      case 'bb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return lightFormatters.h(date, token);
  },
  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), {
        unit: 'hour'
      });
    }

    return lightFormatters.H(date, token);
  },
  // Hour [0-11]
  K: function (date, token, localize) {
    var hours = date.getUTCHours() % 12;

    if (token === 'Ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Hour [1-24]
  k: function (date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;

    if (token === 'ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Minute
  m: function (date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), {
        unit: 'minute'
      });
    }

    return lightFormatters.m(date, token);
  },
  // Second
  s: function (date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), {
        unit: 'second'
      });
    }

    return lightFormatters.s(date, token);
  },
  // Fraction of second
  S: function (date, token) {
    return lightFormatters.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return 'Z';
    }

    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`

      case 'XXXX':
      case 'XX':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`

      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`

      case 'xxxx':
      case 'xx':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`

      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (GMT)
  O: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (specific non-location)
  z: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Seconds timestamp
  t: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length);
  }
};

function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;

  if (minutes === 0) {
    return sign + String(hours);
  }

  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }

  return formatTimezone(offset, dirtyDelimiter);
}

function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

const formatters$1 = formatters;

function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({
        width: 'short'
      });

    case 'PP':
      return formatLong.date({
        width: 'medium'
      });

    case 'PPP':
      return formatLong.date({
        width: 'long'
      });

    case 'PPPP':
    default:
      return formatLong.date({
        width: 'full'
      });
  }
}

function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({
        width: 'short'
      });

    case 'pp':
      return formatLong.time({
        width: 'medium'
      });

    case 'ppp':
      return formatLong.time({
        width: 'long'
      });

    case 'pppp':
    default:
      return formatLong.time({
        width: 'full'
      });
  }
}

function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/) || [];
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  var dateTimeFormat;

  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({
        width: 'short'
      });
      break;

    case 'PP':
      dateTimeFormat = formatLong.dateTime({
        width: 'medium'
      });
      break;

    case 'PPP':
      dateTimeFormat = formatLong.dateTime({
        width: 'long'
      });
      break;

    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({
        width: 'full'
      });
      break;
  }

  return dateTimeFormat.replace('{{date}}', dateLongFormatter(datePattern, formatLong)).replace('{{time}}', timeLongFormatter(timePattern, formatLong));
}

var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};
const longFormatters$1 = longFormatters;

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  var utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return date.getTime() - utcDate.getTime();
}

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];
function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1;
}
function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1;
}
function throwProtectedError(token, format, input) {
  if (token === 'YYYY') {
    throw new RangeError("Use `yyyy` instead of `YYYY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'YY') {
    throw new RangeError("Use `yy` instead of `YY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'D') {
    throw new RangeError("Use `d` instead of `D` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'DD') {
    throw new RangeError("Use `dd` instead of `DD` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  }
}

// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;
/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://git.io/fxCyr
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://git.io/fxCyr
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The second argument is now required for the sake of explicitness.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   format(new Date(2016, 0, 1))
 *
 *   // v2.0.0 onward
 *   format(new Date(2016, 0, 1), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
 *   ```
 *
 * - New format string API for `format` function
 *   which is based on [Unicode Technical Standard #35](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
 *   See [this post](https://blog.date-fns.org/post/unicode-tokens-in-date-fns-v2-sreatyki91jg) for more details.
 *
 * - Characters are now escaped using single quote symbols (`'`) instead of square brackets.
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://git.io/fxCyr
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://git.io/fxCyr
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * var result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * var result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * var result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */

function format(dirtyDate, dirtyFormatStr, dirtyOptions) {
  requiredArgs(2, arguments);
  var formatStr = String(dirtyFormatStr);
  var options = dirtyOptions || {};
  var locale = options.locale || defaultLocale;
  var localeFirstWeekContainsDate = locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var localeWeekStartsOn = locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (!locale.localize) {
    throw new RangeError('locale must contain localize property');
  }

  if (!locale.formatLong) {
    throw new RangeError('locale must contain formatLong property');
  }

  var originalDate = toDate(dirtyDate);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376


  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);
  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale,
    _originalDate: originalDate
  };
  var result = formatStr.match(longFormattingTokensRegExp).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong, formatterOptions);
    }

    return substring;
  }).join('').match(formattingTokensRegExp).map(function (substring) {
    // Replace two single quote characters with one single quote character
    if (substring === "''") {
      return "'";
    }

    var firstCharacter = substring[0];

    if (firstCharacter === "'") {
      return cleanEscapedString(substring);
    }

    var formatter = formatters$1[firstCharacter];

    if (formatter) {
      if (!options.useAdditionalWeekYearTokens && isProtectedWeekYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      if (!options.useAdditionalDayOfYearTokens && isProtectedDayOfYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      return formatter(utcDate, substring, locale.localize, formatterOptions);
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return substring;
  }).join('');
  return result;
}

function cleanEscapedString(input) {
  return input.match(escapedStringRegExp)[1].replace(doubleQuoteRegExp, "'");
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */

function startOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day (and year and month)?
 *
 * @description
 * Are the given dates in the same day (and year and month)?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same day (and year and month)
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * var result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 * 
 * @example
 * // Are 4 September and 4 October in the same day?
 * var result = isSameDay(new Date(2014, 8, 4), new Date(2014, 9, 4))
 * //=> false
 * 
 * @example
 * // Are 4 September, 2014 and 4 September, 2015 in the same day?
 * var result = isSameDay(new Date(2014, 8, 4), new Date(2015, 8, 4))
 * //=> false
 */

function isSameDay(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);
  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime();
}

// @overview: Helpers to format data in desired format
// no point adding big mapping if we
// only support MYR now
const currencyMap = {
    MYR: 'RM',
};
const commaPositionRegex = /\B(?=(\d{3})+(?!\d))/g;
function insertThousandSeparator(number) {
    const numString = String(number);
    if (numString.includes('.')) {
        const [integer, decimal] = numString.split('.');
        return `${integer.replace(commaPositionRegex, ',')}.${decimal}`;
    }
    return numString.replace(commaPositionRegex, ',');
}
const numberFormatters = {};
/**
 *
 * @param value the number value that you wish to format
 * @param currency can be 'MYR'
 */
const formatMoney = (value, currencyOrOptions = '') => {
    if (!isDefined(value)) {
        return '';
    }
    const numValue = Number(value);
    const decimalPlaces = !isString(currencyOrOptions) && isDefined(currencyOrOptions.decimalPlaces)
        ? currencyOrOptions.decimalPlaces
        : 2;
    let displayValue = '';
    if (isNaN(numValue)) {
        return value;
    }
    if (featureCheck.intlNumberFormat) {
        if (!numberFormatters[decimalPlaces]) {
            numberFormatters[decimalPlaces] = new Intl.NumberFormat('en-US', {
                minimumFractionDigits: decimalPlaces,
                maximumFractionDigits: decimalPlaces,
            });
        }
        displayValue = numberFormatters[decimalPlaces].format(numValue);
    }
    else {
        displayValue = insertThousandSeparator(numValue.toFixed(decimalPlaces));
    }
    const currency = isString(currencyOrOptions)
        ? currencyOrOptions
        : currencyOrOptions.currency || '';
    const currencyDisplay = currencyMap[currency] || currency;
    return currencyDisplay ? `${currencyDisplay} ${displayValue}` : displayValue;
};
function formatFileSize(fileSizeInByte, { decimalPlaces = 0 } = {}) {
    if (fileSizeInByte > bytePerGb) {
        return `${formatMoney(fileSizeInByte / bytePerGb, { decimalPlaces })}GB`;
    }
    if (fileSizeInByte > bytePerMb) {
        return `${formatMoney(fileSizeInByte / bytePerMb, { decimalPlaces })}MB`;
    }
    if (fileSizeInByte > bytePerKb) {
        return `${formatMoney(fileSizeInByte / bytePerKb, { decimalPlaces })}KB`;
    }
    return `${formatMoney(fileSizeInByte, { decimalPlaces })}B`;
}
const bytePerKb = 1024;
const bytePerMb = 1024 * bytePerKb;
const bytePerGb = 1024 * bytePerMb;
/**
 *
 * @param value original string
 * @param options options to customize behavior. `hasUnderscore: true` will split the string whenever '_' character is encountered.
 * `hasWhitespace: true` will split the string whenever white space is encountered.
 */
const titleCase = (value, options) => {
    if (!value) {
        return value;
    }
    if (options) {
        if (options.hasWhitespace && /\s/.test(value)) {
            return value
                .split(/(\s+)/)
                .filter((part) => part.trim().length > 0)
                .map((x) => titleCase(x, options))
                .join(' ');
        }
        if (options.hasUnderscore && /_/.test(value)) {
            return value
                .split('_')
                .map((x) => titleCase(x, options))
                .join(' ');
        }
        if (options.hasDash && /\-/.test(value)) {
            return value
                .split('-')
                .map((x) => titleCase(x, options))
                .join(' ');
        }
    }
    return value[0].toUpperCase() + value.substring(1).toLowerCase();
};
const stringRepeat = (charToRepeat, count) => {
    if (featureCheck.stringRepeat) {
        return charToRepeat.repeat(count);
    }
    if (count < 0 || count === Infinity) {
        throw RangeError(`Repeat count must be between 0 and Infinity`);
    }
    let result = '';
    for (let index = 0; index < count; index++) {
        result += charToRepeat;
    }
    return result;
};
const padStart = (oriString, maxLength, fillString = ' ') => {
    if (featureCheck.stringPadStart) {
        return oriString.padStart(maxLength, fillString);
    }
    const requiredPadding = maxLength - oriString.length;
    if (requiredPadding <= 0) {
        return oriString;
    }
    return stringRepeat(fillString, requiredPadding) + oriString;
};
const padEnd = (oriString, maxLength, fillString = ' ') => {
    if (featureCheck.stringPadEnd) {
        return oriString.padEnd(maxLength, fillString);
    }
    const requiredPadding = maxLength - oriString.length;
    if (requiredPadding <= 0) {
        return oriString;
    }
    return oriString + stringRepeat(fillString, requiredPadding);
};
function escapeRegExp(string) {
    return string.replace(/[.*+\-?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}
const replaceAll = (oriString, searchValue, replaceValue) => featureCheck.stringReplaceAll
    ? oriString.replaceAll(searchValue, replaceValue)
    : oriString.replace(new RegExp(escapeRegExp(searchValue), 'g'), replaceValue);
/**
 * small abstraction over `date-fns/format` function
 *
 * If the provided `date` is not a valid date, this function return it without modifying.
 *
 * @param date date value to be formatted
 * @param options
 * @param options.formatType convenient shorthand to specify the format, can be 'dateAndTime' or 'dateOnly'. Use to provide default for options.format
 * @param options.format format string that will be passed to second parameter of `date-fns/format`
 */
const formatDate = (date, { formatType = 'dateAndTime', format: format$1 = formatType === 'dateAndTime'
    ? FORMAT_STRING.dayAndTime
    : FORMAT_STRING.dayOnly, } = {}) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return isNaN(dateObj.getTime())
        ? date
        : format(dateObj, format$1);
};
/**
 * format a date range.
 *
 * @param from the earlier date. can be undefined.
 * @param to the later date. can be undefined or same as from.
 * @param dayOnly whether we only cares about date without considering time.
 */
const formatDateRange = (from, to, dayOnly) => {
    if (isDefined(from) && isDefined(to)) {
        if (isSameDay(from, to)) {
            const dayOnlyDisplay = formatDate(from, { formatType: 'dateOnly' });
            if (dayOnly) {
                return dayOnlyDisplay;
            }
            const fromTime = format(from, FORMAT_STRING.timeOnly);
            const toTime = format(to, FORMAT_STRING.timeOnly);
            if (fromTime === toTime) {
                return formatDate(from, { formatType: 'dateAndTime' });
            }
            return `${dayOnlyDisplay}, ${fromTime} - ${toTime}`;
        }
    }
    return [from, to]
        .filter(isDefined)
        .map((d) => formatDate(d, { formatType: dayOnly ? 'dateOnly' : 'dateAndTime' }))
        .join(' - ');
};
/**
 * format a date to UTC date format, e.g. `'2021-02-03T16:00:00.000Z'`.
 *
 * Confession: TBH I don't really understand those date formats, I just copy from https://github.com/date-fns/date-fns/issues/1401#issuecomment-578580199.
 *
 * @param date the date that you want to format
 */
const formatDateUtc = (date) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return format(addMinutes(d, d.getTimezoneOffset()), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
};
const FORMAT_STRING = {
    dayOnly: 'd MMM yyyy',
    dayAndTime: 'd MMM yyyy, h:mm a',
    timeOnly: 'h:mm a',
};

const regexMap = new Map();
/**
 * Check if a user provided className to have specific prefix, useful if you
 * want to avoid conflict of tailwind className
 *
 * @param className the className that is user provided
 * @param utilPrefixes the prefixes you wish to check, e.g. `w-`
 */
const hasUtilClass = (className, ...utilPrefixes) => {
    if (!className) {
        return false;
    }
    const cacheKey = utilPrefixes.join(',');
    const cachedRegex = regexMap.get(cacheKey);
    if (cachedRegex) {
        return cachedRegex.test(className);
    }
    const regexPattern = flattenArray(utilPrefixes.map((prefix) => [`^${prefix}`, `\\s${prefix}`])).join('|');
    const regex = new RegExp(regexPattern);
    regexMap.set(cacheKey, regex);
    return regex.test(className);
};

/**
 * lightweight query string parsing shamelessly
 * copied and tweaked from https://github.com/kawanet/qs-lite/blob/master/qs-lite.js
 */
function parseQueryString(input) {
    const obj = {};
    if (input) {
        const sanitizedInput = input[0] === '?' ? input.slice(1) : input;
        sanitizedInput.replace(/\+/g, ' ').split(/[&;]/).forEach(it);
    }
    return obj;
    function it(pair) {
        const len = pair.length;
        if (!len)
            return;
        let pos = pair.indexOf('=');
        if (pos < 0)
            pos = len;
        const key = decodeURIComponent(pair.substr(0, pos));
        const value = decodeURIComponent(pair.substr(pos + 1));
        const currentValue = obj[key];
        if (currentValue) {
            if (Array.isArray(currentValue)) {
                currentValue.push(value);
            }
            else {
                obj[key] = [currentValue, value];
            }
        }
        else {
            obj[key] = value;
        }
    }
}
const hasOwnProperty = Object.prototype.hasOwnProperty;
const stringifyQueryString = (params, preText = '?') => {
    if (!params) {
        return '';
    }
    const result = [];
    function collectPair(key, value) {
        result.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
    }
    for (const key in params) {
        if (hasOwnProperty.call(params, key)) {
            const value = params[key];
            if (isNumber(value) || value) {
                if (Array.isArray(value)) {
                    value.forEach((val) => collectPair(key, val));
                }
                else {
                    collectPair(key, value);
                }
            }
        }
    }
    return preText + result.join('&');
};

const isEmail = (value) => /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(value);

/**
 * Checks whether or not a value is a boolean.
 *
 * @param value
 */
function isBoolean(value) {
  return typeof value === "boolean";
}

const scrollIntoView = scrollIntoViewIfNeeded;
const boolOrBoolString = (value) => {
    return value === 'true' ? true : isBoolean(value) ? value : false;
};

export { AddImageIcon, AmexIcon, ApiClientProvider, ArrowLeftIcon, BadgeIcon, BankIcon, BellIcon, BigCheckIcon, BookIcon, BoxIcon, BusinessIcon, CalendarIcon, CameraIcon, CardIcon, CardsIcon, CartIcon, CheckIcon, CheckInCircleIcon, CheckInCircleOutlineIcon, CheckedIcon, ChevronDoubleRightIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, CircleIcon, ClipboardIcon, ClockIcon, CogIcon, CompassIcon, ContactBookIcon, CookiesIcon, CreditCardIcon, CrossIcon, CrossInCircleIcon, DiamondIcon, DocIcon, DocumentIcon, DotHorizontalIcon, DotVerticalIcon, Dots, DownloadIcon, EditIcon, EngineIcon, ErrorIcon, ExclamationIcon, ExpandLessIcon, ExpandMoreIcon, ExternalIcon, EyeHideIcon, EyeShowIcon, FamilyIcon, FilledStarIcon, FilterIcon, FuelTankIcon, GasTankIcon, HeartIcon, HelpIcon, HistoryIcon, ImageIcon, InboxIcon, InfoIcon, InfoOutlineIcon, InviteIcon, IsClientProvider, LanguageIcon, LifebuoyIcon, LinkIcon, ListIcon, LocationIcon, LockIcon, LogoutIcon, LorryIcon, LoudspeakerIcon, MailIcon, MapIcon, MastercardIcon, MembershipIcon, MenuIcon, MesraCardIcon, MessageIcon, MeterIcon, MiniAscendingIcon, MiniChevronDownIcon, MiniCloseIcon, MiniDescendingIcon, MiniUnsortedIcon, MinusIcon, MoreIcon, OrderIcon, PetronasGreenIcon, PetronasWhiteIcon, Phone1Icon, PlusIcon, PreviewIcon, PrinterIcon, PumpIcon, ReceiptIcon, ReloadIcon, ReplayIcon, RewardsIcon, ScanIcon, SearchIcon, SetelDefaultIcon, SetelIcon, SetelLightIcon, SetelShareIcon, SetelWalletIcon, SettlementsIcon, ShareIcon, ShoppingBagIcon, SmartPayDarkIcon, SmartPayLightIcon, SparklesIcon, SpinIcon, StarIcon, SyncIcon, ToggleIcon, TopupIcon, TransmissionIcon, TrashIcon, UploadIcon, UserCircleIcon, UserIcon, VehicleIcon, VerifiedIcon, VisaIcon, VoidSalesIcon, VoucherIcon, WalletIcon, WarningIcon, ZoomInIcon, arrayMove, boolOrBoolString, callAll, createAjaxOperation, createArray, createCDNUtils, createDescendantContext, createEventBus, createFillSlot, createRequiredContext, createUploadFileOperation, dedupeArray, featureCheck, findLastIndex, flattenArray, forEach, formatDate, formatDateRange, formatDateUtc, formatFileSize, formatMoney, getComputedStyle, getComputedStyles, getId, hasUtilClass, insertThousandSeparator, isDefined, isEmail, isEmptyObject, isEmptyValue, isNil, isPrimitive, isSimilarArray, omit, padEnd, padStart, parseQueryString, pick, pickAndOmit, replaceAll, scrollIntoView, splitClass, stringifyQueryString, throttle, titleCase, toArray, useAjaxOperations, useApiClient, useCallbackRef, useControllableState, useDebounce, useDefaultSvgProps, useDelayedCallback, useDescendantState, useFileUploads, useImageDimensions, useImageFileDimensions, useInterval, useIsClient, useIsInViewport, useListState, useOnInteractOutside, useOtp, usePaginationState, useRefs, useSortState, useSplitClass, useTransientState, withDefaultSvgProps };
//# sourceMappingURL=web-utils.esm.js.map
